import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify'; // Using react-toastify for notifications
import './JobForm.css';
import VesselVoyageForm from '../masters/VesselVoyageForm'; // Adjust path as needed
import { Select2Dropdown } from '../common'; // Import the global component
import { EmailModal, EmailViewer, ConfirmDialog } from '../common';
import { openFilePreview } from '../common/FilePreviewModal';
import { MultiSelectDropdown } from '../common/MultiSelectDropdown';
import { usePermissions } from '../../services/PermissionsContext';
import PopoutWindow from '../common/PopoutWindow';
import PayableExpensesView from '../admin/PayableExpensesView';
import DepartmentAttachmentsPopout from './DepartmentAttachmentsPopout';
import * as XLSX from 'xlsx'; // Add this import

const API_BASE_URL = '/api/documentation/jobs/';
const MASTERS_API_BASE_URL = '/api/masters/';
const ADMIN_API_BASE_URL = '/api/admin/'; // Add admin API base
const GEONAMES_USERNAME = 'salimh.';

// Helper to get auth headers
const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};


const formatDisplayNumber = (value, defaultValue = '') => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const initialJobState = {
    // Basic Information
    shipmentType: '',
    cargoType: '',
    cargoNature: 'NORMAL',
    dangerousClasses: [],
    emailSubject: '',
    status: [],
    statusRemark: '',
    
    // Related entities
    customer: '',
    supplier: '',
    voyage: '',
    customerRefNo: '',
    shippingLineId: '', 
    blCarrierId: '',  
    
    // Shipment Details
    mblNo: '',
    invoiceNo: '',
    kkFileNo: '',
    hblSoNo: '',
    idfNo: '',
    tansadNo: '',
    tansadDate: '',
    ucrNo: '',
    ucrDate: '',
    tasac: false,
    exemptionRefNo: '',
    // receiptDate: '',

    // Ex Bond - Previous IDF/TANSAD tracking
    previousDocType: '',
    previousIdfNo: '',
    previousTansadNo: '',
    exBondType: '',
    changeOfOwnership: false,
    
    // Supplier & Consignee/Notify Details
    supplierDetails: { name: '', address: '', city: '', country: '' },
    // consigneeSameAsCustomer: null, // Changed from true to null
     consignee: { 
        name: '', 
        address: '', 
        city: '', 
        country: '',
        sameAsCustomer: null  // This is now part of consignee JSON
    },
    // notifySameAsCustomer: null, // Changed from true to null  
    notify: { 
        name: '', 
        address: '', 
        city: '', 
        country: '',
        sameAsCustomer: null  // This is now part of notify JSON
    },
    
    // Vessel Details
    firstVessel: '',
    firstVoyageNumber: '',
    shippedOnBoardDate: '',
    sameAsFinalVessel: false,
    finalVessel: '',
    finalVoyage: '',
    finalVoyageNumber: '', // Add this field
    shippingLine: '',
    eta: '',
    etb: '',
    arrivalDate: '',
    berthingDate: '',
    berthedAt: '',
    icd: '',
    icdTransferDate: '',
    
    // Cargo Details
    countryOfLoading: '',
    portOfLoading: '',
    portOfDischarge: 'DAR ES SALAAM',
    nominatedBy: '',
    folderName: '', // Add Folder Name
    marks: '',
    description: '',
    portMaterial: '',
    totalContainers: '',
    totalPackages: '',
    packageType: '',
    totalGrossWeight: '',
    totalTare: '0',
    totalWeight: '',

    // loose cargo inline (single record convenience)
    lclContainerNo: '',
    lclCbm: '',
    lclPackages: '',
    lclPackageType: '',
    lclGrossWeight: '',
};


const VoyageModalWrapper = ({ isOpen, onClose, onSave }) => {
    if (!isOpen) return null;

    const handleVoyageFormClose = (saved) => {
        if (saved) {
            // If voyage was saved, refresh the voyages list and close modal
            onSave();
        }
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-container voyage-modal">
                <div className="modal-header">
                    <h2>Add New Voyage</h2>
                    <button type="button" className="modal-close" onClick={() => onClose()}>
                        ×
                    </button>
                </div>
                <div className="modal-content">
                    <VesselVoyageForm
                        mode="create"
                        initialData={null}
                        onClose={handleVoyageFormClose}
                    />
                </div>
            </div>
        </div>
    );
};


const NotesModal = ({ isOpen, onClose, title, notes }) => {
    const [expandedNoteId, setExpandedNoteId] = useState(null);
    if (!isOpen) return null;
    return (
        <div className="modal-overlay">
            <div className="dialog-container" style={{ maxWidth: 700, width: '90%' }}>
                <div className="dialog-header">
                    <h3>{title}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#fff',
                            fontSize: '24px',
                            lineHeight: 1,
                            cursor: 'pointer',
                            padding: '0 4px'
                        }}
                    >
                        ×
                    </button>
                </div>
                <div className="dialog-body" style={{ padding: '16px' }}>
                    {!notes || notes.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#666' }}>No notes available.</p>
                    ) : (
                        <table className="table table-sm" style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#f8f9fa' }}>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #ddd' }}>SN</th>
                                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #ddd' }}>SUBJECT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {notes.map((note, idx) => {
                                    const id = note.id || idx;
                                    const isExpanded = expandedNoteId === id;
                                    return (
                                        <React.Fragment key={id}>
                                            <tr
                                                onClick={() => setExpandedNoteId(isExpanded ? null : id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{idx + 1}</td>
                                                <td style={{ padding: '8px', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{(note.subject || '-').toUpperCase()}</td>
                                            </tr>
                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan="2" style={{ padding: '8px', borderBottom: '1px solid #eee', backgroundColor: '#f9f9f9' }}>
                                                        <div style={{ whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: 1.5 }}>
                                                            {(note.notes || '-').toUpperCase()}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

const ShippingLineNotesModal = ({ isOpen, onClose, notes }) => (
    <NotesModal isOpen={isOpen} onClose={onClose} title="Shipping Line Notes" notes={notes} />
);

const CustomerNotesModal = ({ isOpen, onClose, notes }) => (
    <NotesModal isOpen={isOpen} onClose={onClose} title="Customer Notes" notes={notes} />
);


const getPortsByCountry = async (countryCode) => {
    if (!countryCode) return [];
    
    try {
        const response = await fetch(
            `${MASTERS_API_BASE_URL}ports/by_country/?country_code=${countryCode}`,
            { headers: getAuthHeaders() }
        );
        
        if (response.ok) {
            const ports = await response.json();
            console.log('Raw ports data from API:', ports);
            
            // Return array of port names as strings
            return ports.map(port => {
                if (typeof port === 'object' && port.name) {
                    return port.name;
                }
                return String(port);
            });
        }
    } catch (error) {
        console.error('Error fetching ports:', error);
        return [];
    }
};

const openPreview = (fileURL, fileName) => {
    openFilePreview(fileURL, fileName, {
        customTitle: `Preview: ${fileName || 'Document'}`,
        onDownload: (data) => {
            // Custom download handler if needed
            console.log('Download requested:', data);
        },
        onClose: () => {
            // Custom close handler if needed
            console.log('Preview window closed');
        }
    });
};

const DocumentPreviewModal = ({ open, file, onClose }) => {
    const modalRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
   
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setOffset({
            x: e.clientX - modalRef.current.getBoundingClientRect().left,
            y: e.clientY - modalRef.current.getBoundingClientRect().top
        });
        document.body.style.userSelect = 'none';
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        document.body.style.userSelect = 'auto';
    };

    const handleMouseMove = (e) => {
        if (isDragging && modalRef.current) {
            modalRef.current.style.left = `${e.clientX - offset.x}px`;
            modalRef.current.style.top = `${e.clientY - offset.y}px`;
        }
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    });

    if (!open || !file) return null;


    // File preview logic
    let content = null;
    if (file && file.type && file.type.startsWith('image/')) {
        content = <img src={URL.createObjectURL(file)} alt={file.name} style={{ maxWidth: '100%', maxHeight: '60vh' }} />;
    } else if (file && file.type === 'application/pdf') {
        content = <iframe src={URL.createObjectURL(file)} title={file.name} style={{ width: '100%', height: '60vh', border: 'none' }} />;
    } else {
        content = (
            <div>
                <p>Preview not available for this file type.</p>
                <a href={URL.createObjectURL(file)} download={file.name}>Download {file.name}</a>
            </div>
        );
    }

    return (
        <div style={{
            position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)',
            zIndex: 9999, background: '#fff', border: '1px solid #ccc', borderRadius: 8,
            boxShadow: '0 0 10px rgba(0,0,0,0.2)', minWidth: 400, minHeight: 200, maxWidth: '90vw'
        }}
            ref={modalRef}
        >
            <div
                style={{ cursor: 'move', background: '#f1f1f1', padding: 10, borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onMouseDown={handleMouseDown}
            >
                <span>{file.name}</span>
                <button onClick={onClose} style={{ fontSize: 14, background: 'none', border: 'none', cursor: 'pointer' }}>Close</button>
            </div>
            <div style={{ padding: 16 }}>
                {content}
            </div>
        </div>
    );
};

// Add this helper function near the top of your component or in a utils file
const getCurrentUser = () => {
    try {
        const userDataString = localStorage.getItem('kk_user');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                return userData.username || userData.name || userDataString;
            } catch {
                return userDataString; // Return as-is if not JSON
            }
        }
        return localStorage.getItem('kk_user_id') || 'Unknown User';
    } catch {
        return 'Unknown User';
    }
};

const getFilteredCargoTypes = (shipmentType) => {
    if (!shipmentType) return [];
    
    switch (shipmentType) {
        case 'IMPORT-SEA':
        case 'EXPORT-SEA':
            return ['FCL', 'LCL', 'VEHICLE', 'BREAK BULK', 'ZNZ'];
        
        case 'IMPORT-AIR':
        case 'EXPORT-AIR':
        case 'TRANSIT-AIR':
            return ['AIR', 'AIR COURIER'];
        
        case 'IMPORT-ROAD':
            return ['ROAD', 'FCL'];
        
        case 'EXPORT-ROAD':
            return ['EXPORT ENTRY', 'SCT', 'FCL'];
        
        case 'EX BOND':
            return ['VEHICLE', 'FCL'];
        
        default:
            return [];
    }
};


const JobForm = ({
    mode,
    initialData,
    onClose,
    documentsEditable = false,
    openDocumentsOnly = false,
    lockExistingDocuments = false,
    openShipmentDetails = false,
    hideEmailSection = false,
    showEmailButtonOnly = false,
    wharfageOnly = false,
    defaultEmailTemplateName = null, // 1. Accept the new prop here
    documentTypeDepartment = 'Documentation' // Department to filter document types by
}) => {
    const { hasPermission, permissions } = usePermissions(); // Fetch permissions
    const [job, setJob] = useState(initialData || initialJobState);
    const [readOnly, setReadOnly] = useState(mode === 'view' || openDocumentsOnly || !hasPermission('JOBS', 'can_edit'));
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [savingDocuments, setSavingDocuments] = useState(false);
    const [initialEmailCount, setInitialEmailCount] = useState(0);
    const [loadingCities, setLoadingCities] = useState(false);
    const [replacingDocIndex, setReplacingDocIndex] = useState(null); // Add this line
    const isDocumentsOnlyMode = documentsEditable && openDocumentsOnly;
    const isWharfageOnly = wharfageOnly || (defaultEmailTemplateName || '').toLowerCase() === 'wharfage invoice';

    const [showExpensesModal, setShowExpensesModal] = useState(false);
    const showViewExpenses = permissions?.is_superuser || permissions?.department_name?.toUpperCase() === 'ADMIN';
    const isAdmin = permissions?.is_superuser || permissions?.department_name?.toUpperCase() === 'ADMIN';
    const canRemoveRows = mode !== 'edit' || hasPermission('JOBS', 'can_delete');

    const [departmentAttachmentsModal, setDepartmentAttachmentsModal] = useState(null);
    const [collapsedDepartments, setCollapsedDepartments] = useState({});

        
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [editEmailModal, setEditEmailModal] = useState(null);
    const [viewEmailModal, setViewEmailModal] = useState(null);
    const [customerEmailGroups, setCustomerEmailGroups] = useState([]);

    const emailTableHeaderStyle = {
        padding: '8px',
        textAlign: 'left',
        borderBottom: '2px solid #ddd',
        fontSize: '11px',
        fontWeight: 'bold'
    };

    const emailTableCellStyle = {
        padding: '6px 8px',
        borderBottom: '1px solid #eee',
        fontSize: '11px',
        verticalAlign: 'top'
    };

    // Section visibility logic
    const canShowOtherSections = job.shipmentType && job.cargoType && job.emailSubject.length > 4;
    
    // Add this state for the voyage modal
    const [showVoyageModal, setShowVoyageModal] = useState(false);
    const [showShippingLineNotes, setShowShippingLineNotes] = useState(false);
    const [shippingLineNotes, setShippingLineNotes] = useState([]);
    const [showCustomerNotes, setShowCustomerNotes] = useState(false);
    const [customerNotes, setCustomerNotes] = useState([]);
    
    
    // Dropdown data
    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [voyages, setVoyages] = useState([]);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [packageTypes, setPackageTypes] = useState([]);
    const [shippingLines, setShippingLines] = useState([]);
    const [blCarriers, setBlCarriers] = useState([]);
    const [filteredVoyages, setFilteredVoyages] = useState([]);
    const [icdOptions, setIcdOptions] = useState([]); // New state for ICDs
    const [documentTypes, setDocumentTypes] = useState([]);

    // --- Ports state for Border dropdown ---
    const [ports, setPorts] = useState([]);

    // Separate states for repeatable sections
    const [containers, setContainers] = useState([
        {
            container_no: '',
            size: '',
            container_type: '',
            seal_no: '',
            gross_weight: '',
            tare_weight: '',
            kk_seal_no: ''
        }
    ]);
    const [vehicles, setVehicles] = useState([]);
    const [looseCargoItems, setLooseCargoItems] = useState([]);
    const [commodities, setCommodities] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [exBondPreviousDocuments, setExBondPreviousDocuments] = useState([]);
    const [doAttachmentItems, setDoAttachmentItems] = useState([]);
    const [letters, setLetters] = useState([]);
    const [emails, setEmails] = useState([]);
    const [notes, setNotes] = useState([]);
    const [lpcos, setLpcos] = useState([]);
    // Add state for dynamic permit types, with the old list as a default
    const [requiredPermitTypes, setRequiredPermitTypes] = useState(['TBS', 'TMDA', 'GCLA', 'ATOMIC']);

    // LPCO authority options — required permit types plus any authorities
    // already saved on this job's LPCOs (e.g. PVOC / TBS DI or manually
    // added permits that aren't HS-code derived), deduplicated by case.
    const lpcoAuthorityOptions = useMemo(() => {
        const seen = new Set();
        const options = [];
        [...requiredPermitTypes, ...lpcos.map((l) => l.authority)].forEach((auth) => {
            const name = (auth || '').trim();
            const key = name.toUpperCase();
            if (name && !seen.has(key)) {
                seen.add(key);
                options.push({ id: name, text: name });
            }
        });
        return options;
    }, [requiredPermitTypes, lpcos]);
    const [manualPermitsBase, setManualPermitsBase] = useState([]);
    const [pvocDiSelections, setPvocDiSelections] = useState([]);

    // Build read-only document entries from DO/drop-off attachments.
    // These come from the same department-attachments endpoint used by the All Attachments modal,
    // so the JobForm Documents section and the modal are always consistent.
    const doAttachmentDocs = useMemo(() => {
        return (doAttachmentItems || []).map(item => ({
            id: `do-attachment-${item.document_file_url}`,
            is_do_attachment: true,
            document_type: item.document_type,
            document_file: item.document_file_url,
            document_file_url: item.document_file_url,
            file_size: item.file_size,
            uploaded_by_name: item.uploaded_by_name || 'Unknown',
            uploaded_by_department: item.uploaded_by_department || 'Unknown',
            created_at: item.created_at,
            fileMeta: item.file_size ? {
                size: item.file_size,
                uploaded: new Date(item.created_at),
                user: item.uploaded_by_name || 'Unknown User'
            } : null,
        }));
    }, [doAttachmentItems]);

    // --- Helper to fetch all ports for a country and return full objects ---
    const fetchPortsByCountryFull = async (countryCode) => {
        if (!countryCode) return [];
        try {
            const response = await fetch(
                `${MASTERS_API_BASE_URL}ports/by_country/?country_code=${countryCode}`,
                { headers: getAuthHeaders() }
            );
            if (response.ok) {
                const ports = await response.json();
                return Array.isArray(ports) ? ports : [];
            }
        } catch (error) {
            console.error('Error fetching ports:', error);
        }
        return [];
    };

    // Load BORDER ports for TZ when shipmentType is IMPORT-ROAD or EXPORT-ROAD
    useEffect(() => {
        if (['IMPORT-ROAD', 'EXPORT-ROAD'].includes(job.shipmentType)) {
            fetchPortsByCountryFull('TZ').then(setPorts);
        } else {
            setPorts([]);
        }
    }, [job.shipmentType]);


    const [canShowContainers, setCanShowContainers] = useState(false);
    const [canShowVehicles, setCanShowVehicles] = useState(false);
    const [canShowLooseCargo, setCanShowLooseCargo] = useState(false);

    const [shippingLinesLoading, setShippingLinesLoading] = useState(false);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [blCarriersLoading, setBlCarriersLoading] = useState(false);

    const [arrivalStatus, setArrivalStatus] = useState(
        initialData?.vessel_details?.ata ? 'HAS_DATE' : null
    );
    const [berthingStatus, setBerthingStatus] = useState(
        initialData?.vessel_details?.atb ? 'HAS_DATE' : null
    );

    const normalizePermitType = (permit = '') => String(permit).toUpperCase().trim();
    const isPvocPermitType = (permit = '') => normalizePermitType(permit).includes('PVOC');
    const isDiPermitType = (permit = '') => {
        const normalized = normalizePermitType(permit);
        return normalized === 'TBS DI';
    };
    const requiresPvocDiChoice = (job.cargoType || '').toUpperCase() !== 'ZNZ' && requiredPermitTypes.some((permit) => isPvocPermitType(permit) || isDiPermitType(permit));
    const showPvocDiChoices = requiresPvocDiChoice || pvocDiSelections.length > 0;

    const handlePvocDiToggle = (permitType) => {
        const normalized = normalizePermitType(permitType);
        setPvocDiSelections((prev) => (
            prev.includes(normalized)
                ? prev.filter((item) => item !== normalized)
                : [...prev, normalized]
        ));
    };

    const handleManualPermitToggle = (permitType) => {
        const normalized = normalizePermitType(permitType);
        setManualPermitsBase((prev) => (
            prev.includes(normalized)
                ? prev.filter((item) => item !== normalized)
                : [...prev, normalized]
        ));
    };

    // File counter for KK File generation
    //const [kkFileCounter, setKkFileCounter] = useState(1);

    // Add state for modal preview
    const [previewFile, setPreviewFile] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null,
        onCancel: null
    });

    // Ex Bond - Previous IDF/TANSAD lookup
    const [previousJobData, setPreviousJobData] = useState(null); // { mbl_no, invoice_no, customer, supplier }
    const [previousLookupResults, setPreviousLookupResults] = useState([]);
    const [previousLookupOpen, setPreviousLookupOpen] = useState(false);
    const [previousLookupLoading, setPreviousLookupLoading] = useState(false);
    const previousLookupTimer = useRef(null);
    const previousLookupBoxRef = useRef(null);

    const tansadDateRef = useRef(null);
    const cbmRef = useRef(null);
    const mblRef = useRef(null);
    const hblSoRef = useRef(null);
    const arrivalDateRef = useRef(null);
    const berthingDateRef = useRef(null);
    const idfRef = useRef(null);
    const tansadNoRef = useRef(null);
    

    // Dropdown options
    const shipmentTypes = [
        'IMPORT-SEA', 'IMPORT-AIR', 'IMPORT-ROAD', 'EXPORT-SEA', 'EXPORT-AIR', 'EXPORT-ROAD',
        'TRANSIT-SEA', 'TRANSIT-AIR', 'EX BOND'
    ];
    
    const cargoTypes = [
        'FCL', 'LCL', 'VEHICLE', 'BREAK BULK', 'EXPORT ENTRY', 'SCT', 'ROAD', 'AIR', 'AIR COURIER'
    ];

    const cargoNatureOptions = ['NORMAL', 'DANGEROUS'];
    
    const dangerousClassOptions = [
        'CLASS 1 - EXPLOSIVES', 
        'CLASS 2 - GASES', 
        'CLASS 3 - FLAMMABLE LIQUIDS',
        'CLASS 4 - FLAMMABLE SOLIDS', 
        'CLASS 5 - OXIDIZING SUBSTANCES',
        'CLASS 6 - TOXIC AND INFECTIOUS SUBSTANCES', 
        'CLASS 7 - RADIOACTIVE MATERIAL',
        'CLASS 8 - CORROSIVE SUBSTANCES', 
        'CLASS 9 - MISCELLANEOUS DANGEROUS SUBSTANCES'
    ];

    const statusOptions = [
        'ATOMIC', 
        'BL / AWB / RCN', 
        'CHEMICAL PERMIT', 
        'EXEMPTION', 
        'LCL SHIPMENT',
        'MISSING INFO / MANIFEST', 
        'MISSING PERMIT', 
        'MISSING / REVISED DOCS',
        'PVOC / TBS', 
        'SHIPPING BILL INDIA', 
        'TMDA PERMIT'
    ];

    const containerTypeOptionsBySize = {
        '20 ST': [
            'DRY', 'REEFER', 'TANK', 'FLAT RACK', 'OPEN TOP', 'OPEN SIDE', 'HALF HEIGHT', 'DOUBLE DOOR', 'HARD TOP'
        ],
        '40 ST': [
            'DRY', 'REEFER', 'FLAT RACK', 'OPEN TOP', 'OPEN SIDE', 'DOUBLE DOOR', 'HARD TOP'
        ],
        '40 HC': [
            'DRY', 'REEFER', 'TANK', 'OPEN TOP', 'OPEN SIDE', 'DOUBLE DOOR', 'HARD TOP'
        ],
        '45 HC': [
            'DRY', 'REEFER'
        ]
    };

    const containerSizeOptions = [
    { id: '20 ST', text: '20 ST' },
    { id: '40 ST', text: '40 ST' },
    { id: '40 HC', text: '40 HC' },
    { id: '45 HC', text: '45 HC' }
    ];
    // const authorities = ['TBS', 'TMDA', 'GCLA', 'ATOMIC'];
    const nominatedByOptions = ['SHIPPER', 'CONSIGNEE', 'NOT APPLICABLE'];
    /* const icdOptions = [
        'AMI',
        'AFICD', 
        'AL-HUSHOOM',
        'AZAM',
        'DICD',
        'ETC CARGO',
        'EAST COST LIQUID',
        'GALCO 025',
        'GALCO 046',
        'JEFAG',
        'KICD',
        'PMM',
        'TRH',
        'HESU',
        'KEFA',
        'SILVER ENTERTRADE',
        'FARION TRADING LTD',
        'TRANS AFRICAN LOGISTICS LIMITED (TALL)',
        'SALISLA',
        'ETC CARGO LIMITED',
        'TANZANIA EAST AFRICA GATEWAY',
        'ROUTE MASTER LOGISTICS COMPANY LIMITED',
        'SSA LOGISTICS',
        'BLOOMER TANZANIA LIMITED',
        'DAR STAR INLAND CONTAINER DEPOT LIMITED',
        'TEAGTL',
        'DP WORLD',
        'ZAMBIA CARGO AND LOGISTICS LIMITED / TZDAR'
    ];*/

    /*const documentTypes = [
        "Assessment notice",
        "Bill of lading / AWB / RCN",
        "C36 form",
        "COA",
        "COO",
        "Commercial invoice",
        "GCLA permit",
        "Livestock permit",
        "Packing list",
        "Payment note",
        "Proforma invoice",
        "TBS permit",
        "TDB permit",
        "TAEC permit",
        "TMDA permit",
        "TRA authorization letter",
        "TRA payment",
        "Wharfage invoice"
    ];*/

    useEffect(() => {
        // When opened from Accepted Report, collapse all but the key sections
        if (showEmailButtonOnly) {
            document.querySelectorAll('.section-content').forEach(el => {
                const sectionId = el.id;
                if (sectionId !== 'basic-section' && sectionId !== 'shipment-details-section') {
                    el.style.display = 'none';
                } else {
                    el.style.display = 'block';
                }
            });
        }
    }, [showEmailButtonOnly]);
   
    useEffect(() => {
        console.log('Port selection debug:', {
            selectedPort: job.portOfLoading,
            availablePorts: cities,
            countrySelected: job.countryOfLoading,
            loadingPorts: loadingCities
        });
    }, [job.portOfLoading, cities, job.countryOfLoading, loadingCities]);

    useEffect(() => {
        setReadOnly(mode === 'view' || openDocumentsOnly);
    }, [mode, openDocumentsOnly]);

    // Load data on component mount
    useEffect(() => {
        fetchDropdownData();
        loadCountries();
        if (initialData?.id) {
            console.log("Loading initial data:", initialData);
            
            // Add a delay to ensure dropdowns are loaded first
            const loadTimer = setTimeout(async () => {
                await populateFormData(initialData); // Make this async
                fetchRepeatableSections(initialData.id);
            }, 300); // Increased delay
            
            return () => clearTimeout(loadTimer);
        }

        setManualPermitsBase([]);
        setPvocDiSelections([]);
    }, [initialData]);

    // Add effect to collapse sections & open documents when requested
     useEffect(() => {
        if (isDocumentsOnlyMode) {
            document.querySelectorAll('.section-content').forEach(el => {
                if (el.id === 'documents-section') el.style.display = 'block';
                else el.style.display = 'none';
            });
            const docSection = document.getElementById('documents-section');
            docSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [isDocumentsOnlyMode]);

    // Effect to fetch required permits when commodities change
    useEffect(() => {
        const fetchRequiredPermits = async () => {
            // Hide required permits for SCT cargo type or EX BOND VEHICLE
            if (job.cargoType === 'SCT' || (job.shipmentType === 'EX BOND' && (job.cargoType || '').toUpperCase() === 'VEHICLE')) {
                setRequiredPermitTypes([]);
                return;
            }

            // For ZNZ, use all permit types from permit-document mappings as choices
            if (job.cargoType === 'ZNZ') {
                try {
                    const res = await fetch(`${ADMIN_API_BASE_URL}permit-document-mappings/`, { headers: getAuthHeaders() });
                    if (res.ok) {
                        const data = await res.json();
                        const allPermitTypes = [...new Set(data.map(m => (m.permit_type || '').toUpperCase()))].sort();
                        setRequiredPermitTypes(allPermitTypes);
                    } else {
                        setRequiredPermitTypes([]);
                    }
                } catch (error) {
                    console.error("Failed to fetch permit types:", error);
                    setRequiredPermitTypes([]);
                }
                return;
            }
            // If there are no commodities or no HS codes, the list should be empty.
            if (commodities.length === 0) {
                setRequiredPermitTypes([]);
                return;
            }

            const uniqueHsCodes = [...new Set(commodities.map(c => c.hs_code).filter(Boolean))];

            if (uniqueHsCodes.length === 0) {
                setRequiredPermitTypes([]);
                return;
            }

            try {
                const res = await fetch(`${ADMIN_API_BASE_URL}permits/get-permits-for-hs-codes/`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ hs_codes: uniqueHsCodes }),
                });

                if (res.ok) {
                    const permitTypes = await res.json();
                    // Only use the permits returned from the API.
                    setRequiredPermitTypes(permitTypes.sort());
                } else {
                    // On failure, reset to an empty list.
                    setRequiredPermitTypes([]);
                }
            } catch (error) {
                console.error("Failed to fetch required permits:", error);
                // On error, reset to an empty list.
                setRequiredPermitTypes([]);
            }
        };

        fetchRequiredPermits();
    }, [commodities, job.cargoType, job.shipmentType]);

    useEffect(() => {
        recalculateCargoTotals();
    }, [containers]);
    
    useEffect(() => {
        console.log('Customer email groups updated:', customerEmailGroups);
        console.log('Number of email groups:', customerEmailGroups.length);
        if (customerEmailGroups.length > 0) {
            console.log('Sample email group:', customerEmailGroups[0]);
        }
    }, [customerEmailGroups]);

    // When data is loaded, load the ports for the selected country
   useEffect(() => {
        if (job.countryOfLoading && job.portOfLoading) {
            console.log('Country:', job.countryOfLoading);
            console.log('Port:', job.portOfLoading);
            console.log('Available ports:', cities);
        }
    }, [job.countryOfLoading, job.portOfLoading, cities]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            // Close dangerous classes dropdown
            const dangerousDropdown = document.getElementById('dangerous-classes-dropdown');
            const dangerousHeader = dangerousDropdown?.previousElementSibling;
            if (dangerousDropdown && !dangerousDropdown.contains(event.target) && !dangerousHeader?.contains(event.target)) {
                dangerousDropdown.classList.remove('open');
            }

            // Close status dropdown
            const statusDropdown = document.getElementById('status-dropdown');
            const statusHeader = statusDropdown?.previousElementSibling;
            if (statusDropdown && !statusDropdown.contains(event.target) && !statusHeader?.contains(event.target)) {
                statusDropdown.classList.remove('open');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

   /* useEffect(() => {
        console.log("Current job state:", job);
        console.log("Containers state:", containers);
        console.log("canShowOtherSections:", canShowOtherSections);
        console.log("canShowContainers:", canShowContainers);
        console.log("canShowVehicles:", canShowVehicles);
        console.log("canShowLooseCargo:", canShowLooseCargo);
    }, [job, containers, canShowOtherSections, canShowContainers, canShowVehicles, canShowLooseCargo]);
    */
    useEffect(() => {
        setCanShowContainers(job.cargoType === 'FCL');
        setCanShowVehicles(job.cargoType === 'VEHICLE');
        setCanShowLooseCargo(job.cargoType === 'LCL' || job.cargoType === 'BREAK BULK');
    }, [job.cargoType]);

    const handleWharfageSent = async () => {
        if (!job.id) return;
        
        try {
            const res = await fetch(`/api/documentation/jobs/${job.id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({ wharfage_sent: true })
            });

            if (!res.ok) {
                throw new Error('Failed to mark job as wharfage sent.');
            }
            
            toast.success('Job marked as Wharfage Sent.');
            onClose(true); // Close modal and trigger refresh in the parent report

        } catch (error) {
            toast.error(error.message);
            console.error("Error marking wharfage as sent:", error);
        }
    };


    const handleCreateDocumentType = async (newTypeName) => {
        const trimmedName = newTypeName.trim();
        if (!trimmedName) return null;
    
        try {
            const res = await fetch(`${MASTERS_API_BASE_URL}document-types/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name: trimmedName }),
            });
    
            if (res.ok) {
                const newDocType = await res.json();
                toast.success(`Document type "${newDocType.name}" saved.`);
                // Add to state if it's not already there
                setDocumentTypes(prev => {
                    if (prev.find(dt => dt.id === newDocType.id)) {
                        return prev;
                    }
                    return [...prev, newDocType].sort((a, b) => a.name.localeCompare(b.name));
                });
                return newDocType; // Return the created/fetched object
            } else {
                const errorData = await res.json();
                throw new Error(errorData.detail || "Failed to create document type.");
            }
        } catch (error) {
            console.error("Error creating document type:", error);
            toast.error(error.message);
            return null;
        }
    };

    const handleSaveDocuments = async () => {
        const jobId = initialData?.id;
        if (!jobId) {
            toast.error("Job ID is missing. Cannot save documents.");
            return;
        }

        setSavingDocuments(true);
        try {
            const success = await saveDocuments(jobId);
            if (success) {
                // Reload documents from API to sync state with server
                const res = await fetch(`${API_BASE_URL}${jobId}/documents/`, {
                    headers: getAuthHeaders()
                });
                if (res.ok) {
                    const data = await res.json();
                    const processedDocuments = data.map(doc => ({
                        ...doc,
                        file_obj: null,
                        local_preview_url: null,
                        fileMeta: doc.file_size ? {
                            size: doc.file_size,
                            uploaded: new Date(doc.created_at),
                            user: doc.uploaded_by_name || 'Unknown User'
                        } : null
                    }));
                    setDocuments(processedDocuments);
                }
                toast.success("Documents saved successfully!");
            } else {
                toast.error("Failed to save documents.");
            }
        } catch (error) {
            console.error("Error saving documents:", error);
            toast.error("Error saving documents.");
        } finally {
            setSavingDocuments(false);
        }
    };

    const populateFormData = async (data) => {
        console.log('Populating form with data:', data); // Debug log
        const shipmentType = data.shipment_type || '';
        const existingManualPermits = Array.isArray(data.manual_permits) ? data.manual_permits : [];
        const isZnzCargo = (data.cargo_type || '').toUpperCase() === 'ZNZ';
        if (isZnzCargo) {
            setPvocDiSelections([]);
            setManualPermitsBase(existingManualPermits.map(normalizePermitType));
        } else {
            const existingPvocDiSelections = [];
            if (existingManualPermits.some((permit) => isPvocPermitType(permit))) {
                existingPvocDiSelections.push('PVOC');
            }
            if (existingManualPermits.some((permit) => isDiPermitType(permit))) {
                existingPvocDiSelections.push('TBS DI');
            }
            setPvocDiSelections(existingPvocDiSelections);
            setManualPermitsBase(existingManualPermits.filter((permit) => !isPvocPermitType(permit) && !isDiPermitType(permit)));
        }
        setJob({
            ...initialJobState,
            ...data,
        
            // Basic Information
            shipmentType: shipmentType,
            cargoType: data.cargo_type || '',
            cargoNature: data.cargo_nature || 'NORMAL',
            // Fix: Safely handle arrays with proper fallbacks
            dangerousClasses: Array.isArray(data.dangerous_classes) 
                ? data.dangerous_classes.map(item => String(item).toUpperCase()) 
                : [],
            emailSubject: data.email_subject || '',
            status: Array.isArray(data.status) 
                ? data.status.map(item => String(item).toUpperCase()) 
                : [],
            statusRemark: data.status_remark || '',
            
            // Related entities
            customer: data.customer || '',
            supplier: data.supplier || '',
            voyage: data.voyage || '',
            customerRefNo: data.customer_ref || '',
            shippingLineId: data.shipping_line || '',
            blCarrierId: data.bl_carrier || '',
            
            // Shipment Details - Map all the snake_case fields to camelCase
            mblNo: data.mbl_no || '',
            invoiceNo: data.invoice_no || '',
            kkFileNo: data.kk_file_no || '',
            hblSoNo: data.hbl_so_no || '',
            idfNo: (data.idf_no || '').replace(/\s*\(TASAC\)\s*/gi, '').trim(),
            tansadNo: data.tansad_no || '',
            tansadDate: data.tansad_date || null,
            ucrNo: data.ucr_no || '',
            ucrDate: data.ucr_date || null,
            tasac: data.tasac || false,
            exemptionRefNo: data.exemption_ref_no || '',

            // Ex Bond - Previous IDF/TANSAD tracking
            previousDocType: data.previous_doc_type || '',
            previousIdfNo: data.previous_idf_no || '',
            previousTansadNo: data.previous_tansad_no || '',
            exBondType: data.ex_bond_type || '',
            changeOfOwnership: data.change_of_ownership || false,
            
        
            // receiptDate: data.receipt_date || '',
            
            // Supplier & Consignee/Notify Details
            supplierDetails: data.supplier_details ? {
                name: data.supplier_details.name || '',
                address: data.supplier_details.address || '',
                city: data.supplier_details.city || '',
                country: data.supplier_details.country || ''
            } : { name: '', address: '', city: '', country: '' },
            
            // Handle boolean values properly - convert to true/false or keep null
           //  consigneeSameAsCustomer: data.consignee_same_as_customer === null ? null : 
           //                         data.consignee_same_as_customer === true ? true : false,
           // Updated structure: sameAsCustomer is now part of the JSON objects
            consignee: data.consignee ? {
                name: data.consignee.name || '',
                address: data.consignee.address || '',
                city: data.consignee.city || '',
                country: data.consignee.country || '',
                sameAsCustomer: data.consignee.sameAsCustomer !== undefined ? data.consignee.sameAsCustomer : null
            } : { name: '', address: '', city: '', country: '', sameAsCustomer: null },
            
            // notifySameAsCustomer: data.notify_same_as_customer === null ? null : 
            //                    data.notify_same_as_customer === true ? true : false,
            notify: data.notify ? {
                name: data.notify.name || '',
                address: data.notify.address || '',
                city: data.notify.city || '',
                country: data.notify.country || '',
                sameAsCustomer: data.notify.sameAsCustomer !== undefined ? data.notify.sameAsCustomer : null
            } : data.notify_party ? {
                name: data.notify_party.name || '',
                address: data.notify_party.address || '',
                city: data.notify_party.city || '',
                country: data.notify_party.country || '',
                sameAsCustomer: data.notify_party.sameAsCustomer !== undefined ? data.notify_party.sameAsCustomer : null
            } : { 
                name: '', 
                address: '', 
                city: '', 
                country: '', 
                sameAsCustomer: null 
            },
            // Vessel details from nested objects or direct fields
            firstVessel: data.vessel_details?.first_vessel || '',
            firstVoyageNumber: data.vessel_details?.first_voyage_number || '',
            shippedOnBoardDate: data.vessel_details?.shipped_on_board_date || '',
            shippingLine: data.vessel_details?.shipping_line || '',
            sameAsFinalVessel: data.vessel_details?.same_as_final_vessel || false,
            finalVessel: data.vessel_details?.final_vessel || '',
            finalVoyageNumber: data.vessel_details?.final_voyage_number || '',
            
            // Arrival details
            eta: data.vessel_details?.eta || '',
            etb: data.vessel_details?.etb || '',
            arrivalDate: data.vessel_details?.arrival_date || data.vessel_details?.ata || '',
            berthingDate: data.vessel_details?.berthing_date || data.vessel_details?.atb || '',
            berthedAt: data.vessel_details?.berthed_at || '',
            icd: data.vessel_details?.icd || '',
            icdTransferDate: data.vessel_details?.icd_transfer_date || '',
            
            
            // Cargo Details
            countryOfLoading: data.cargo_details?.country_of_loading || data.country_of_loading || '',
            portOfLoading: (() => {
            const portData = data.cargo_details?.port_of_loading || data.port_of_loading;
                if (typeof portData === 'object' && portData?.text) {
                    return portData.text; // Extract text from object
                }
                return portData || ''; // Use as string if already string
            })(),
            portOfDischarge: data.cargo_details?.port_of_discharge || data.port_of_discharge || 'DAR ES SALAAM',
            nominatedBy: data.cargo_details?.nominated_by || data.nominated_by || '',
            folderName: data.cargo_details?.folder_name || '', // Load Folder Name
            nominatedIcd: data.cargo_details?.nominated_icd || data.nominated_icd || '',
            marks: data.cargo_details?.marks || data.marks || '',
            description: data.cargo_details?.description || data.description || '',
            portMaterial: data.cargo_details?.port_material || '',
            totalContainers: data.cargo_details?.total_containers || data.total_containers || '',
            totalPackages: data.cargo_details?.total_packages || data.total_packages || '',
            packageType: data.cargo_details?.package_type || data.package_type || '',
            totalGrossWeight:
                data.cargo_details?.total_gross_weight ??
                data.cargo_details?.total_gross ??
                data.total_gross_weight ??
                data.total_gross ??
                '',
            totalTare:
                data.cargo_details?.total_tare ??
                data.cargo_details?.total_tare_weight ??
                data.total_tare ??
                data.total_tare_weight ??
                '0',
            totalWeight: data.cargo_details?.total_weight || data.total_weight || '',
            lclContainerNo:
                data.loose_cargo?.[0]?.container_no ||
                data.cargo_details?.loose_cargo?.container_no || '',
            lclCbm:
                data.loose_cargo?.[0]?.cbm != null
                ? String(data.loose_cargo[0].cbm)
                : (data.cargo_details?.loose_cargo?.cbm != null
                    ? String(data.cargo_details.loose_cargo.cbm)
                    : ''),
            lclPackages:
                data.loose_cargo?.[0]?.packages != null
                ? String(data.loose_cargo[0].packages)
                : (data.cargo_details?.loose_cargo?.packages != null
                    ? String(data.cargo_details.loose_cargo.packages)
                    : ''),
            lclPackageType:
                data.loose_cargo?.[0]?.package_type ||
                data.cargo_details?.loose_cargo?.package_type || '',
            lclGrossWeight:
                data.loose_cargo?.[0]?.gross_weight != null
                ? String(data.loose_cargo[0].gross_weight)
                : (data.cargo_details?.loose_cargo?.gross_weight != null
                    ? String(data.cargo_details.loose_cargo.gross_weight)
                    : ''),

        });
        setArrivalStatus(
            (data.vessel_details?.arrival_date || data.vessel_details?.ata) ? 'HAS_DATE' : null
        );
        setBerthingStatus(
            (data.vessel_details?.berthing_date || data.vessel_details?.atb) ? 'HAS_DATE' : null
        );


        if (shipmentType && data.cargo_type) {
            const validCargoTypes = getFilteredCargoTypes(shipmentType);
            if (!validCargoTypes.includes(data.cargo_type)) {
                console.warn(`Cargo type "${data.cargo_type}" is not valid for shipment type "${shipmentType}"`);
                // Optionally, you could clear the cargo type or show a warning
            }
        }

        // Load BL carriers if shipping line is selected
       if (data.shipping_line) {
            console.log('Loading BL carriers for shipping line:', data.shipping_line);
            console.log('BL carrier to select:', data.bl_carrier);
            
            // Load BL carriers and then set the BL carrier
            loadBLCarriersAndSetValue(data.shipping_line, data.bl_carrier);
        }

        // Load ports if country is selected
        const countryCode = data.cargo_details?.country_of_loading || data.country_of_loading;
        if (countryCode) {
            console.log('Loading ports for country:', countryCode);
            // Load ports with the selected port value
            const selectedPort = (() => {
                const portData = data.cargo_details?.port_of_loading || data.port_of_loading;
                if (typeof portData === 'object' && portData?.text) {
                    return portData.text;
                }
                return portData || '';
            })();
            
            if (['IMPORT-AIR', 'IMPORT-ROAD', 'EXPORT-ROAD'].includes(shipmentType)) {
                updateCitiesWithSelection(countryCode, selectedPort);
            } else {
                updatePortsWithSelection(countryCode, selectedPort);
            }
        }

        // Fetch customer email groups if customer is selected
        if (data.customer) {
            try {
                const response = await fetch(`${MASTERS_API_BASE_URL}customers/${data.customer}/`, {
                    headers: getAuthHeaders()
                });
                
                if (response.ok) {
                    const customerData = await response.json();
                    const emailGroups = customerData.email_groups || [];
                    setCustomerEmailGroups(emailGroups);
                    console.log('Loaded email groups for existing job:', emailGroups);
                }
            } catch (error) {
                console.error('Error fetching customer email groups for existing job:', error);
                setCustomerEmailGroups([]);
            }
        }

        // If this is an existing Ex Bond job, try to resolve and load the previous job's documents
        if (shipmentType === 'EX BOND' && data.previous_doc_type) {
            const docType = (data.previous_doc_type || '').toUpperCase();
            const term = docType === 'IDF' ? (data.previous_idf_no || '') : (data.previous_tansad_no || '');
            if (term) {
                try {
                    const res = await fetch(
                        `${API_BASE_URL}previous-job-lookup/?doc_type=${docType}&q=${encodeURIComponent(term)}`,
                        { headers: getAuthHeaders() }
                    );
                    if (res.ok) {
                        const results = await res.json();
                        const termUpper = term.toUpperCase();
                        const match = (results || []).find(r =>
                            (docType === 'IDF' && (r.idf_no || '').toUpperCase() === termUpper) ||
                            (docType === 'TANSAD' && (r.tansad_no || '').toUpperCase() === termUpper)
                        );
                        if (match) {
                            setPreviousJobData(match);
                            fetchPreviousJobDocuments(match.id);
                        }
                    }
                } catch (err) {
                    console.error('Error resolving previous job for Ex Bond documents on edit:', err);
                }
            }
        }
    };

    const getContainerSummary = () => {
        const counts = containers.reduce((acc, c) => {
            const size = (c.size || '').trim().toUpperCase();
            const type = (c.container_type || '').trim().toUpperCase();
            if (size && type) {
                const key = `${size} - ${type}`;
                acc[key] = (acc[key] || 0) + 1;
            }
            return acc;
        }, {});
        return Object.entries(counts)
            .map(([k, count]) => `${count} x ${k}`)   // e.g. "2 x 20 ST x DRY"
            .join(' | ') || 'No containers';
    };


     const loadBLCarriersAndSetValue = async (shippingLineId, blCarrierId) => {
        try {
            console.log('🔄 Loading BL carriers for shipping line:', shippingLineId);
            
            const response = await fetch(`${MASTERS_API_BASE_URL}shippinglines/${shippingLineId}/blcarriers/`, {
                headers: getAuthHeaders()
            });
            
            if (response.ok) {
                const blCarriersData = await response.json();
                console.log('✅ BL Carriers loaded:', blCarriersData);
                
                setBlCarriers(Array.isArray(blCarriersData) ? blCarriersData : []);
                
                // After loading BL carriers, set the BL carrier value
                if (blCarrierId) {
                    console.log('🎯 Setting BL carrier to:', blCarrierId);
                    
                    // Small delay to ensure state is updated
                    setTimeout(() => {
                        setJob(prev => ({
                            ...prev,
                            blCarrierId: blCarrierId
                        }));
                    }, 100);
                }
                
                // Also load voyages for the shipping line
                const voyagesResponse = await fetch(`${MASTERS_API_BASE_URL}shippinglines/${shippingLineId}/voyages/`, {
                    headers: getAuthHeaders()
                });
                
                if (voyagesResponse.ok) {
                    const voyagesData = await voyagesResponse.json();
                    console.log('✅ Voyages loaded:', voyagesData);
                    
                    const formattedVoyages = voyagesData.map(voyage => ({
                        ...voyage,
                        display_name: `${voyage.bl_carrier || 'N/A'} - ${voyage.voyage_number || 'N/A'} - ${voyage.vessel_name || voyage.vessel || 'N/A'}`
                    }));
                    
                    setFilteredVoyages(formattedVoyages);
                }
                
            } else {
                console.error('❌ Failed to load BL carriers:', response.status);
                setBlCarriers([]);
            }
        } catch (error) {
            console.error('❌ Error loading BL carriers:', error);
            setBlCarriers([]);
        }
    };

    const fetchShippingLines = async () => {
        setShippingLinesLoading(true);
        try {
            const res = await fetch(`${MASTERS_API_BASE_URL}shippinglines/`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setShippingLines(data.results || data);
            }
        } catch (err) {
            setError('Failed to load shipping lines');
        } finally {
            setShippingLinesLoading(false);
        }
    };

    const fetchBLCarriers = async (shippingLineId) => {
        setBlCarriersLoading(true);
        try {
            const res = await fetch(`${MASTERS_API_BASE_URL}shippinglines/${shippingLineId}/blcarriers/`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setBlCarriers(data.results || data);
            }
        } catch (err) {
            setError('Failed to load BL carriers');
        } finally {
            setBlCarriersLoading(false);
        }
    };

    const fetchCustomers = async () => {
        setCustomersLoading(true);
        try {
            const res = await fetch(`${MASTERS_API_BASE_URL}customers/`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setCustomers(data.results || data);
            }
        } catch (err) {
            setError('Failed to load customers');
        } finally {
            setCustomersLoading(false);
        }
    };

    const fetchSuppliers = async () => {
        setSuppliersLoading(true);
        try {
            const res = await fetch(`${MASTERS_API_BASE_URL}suppliers/`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setSuppliers(data.results || data);
            }
        } catch (err) {
            setError('Failed to load suppliers');
        } finally {
            setSuppliersLoading(false);
        }
    };
    

    const fetchDropdownData = async () => {
        try {
            const [customersRes, suppliersRes, shippingLinesRes, packageTypesRes, icdOptionsRes, docTypesRes] = await Promise.all([
                fetch(`${MASTERS_API_BASE_URL}customers/`, { headers: getAuthHeaders() }),
                fetch(`${MASTERS_API_BASE_URL}suppliers/`, { headers: getAuthHeaders() }),
                fetch(`${MASTERS_API_BASE_URL}shippinglines/`, { headers: getAuthHeaders() }),
                fetch(`${MASTERS_API_BASE_URL}package-types/`, { headers: getAuthHeaders() }),
                fetch(`${MASTERS_API_BASE_URL}icds/`, { headers: getAuthHeaders() }),
                fetch(`${MASTERS_API_BASE_URL}document-types/?department_name=${encodeURIComponent(documentTypeDepartment)}`, { headers: getAuthHeaders() }),
            ]);

            console.log('✅ API Responses received:', {
                customers: customersRes.status,
                suppliers: suppliersRes.status,
                shippingLines: shippingLinesRes.status,
                packageTypes: packageTypesRes.status,
                icdOptions: icdOptionsRes.status,
                docTypes: docTypesRes.status
            });

            if (customersRes.ok) {
                const customersData = await customersRes.json();
                // Handle both paginated and non-paginated responses
                setCustomers(customersData.results || customersData);
            }
            
            if (suppliersRes.ok) {
                const suppliersData = await suppliersRes.json();
                // Handle both paginated and non-paginated responses
                setSuppliers(suppliersData.results || suppliersData);
            }            
            
            if (shippingLinesRes.ok) {
                const shippingLinesData = await shippingLinesRes.json();
                setShippingLines(shippingLinesData.results || shippingLinesData);
                console.log('Shipping lines data:', shippingLinesData.results || shippingLinesData);
            } 
            
            if (packageTypesRes.ok) {
                const packageTypesData = await packageTypesRes.json();
                setPackageTypes(packageTypesData.results || packageTypesData);
            }

            if (icdOptionsRes.ok) {
                const icdData = await icdOptionsRes.json();
                setIcdOptions(icdData.results || icdData);
            }

            if (docTypesRes.ok) {
                const docTypesData = await docTypesRes.json();
                const types = docTypesData.results || docTypesData;
                if (isWharfageOnly) {
                    const wharfageTypes = types.filter(t => (t.name || '').toLowerCase() === 'wharfage invoice');
                    setDocumentTypes(wharfageTypes.length ? wharfageTypes : [{ name: 'Wharfage Invoice' }]);
                } else {
                    setDocumentTypes(types);
                }
            }

        } catch (err) {
            console.error('Error fetching dropdown data:', err);
            setError('Failed to load dropdown data');
        }
    };

    const fetchRepeatableSections = async (jobId) => {
        try {
            console.log("Fetching repeatable sections for job:", jobId);
            console.log("API base URL:", API_BASE_URL);
                    
            const results = await Promise.allSettled([
                fetch(`${API_BASE_URL}${jobId}/containers/`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE_URL}${jobId}/commodities/`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE_URL}${jobId}/documents/`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE_URL}${jobId}/notes/`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE_URL}${jobId}/lpcos/`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE_URL}${jobId}/letters/`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE_URL}${jobId}/emails/`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE_URL}${jobId}/loose-cargo/`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE_URL}${jobId}/vehicles/`, { headers: getAuthHeaders() }),
                fetch(`${API_BASE_URL}${jobId}/department-attachments/?department=ALL`, { headers: getAuthHeaders() }),
            ]);

            const [containersResult, commoditiesResult, documentsResult, notesResult, lpcosResult, lettersResult, emailsResult, looseCargoResult, vehiclesResult, deptAttachmentsResult] = results;

            if (looseCargoResult.status === 'fulfilled' && looseCargoResult.value.ok) {
                const lcData = await looseCargoResult.value.json();
                if (Array.isArray(lcData) && lcData.length) {
                    const first = lcData[0];
                    setJob(prev => ({
                        ...prev,
                        lclContainerNo: first.container_no || '',
                        lclCbm: first.cbm != null ? String(first.cbm) : '',
                        lclPackages: first.packages != null ? String(first.packages) : '',
                        lclPackageType: first.package_type || '',
                        lclGrossWeight: first.gross_weight != null ? String(first.gross_weight) : '',
                    }));
                }
            }

            // Process vehicles result
            if (vehiclesResult.status === 'fulfilled' && vehiclesResult.value.ok) {
                const vehicleData = await vehiclesResult.value.json();
                console.log("Vehicle data loaded:", vehicleData);
                if (Array.isArray(vehicleData) && vehicleData.length > 0) {
                    setVehicles(vehicleData);
                }
            } else if (vehiclesResult.status === 'rejected') {
                console.error("Vehicle request failed:", vehiclesResult.reason);
            }

            // Process containers result
             if (containersResult.status === 'fulfilled') {
                const response = containersResult.value; // This is the actual fetch response
                if (response.ok) {
                    const containerData = await response.json();
                    console.log("Container data loaded:", containerData);
                    setContainers(
                        containerData.length > 0
                            ? containerData
                            : [{
                                container_no: '',
                                size: '',
                                container_type: '',
                                seal_no: '',
                                gross_weight: '',
                                tare_weight: '',
                                kk_seal_no: ''
                            }]
                    );
                } else {
                    // Include more details in the error message
                    console.error(`Failed to load containers: HTTP ${response.status} ${response.statusText}`);
                    // Try to get error details from response
                    try {
                        const errorText = await response.text();
                        console.error("Error details:", errorText);
                    } catch (e) {
                        console.error("Could not read error details");
                    }
                }
            } else {
                console.error("Container request failed:", containersResult.reason);
            }

            // Process commodities result
            if (commoditiesResult.status === 'fulfilled') {
                const response = commoditiesResult.value; // This is the actual fetch response
                if (response.ok) {
                    const commodityData = await response.json();
                    console.log("Commodity data loaded:", commodityData);
                    // Format HS codes in the loaded data
                    const formattedCommodities = commodityData.map(commodity => ({
                        ...commodity,
                        hs_code: formatHSCode(commodity.hs_code)
                    }));
                
                setCommodities(formattedCommodities);
                } else {
                    // Include more details in the error message
                    console.error(`Failed to load commodities: HTTP ${response.status} ${response.statusText}`);
                    // Try to get error details from response
                    try {
                        const errorText = await response.text();
                        console.error("Error details:", errorText);
                    } catch (e) {
                        console.error("Could not read error details");
                    }
                }
            } else {
                console.error("Commodity request failed:", commoditiesResult.reason);
            }

            // Process documents result
            if (documentsResult.status === 'fulfilled') {
                const response = documentsResult.value;
                if (response.ok) {
                    const documentsData = await response.json();
                    console.log("Documents data loaded:", documentsData);
                    
                    // Process existing documents to ensure proper file paths
                    let processedDocuments = documentsData.map(doc => ({
                        ...doc,
                        // Keep the original document_file path for display/download
                        // Don't set file_obj for existing documents
                        fileMeta: doc.file_size ? {
                            size: doc.file_size,
                            uploaded: new Date(doc.created_at),
                            user: doc.uploaded_by_name || 'Unknown User'
                        } : null
                    }));

                    setDocuments(processedDocuments);
                }
            }

            // Process notes result
            if (notesResult.status === 'fulfilled') {
                const response = notesResult.value;
                if (response.ok) {
                    setNotes(await response.json());
                }
            }

            // Process lpco result
            if (lpcosResult.status === 'fulfilled') {
                const response = lpcosResult.value;
                if (response.ok) {
                    setLpcos(await response.json());
                }
            }
            
            // Process letters result
            if (lettersResult.status === 'fulfilled') {
                const response = lettersResult.value;
                if (response.ok) {
                    const lettersData = await response.json();
                    console.log("Letters data loaded:", lettersData);
                    setLetters(lettersData);
                } else {
                    console.error(`Failed to load letters: HTTP ${response.status} ${response.statusText}`);
                }
            } else {
                console.error("Letters request failed:", lettersResult.reason);
            }

            // Process department attachments (includes DO/drop-off attachments)
            if (deptAttachmentsResult.status === 'fulfilled') {
                const response = deptAttachmentsResult.value;
                if (response.ok) {
                    const deptData = await response.json();
                    console.log("Department attachments loaded:", deptData);
                    setDoAttachmentItems(deptData.do_attachments || []);
                } else {
                    console.error(`Failed to load department attachments: HTTP ${response.status} ${response.statusText}`);
                }
            } else {
                console.error("Department attachments request failed:", deptAttachmentsResult.reason);
            }

            // Process emails result
             if (emailsResult.status === 'fulfilled') {
                const response = emailsResult.value;
                if (response.ok) {
                    const emailsData = await response.json();
                    console.log("Emails data loaded:", emailsData);
                    setEmails(emailsData);
                    setInitialEmailCount(emailsData.length); // <-- ADD THIS LINE
                } else {
                    console.error(`Failed to load emails: HTTP ${response.status} ${response.statusText}`);
                    try {
                        const errorText = await response.text();
                        console.error("Email error details:", errorText);
                    } catch (e) {
                        console.error("Could not read email error details");
                    }
                }
            } else {
                console.error("Emails request failed:", emailsResult.reason);
            }
        } catch (err) {
            console.error('Error fetching repeatable sections:', err);
        }
    };

    const loadCountries = useCallback(async () => {
        try {
            // Add a simple cache to avoid reloading countries every time
            const cachedCountries = sessionStorage.getItem('countries_cache_v2');
            if (cachedCountries) {
                const parsed = JSON.parse(cachedCountries);
                if (parsed.timestamp && Date.now() - parsed.timestamp < 300000) { // 5 minutes cache
                    setCountries(parsed.data);
                    return;
                }
            }
            
            const response = await fetch(`${MASTERS_API_BASE_URL}countries/`, { headers: getAuthHeaders() });
            const data = await response.json();
            const countryList = Array.isArray(data) ? data : (data.results || []);
            
            sessionStorage.setItem('countries_cache_v2', JSON.stringify({
                data: countryList,
                timestamp: Date.now()
            }));
            
            setCountries(countryList);
        } catch (err) {
            console.error('Failed to load countries:', err);
        }
    }, []);

    const updateCitiesWithSelection = useCallback(async (countryCode, selectedCity = '') => {
        if (!countryCode) {
            setCities([]);
            return;
        }
        
        setLoadingCities(true);
        setCities([]);

        try {
            const response = await fetch(`https://secure.geonames.org/searchJSON?country=${countryCode}&featureClass=P&maxRows=1000&username=${GEONAMES_USERNAME}`);
            const data = await response.json();
            if (data.geonames && data.geonames.length > 0) {
                const cityList = [...new Set(data.geonames.map(city => city.name))].sort();
                setCities(cityList);

                if (selectedCity) {
                    const exactMatch = cityList.find(city => 
                        city.trim().toUpperCase() === selectedCity.trim().toUpperCase()
                    );
                    if (exactMatch) {
                        setTimeout(() => {
                            setJob(prev => ({
                                ...prev,
                                portOfLoading: exactMatch
                            }));
                        }, 200);
                    }
                }
            } else {
                setCities([]);
            }
        } catch (err) {
            console.error("Failed to load cities:", err);
            setCities([]);
        } finally {
            setLoadingCities(false);
        }
    }, [GEONAMES_USERNAME]);

    const updateCities = useCallback(async (countryCode) => {
        if (!countryCode) {
            setCities([]);
            return;
        }
        try {
            const response = await fetch(`https://secure.geonames.org/searchJSON?country=${countryCode}&featureClass=P&maxRows=1000&username=${GEONAMES_USERNAME}`);
            const data = await response.json();
            if (data.geonames && data.geonames.length > 0) {
                const cityList = [...new Set(data.geonames.map(city => city.name))].sort();
                setCities(cityList);
            } else {
                setCities([]);
            }
        } catch (err) {
            console.error("Failed to load cities:", err);
        }
    }, []);

    const updatePorts = useCallback(async (countryCode) => {
        if (!countryCode) {
            setCities([]);
            return;
        }
        
        setLoadingCities(true);
        setCities([]);
        
        try {
            console.log(`Fetching ports for country: ${countryCode}`);
            
            // Get ports from database
            const ports = await getPortsByCountry(countryCode);
            
            if (ports.length > 0) {
                console.log(`Found ${ports.length} ports from database`);
                setCities(ports.sort());
            } else {
                console.log('No ports found in database for this country');
                setCities([]);
            }
            
        } catch (err) {
            console.error("Failed to load ports:", err);
            setCities([]);
        } finally {
            setLoadingCities(false);
        }
    }, []);

    const updatePortsWithSelection = useCallback(async (countryCode, selectedPort = '') => {
        if (!countryCode) {
            setCities([]);
            return;
        }
        
        setLoadingCities(true);
        setCities([]);
        
        try {
            console.log(`Fetching ports for country: ${countryCode}, selected port: ${selectedPort}`);
            
            // Get ports from database
            const ports = await getPortsByCountry(countryCode);
            
            if (ports.length > 0) {
                console.log(`Found ${ports.length} ports from database`);
                const sortedPorts = ports.sort();
                setCities(sortedPorts);
                
                // If we have a selected port, find it with case-insensitive matching
                if (selectedPort) {
                    console.log(`Looking for selected port: "${selectedPort}"`);
                    
                    // Find matching port (case-insensitive)
                    const exactMatch = ports.find(port => 
                        port.trim().toUpperCase() === selectedPort.trim().toUpperCase()
                    );
                    
                    if (exactMatch) {
                        console.log(`✅ Found matching port: "${exactMatch}"`);
                        // Use setTimeout to ensure the cities state is updated first
                        setTimeout(() => {
                            setJob(prev => ({
                                ...prev,
                                portOfLoading: exactMatch  // Use the exact case from the database
                            }));
                            console.log(`✅ Port set to: "${exactMatch}"`);
                        }, 200);
                    } else {
                        console.warn(`❌ Port "${selectedPort}" not found in available ports:`, ports);
                    }
                }
            } else {
                console.log('No ports found in database for this country');
                setCities([]);
            }
            
        } catch (err) {
            console.error("Failed to load ports:", err);
            setCities([]);
        } finally {
            setLoadingCities(false);
        }
    }, []);

   const toggleSection = (sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) {
            console.log(`Toggling section ${sectionId}, current display: ${section.style.display}`);
            section.style.display = section.style.display === 'none' ? 'block' : 'none';
            console.log(`Section ${sectionId} new display: ${section.style.display}`);
        } else {
            console.warn(`Section with ID ${sectionId} not found`);
        }
    };

    const expandAll = () => {
        document.querySelectorAll('.section-content').forEach(el => el.style.display = 'block');
    };

    const collapseAll = () => {
        document.querySelectorAll('.section-content').forEach(el => {
            if (el.parentElement.querySelector('h3').textContent !== 'Shipment / Cargo Type') {
                el.style.display = 'none';
            }
        });
    };

    // Add these functions to your JobForm.js file

    // Helper to format IDF No. as 1234567-YY (YY = current year)
    const formatIdfNoInput = (e) => {
        const hasTasac = /\(TASAC\)/i.test(e.target.value);
        let val = e.target.value.replace(/\s*\(TASAC\)\s*/gi, '').replace(/\D/g, ''); // Strip TASAC suffix, then only digits
        const year = new Date().getFullYear().toString().slice(-2); // Last 2 digits
        if (val.length > 7) val = val.slice(0, 7);
        let formatted = val.length === 7 ? val + '-' + year : val;
        e.target.value = hasTasac ? formatted + ' (TASAC)' : formatted;
    };

    // Previous IDF/TANSAD: OLD = free-form (user controls the year); IDF/TANSAD keep the auto-year format
    const formatPreviousIdfNoInput = (e) => {
        if (job.previousDocType === 'OLD') return;
        formatIdfNoInput(e);
    };

    const formatPreviousTansadNoInput = (e) => {
        if (job.previousDocType === 'OLD') return;
        formatTansadNoInput(e);
    };

    // Helper to format TANSAD No. as TZDL-YY-1234567
    const formatTansadNoInput = (e) => {
        // Remove dashes, force uppercase
        let raw = e.target.value.replace(/-/g, '').toUpperCase();
        const year = new Date().getFullYear().toString().slice(-2);

        // Extract prefix (first 4 alphanumeric)
        let prefix = raw.slice(0, 4).replace(/[^A-Z0-9]/g, '');

        // Extract digits after the prefix and year (ignore any user-typed year)
        let afterPrefix = raw.slice(4);
        // Remove any leading year digits if user tries to type them
        if (afterPrefix.startsWith(year)) {
            afterPrefix = afterPrefix.slice(2);
        }
        let digits = afterPrefix.replace(/\D/g, '').slice(0, 7);

        let formatted = prefix;
        if (prefix.length === 4) {
            formatted += '-' + year + '-';
            formatted += digits;
        }
        e.target.value = formatted;
    };

    const formatHSCode = (hsCode) => {
        if (!hsCode) return '';
        
        // Convert to string and remove any existing dots and spaces
        const cleaned = String(hsCode).replace(/[.\s]/g, '');
        
        // Ensure it's numeric and has the right length
        if (!/^\d{6,8}$/.test(cleaned)) {
            return String(hsCode); // Return original if not valid format
        }
        
        // Format as xxxx.xx.xx
        if (cleaned.length >= 6) {
            const formatted = cleaned.substring(0, 4) + '.' + 
                            cleaned.substring(4, 6) + '.' + 
                            cleaned.substring(6, 8);
            return formatted;
        }
        
        return String(hsCode); // Return original if too short
    };

    const formatAccounting = (value) => {
        if (value === undefined || value === null || value === '') return '';
        const num = Number(value);
        if (isNaN(num)) return value;
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const handleInputChange = (field, value) => {
        if (readOnly) return;
        
        let finalValue = value;
        if (typeof value === 'string' && field !== 'emailSubject' && field !== 'password' && field !== 'username' && field !== 'portOfLoading') {
            finalValue = value.toUpperCase();
        }

        setJob(prev => {
            const newJob = {
                ...prev,
                [field]: finalValue,
                // Clear port when country changes
                ...(field === 'countryOfLoading' ? { portOfLoading: '' } : {})
            };

            // Calculate total weight if gross or tare weight changes
            if (field === 'totalGrossWeight' || field === 'totalTare') {
                const grossWeight = parseFloat(field === 'totalGrossWeight' ? finalValue : prev.totalGrossWeight) || 0;
                const tareWeight = parseFloat(field === 'totalTare' ? finalValue : prev.totalTare) || 0;
                newJob.totalWeight = (grossWeight + tareWeight).toString();
            }


            return newJob;
        });

        // Handle specific field logic
        if (field === 'shipmentType') {
            handleShipmentTypeChange(value);
        } else if (field === 'customer') {
            handleCustomerChange(value);
        } else if (field === 'supplier') {
            handleSupplierChange(value);
        } else if (field === 'countryOfLoading') {
            if (['IMPORT-AIR', 'IMPORT-ROAD', 'EXPORT-ROAD'].includes(job.shipmentType)) {
                updateCitiesWithSelection(value, '');
            } else {
                updatePortsWithSelection(value, ''); 
            }
        } 
    };

    const handleNestedChange = (section, field, value) => {
        if (readOnly) return;
        
        let finalValue = value;
        if (typeof value === 'string' && field !== 'emailSubject') {
            finalValue = value.toUpperCase();
        }

        setJob(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: finalValue
            }
        }));
    };

   const handleShipmentTypeChange = (value) => {
    // Clear cargo type when shipment type changes
    setJob(prev => ({ ...prev, cargoType: '' }));

    if (value === 'IMPORT-ROAD') {
        setJob(prev => ({ ...prev, cargoType: 'ROAD' }));
    }

    // Shipping Line, Nomination and Container details only apply to IMPORT-SEA / EXPORT-SEA
    if (!['IMPORT-SEA', 'EXPORT-SEA'].includes(value)) {
        setJob(prev => ({
            ...prev,
            shippingLineId: '',
            blCarrierId: '',
            voyage: '',
            nominatedBy: '',
            folderName: '',
            nominatedIcd: '',
            totalContainers: '',
            vesselDetails: { ...prev.vesselDetails, shippingLine: '', vessel: '', voyage: '' },
        }));
        setBlCarriers([]);
        setContainers([{ id: 1, container_no: '', size: '', type: '' }]);
    }

    if (job.countryOfLoading) {
        if (['IMPORT-AIR', 'IMPORT-ROAD', 'EXPORT-ROAD'].includes(value)) {
            updateCitiesWithSelection(job.countryOfLoading, '');
        } else {
            updatePortsWithSelection(job.countryOfLoading, '');
        }
    }

    if (value !== 'EX BOND') {
        setJob(prev => ({
            ...prev,
            previousDocType: '',
            previousIdfNo: '',
            previousTansadNo: '',
            exBondType: '',
            changeOfOwnership: false,
        }));
        setPreviousJobData(null);
        setPreviousLookupResults([]);
        setPreviousLookupOpen(false);
        setExBondPreviousDocuments([]);
    }
};

    // --- Ex Bond: Previous IDF/TANSAD lookup & auto-population helpers ---

    const applyPreviousJobData = (data, changeOfOwnership, exBondType) => {
        if (!data) return;
        setJob(prev => {
            const updated = { ...prev };
            if (!changeOfOwnership) {
                updated.invoiceNo = data.invoice_no || '';
                updated.customer = data.customer != null ? data.customer : '';
                updated.supplier = data.supplier != null ? data.supplier : '';
                updated.customerRefNo = '';
            } else {
                updated.invoiceNo = '';
                updated.customer = '';
                updated.supplier = '';
                updated.customerRefNo = '';
            }
            if (exBondType === 'FULL') {
                updated.mblNo = data.mbl_no || '';
            }
            return updated;
        });

        if (changeOfOwnership) return;

        const customerId = data.customer != null ? parseInt(data.customer, 10) : null;
        if (customerId) {
            // Make sure the customer appears in the dropdown options
            setCustomers(prev =>
                prev.some(c => c.id === customerId)
                    ? prev
                    : [...prev, { id: customerId, name: data.customer_name || '' }]
            );

            // Load customer details (email groups, etc.) in the background
            fetch(`${MASTERS_API_BASE_URL}customers/${customerId}/`, { headers: getAuthHeaders() })
                .then(res => res.ok ? res.json() : null)
                .then(customerData => {
                    if (!customerData) return;
                    setCustomerEmailGroups(customerData.email_groups || []);
                    setCustomers(prev =>
                        prev.map(c => c.id === customerId ? { ...c, ...customerData } : c)
                    );
                    // Auto-fill notify party if "Same As Customer" is already selected
                    setJob(prev => ({
                        ...prev,
                        ...(prev.notify?.sameAsCustomer === true ? {
                            notify: {
                                ...prev.notify,
                                name: customerData.name || prev.notify.name,
                                address: customerData.address || prev.notify.address,
                                city: customerData.city || prev.notify.city,
                                country: customerData.country || prev.notify.country
                            }
                        } : {})
                    }));
                })
                .catch(err => console.error('Error fetching previous customer details:', err));
        }

        const supplierId = data.supplier != null ? parseInt(data.supplier, 10) : null;
        if (supplierId) {
            setSuppliers(prev =>
                prev.some(s => s.id === supplierId)
                    ? prev
                    : [...prev, { id: supplierId, name: data.supplier_name || '' }]
            );

            fetch(`${MASTERS_API_BASE_URL}suppliers/${supplierId}/`, { headers: getAuthHeaders() })
                .then(res => res.ok ? res.json() : null)
                .then(supplierData => {
                    if (!supplierData) return;
                    setSuppliers(prev =>
                        prev.map(s => s.id === supplierId ? { ...s, ...supplierData } : s)
                    );
                    setJob(prev => ({
                        ...prev,
                        supplierDetails: {
                            name: supplierData.name || '',
                            address: supplierData.address || '',
                            city: supplierData.city || '',
                            country: supplierData.country || ''
                        }
                    }));
                })
                .catch(err => console.error('Error fetching previous supplier details:', err));
        }
    };

    const fetchPreviousJobDocuments = async (jobId) => {
        if (!jobId) {
            setExBondPreviousDocuments([]);
            return;
        }
        try {
            const res = await fetch(`${API_BASE_URL}${jobId}/documents/`, { headers: getAuthHeaders() });
            if (!res.ok) {
                setExBondPreviousDocuments([]);
                return;
            }
            const data = await res.json();
            const docs = (Array.isArray(data) ? data : []).filter(doc =>
                (doc.uploaded_by_department || '').toUpperCase() === 'DOCUMENTATION'
            );
            // Add a flag so the renderer knows these are inherited read-only docs
            setExBondPreviousDocuments(docs.map(doc => ({
                ...doc,
                is_ex_bond_previous: true,
                file_obj: null,
                local_preview_url: null,
                fileMeta: doc.file_size ? {
                    size: doc.file_size,
                    uploaded: new Date(doc.created_at),
                    user: doc.uploaded_by_name || 'Unknown User'
                } : null
            })));
        } catch (err) {
            console.error('Error fetching previous job documents:', err);
            setExBondPreviousDocuments([]);
        }
    };

    const handlePreviousDocTypeChange = (value) => {
        if (readOnly) return;
        setJob(prev => ({ ...prev, previousDocType: value, previousIdfNo: '', previousTansadNo: '' }));
        setPreviousJobData(null);
        setPreviousLookupResults([]);
        setPreviousLookupOpen(false);
        setExBondPreviousDocuments([]);
    };

    const runPreviousJobLookup = (docType, term) => {
        if (previousLookupTimer.current) clearTimeout(previousLookupTimer.current);
        if (!term || term.trim().length < 2) {
            setPreviousLookupResults([]);
            setPreviousLookupOpen(false);
            return;
        }
        previousLookupTimer.current = setTimeout(async () => {
            setPreviousLookupLoading(true);
            try {
                const res = await fetch(
                    `${API_BASE_URL}previous-job-lookup/?doc_type=${docType}&q=${encodeURIComponent(term.trim())}`,
                    { headers: getAuthHeaders() }
                );
                if (res.ok) {
                    const data = await res.json();
                    setPreviousLookupResults(Array.isArray(data) ? data : []);
                    setPreviousLookupOpen(true);
                }
            } catch (err) {
                console.error('Error looking up previous job:', err);
            } finally {
                setPreviousLookupLoading(false);
            }
        }, 400);
    };

    const handlePreviousIdfInputChange = (value) => {
        if (readOnly) return;
        const upper = value.toUpperCase();
        setJob(prev => ({ ...prev, previousIdfNo: upper }));
        if (job.previousDocType === 'IDF') {
            runPreviousJobLookup('IDF', upper);
        }
    };

    const handlePreviousTansadInputChange = (value) => {
        if (readOnly) return;
        const upper = value.toUpperCase();
        setJob(prev => ({ ...prev, previousTansadNo: upper }));
        if (job.previousDocType === 'TANSAD') {
            runPreviousJobLookup('TANSAD', upper);
        }
    };

    const handleSelectPreviousJob = (result) => {
        setJob(prev => ({
            ...prev,
            previousIdfNo: result.idf_no || '',
            previousTansadNo: result.tansad_no || '',
        }));
        setPreviousJobData(result);
        setPreviousLookupResults([]);
        setPreviousLookupOpen(false);
        applyPreviousJobData(result, job.changeOfOwnership, job.exBondType);
        fetchPreviousJobDocuments(result.id);
    };

    const handleExBondTypeChange = (value) => {
        if (readOnly) return;
        setJob(prev => ({ ...prev, exBondType: value }));
        applyPreviousJobData(previousJobData, job.changeOfOwnership, value);
    };

    const handleChangeOfOwnershipToggle = (checked) => {
        if (readOnly) return;
        setJob(prev => ({ ...prev, changeOfOwnership: checked }));
        applyPreviousJobData(previousJobData, checked, job.exBondType);
    };

    useEffect(() => {
        const handleClickOutsidePreviousLookup = (event) => {
            if (previousLookupBoxRef.current && !previousLookupBoxRef.current.contains(event.target)) {
                setPreviousLookupOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsidePreviousLookup);
        return () => document.removeEventListener('mousedown', handleClickOutsidePreviousLookup);
    }, []);

    // Add new function to generate KK File Number from backend - around line 1050
    /* const generateKkFileNumberFromBackend = async (shipmentType) => {
        try {
            // Call backend API to get the next KK File Number for this sequence
            const response = await fetch(`${API_BASE_URL}generate-kk-file-number/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    shipment_type: shipmentType
                })
            });

            if (response.ok) {
                const data = await response.json();
                return data.kk_file_no;
            } else {
                const errorData = await response.json();
                console.error('Failed to generate KK File Number:', errorData);
                return null;
            }
        } catch (error) {
            console.error('Error calling KK File Number generation API:', error);
            return null;
        }
    };*/


    const handleCargoTypeChange = (value) => {
        handleInputChange('cargoType', value); // Also update job.cargoType
        const upper = value.toUpperCase();
        if (upper !== 'LCL' && upper !== 'VEHICLE') {
            setJob(prev => ({
                ...prev,
                lclContainerNo: '',
                lclCbm: '',
                lclPackages: '',
                lclPackageType: '',
                lclGrossWeight: ''
            }));
        }
        // Switching to LCL or VEHICLE removes FCL container rows
        if (upper === 'LCL' || upper === 'VEHICLE') {
            setContainers([]);
        }
        // Vehicles are only valid for VEHICLE cargo
        if (upper !== 'VEHICLE') {
            setVehicles([]);
        }
    };

    const handlePortChange = (value) => {
        handleInputChange('portOfLoading', value);
    };

  
    // On BL blur (new jobs only): if a pending/accepted Nomination matches this
    // BL + customer, populate the consignee-nomination details automatically.
    const handleNominationBlur = async (blValue) => {
        if (readOnly || initialData) return;
        const bl = (blValue || '').trim();
        if (!bl || !job.customer) return;
        try {
            const res = await fetch(
                `/api/documentation/nominations/match/?bl=${encodeURIComponent(bl)}&customer=${job.customer}`,
                { headers: getAuthHeaders() }
            );
            if (!res.ok) return;
            const data = await res.json();
            if (!data.found || !data.nomination) return;
            const nom = data.nomination;
            handleInputChange('nominatedBy', 'CONSIGNEE');
            if (nom.folder_name) handleInputChange('folderName', nom.folder_name);
            if (nom.icd_name) handleInputChange('nominatedIcd', nom.icd_name);
            if (nom.shipping_line) handleShippingLineChange(nom.shipping_line);
        } catch (e) {
            // Nomination lookup is best-effort — never block data entry.
        }
    };

    const handleShippingLineChange = async (shippingLineId) => {
        if (readOnly) return;
        
        // Update the shipping line selection
        handleInputChange('shippingLineId', shippingLineId);
        
        // Clear dependent fields when shipping line changes
        handleInputChange('blCarrierId', '');
        handleInputChange('voyage', '');
        
        if (shippingLineId) {
            try {
                // Fetch BL carriers and voyages in parallel
                const [blCarriersRes, voyagesRes] = await Promise.all([
                    fetch(`${MASTERS_API_BASE_URL}shippinglines/${shippingLineId}/blcarriers/`, {
                        headers: getAuthHeaders()
                    }),
                    fetch(`${MASTERS_API_BASE_URL}shippinglines/${shippingLineId}/voyages/`, {
                        headers: getAuthHeaders()
                    })
                ]);
                
                // Handle BL carriers response
                if (blCarriersRes.ok) {
                    const blCarriersData = await blCarriersRes.json();
                    console.log('✅ BL Carriers loaded:', blCarriersData);
                    setBlCarriers(Array.isArray(blCarriersData) ? blCarriersData : []);
                } else {
                    console.error('❌ Failed to fetch BL carriers:', blCarriersRes.status);
                    setBlCarriers([]);
                }
                
                // Handle voyages response
                if (voyagesRes.ok) {
                    const voyagesData = await voyagesRes.json();
                    console.log('✅ Voyages for shipping line loaded:', voyagesData);
                    
                    // Format the voyages for the dropdown
                    const formattedVoyages = voyagesData.map(voyage => ({
                        ...voyage,
                        display_name: `${voyage.bl_carrier || 'N/A'} - ${voyage.voyage_number || 'N/A'} - ${voyage.vessel_name || voyage.vessel || 'N/A'}`
                    }));
                    
                    setFilteredVoyages(formattedVoyages);
                } else {
                    console.error('❌ Failed to fetch voyages:', voyagesRes.status);
                    setFilteredVoyages([]);
                }
                
            } catch (error) {
                console.error('❌ Error fetching shipping line data:', error);
                setBlCarriers([]);
                setFilteredVoyages([]);
            }
        } else {
            setBlCarriers([]);
            setFilteredVoyages([]);
        }
    };

    const handleBlCarrierChange = (blCarrierId) => {
        if (readOnly) return;
        handleInputChange('blCarrierId', blCarrierId);
    };

    const openShippingLineNotes = () => {
        setShowShippingLineNotes(true);
    };

    const openCustomerNotes = () => {
        setShowCustomerNotes(true);
    };

    useEffect(() => {
        if (!job.shippingLineId) {
            setShippingLineNotes([]);
            return;
        }
        const loadShippingLineNotes = async () => {
            try {
                const res = await fetch(`${MASTERS_API_BASE_URL}shippinglines/${parseInt(job.shippingLineId)}/`, {
                    headers: getAuthHeaders()
                });
                if (res.ok) {
                    const data = await res.json();
                    setShippingLineNotes(data.notes || []);
                } else {
                    setShippingLineNotes([]);
                }
            } catch (err) {
                console.error('Error fetching shipping line notes:', err);
                setShippingLineNotes([]);
            }
        };
        loadShippingLineNotes();
    }, [job.shippingLineId]);

    useEffect(() => {
        if (!job.customer) {
            setCustomerNotes([]);
            return;
        }
        const loadCustomerNotes = async () => {
            try {
                const res = await fetch(`${MASTERS_API_BASE_URL}customers/${parseInt(job.customer)}/`, {
                    headers: getAuthHeaders()
                });
                if (res.ok) {
                    const data = await res.json();
                    setCustomerNotes(data.notes || []);
                } else {
                    setCustomerNotes([]);
                }
            } catch (err) {
                console.error('Error fetching customer notes:', err);
                setCustomerNotes([]);
            }
        };
        loadCustomerNotes();
    }, [job.customer]);

    // Replace the existing handleCustomerChange function:
    const handleCustomerChange = async (customerId) => {
        const customer = customers.find(c => c.id === parseInt(customerId));
        
        if (customer) {
            // Update job state with customer
            setJob(prev => {
                const updates = { ...prev };
                
                // Auto-populate consignee if same as customer is selected
                if (prev.consignee.sameAsCustomer === true) {
                    updates.consignee = {
                        ...prev.consignee,
                        name: customer.name,
                        address: customer.address,
                        city: customer.city,
                        country: customer.country,
                        sameAsCustomer: true  // Keep the flag
                    };
                }
                
                // Auto-populate notify if same as customer is selected
                if (prev.notify.sameAsCustomer === true) {
                    updates.notify = {
                        ...prev.notify,
                        name: customer.name,
                        address: customer.address,
                        city: customer.city,
                        country: customer.country,
                        sameAsCustomer: true  // Keep the flag
                    };
                }

                // Clear customer reference when customer changes
                updates.customerRefNo = '';
                
                return updates;
            });

            // Fetch customer email groups
            try {
                const response = await fetch(`${MASTERS_API_BASE_URL}customers/${customerId}/`, {
                    headers: getAuthHeaders()
                });
                
                if (response.ok) {
                    const customerData = await response.json();
                    console.log('Customer data with email groups:', customerData);
                    
                    // Set email groups, ensuring we handle the data structure properly
                    const emailGroups = customerData.email_groups || [];
                    setCustomerEmailGroups(emailGroups);
                    console.log('Customer email groups set:', emailGroups);
                } else {
                    console.error('Failed to fetch customer details:', response.status);
                    setCustomerEmailGroups([]);
                }
            } catch (error) {
                console.error('Error fetching customer email groups:', error);
                setCustomerEmailGroups([]);
            }
        } else {
            // Clear email groups when no customer is selected
            setCustomerEmailGroups([]);
        }
    };

    const handleSupplierChange = (supplierId) => {
        const supplier = suppliers.find(s => s.id === parseInt(supplierId));
        if (supplier) {
            setJob(prev => ({
                ...prev,
                supplierDetails: {
                    name: supplier.name,
                    address: supplier.address,
                    city: supplier.city,
                    country: supplier.country
                }
            }));
        }
    };

    /* const generateKkFileNumber = (shipmentType) => {
         if (!job.tansadDate) {
            console.log('KK File Number not generated - IDF/TANSAD Date is required');
            return;
        }
        
        const year = new Date().getFullYear();
        let prefix = 'KK';
        let fileNo = '';

        if (shipmentType.startsWith('IMPORT')) {
            if (shipmentType === 'IMPORT-SEA' || shipmentType === 'IMPORT-ROAD') {
                prefix = 'KK';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            } else if (shipmentType === 'IMPORT-AIR') {
                prefix = 'KK/A';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            }
        } else if (shipmentType.startsWith('EXPORT')) {
            if (shipmentType === 'EXPORT-SEA') {
                prefix = 'KK/EXP/SEA';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            } else if (shipmentType === 'EXPORT-ROAD') {
                prefix = 'KK/EXP/ROAD';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            } else if (shipmentType === 'EXPORT-AIR') {
                prefix = 'KK/EXP/AIR';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            }
        }

        if (fileNo) {
            setJob(prev => ({ ...prev, kkFileNo: fileNo }));
            setKkFileCounter(prev => prev + 1);
        }
    };

    const generateKkFileNumberSync = (shipmentType) => {
        const year = new Date().getFullYear();
        let prefix = 'KK';
        let fileNo = '';

        if (shipmentType.startsWith('IMPORT')) {
            if (shipmentType === 'IMPORT-SEA' || shipmentType === 'IMPORT-ROAD') {
                prefix = 'KK';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            } else if (shipmentType === 'IMPORT-AIR') {
                prefix = 'KK/A';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            }
        } else if (shipmentType.startsWith('EXPORT')) {
            if (shipmentType === 'EXPORT-SEA') {
                prefix = 'KK/EXP/SEA';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            } else if (shipmentType === 'EXPORT-ROAD') {
                prefix = 'KK/EXP/ROAD';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            } else if (shipmentType === 'EXPORT-AIR') {
                prefix = 'KK/EXP/AIR';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            }
        } else if (shipmentType.startsWith('TRANSIT')) {
            if (shipmentType === 'TRANSIT-SEA') {
                prefix = 'KK/TRA/SEA';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            } else if (shipmentType === 'TRANSIT-AIR') {
                prefix = 'KK/TRA/AIR';
                fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
            }
        } else if (shipmentType === 'DOMESTIC') {
            prefix = 'KK/DOM';
            fileNo = `${prefix}/${String(kkFileCounter).padStart(3, '0')}/${year}`;
        }

        return fileNo;
    };*/

    const handleSameAsFinalVessel = (checked) => {
        if (readOnly) return;
        
        if (checked) {
            setJob(prev => ({
                ...prev,
                sameAsFinalVessel: true,
                finalVessel: prev.firstVessel,
                finalVoyageNumber: prev.firstVoyageNumber
            }));
        } else {
            setJob(prev => ({
                ...prev,
                sameAsFinalVessel: false,
                finalVessel: '',
                finalVoyageNumber: ''
            }));
        }
    };

    const handleVoyageChange = (voyageId) => {
        if (readOnly) return;
        
        // Use filteredVoyages instead of voyages
        const selectedVoyage = filteredVoyages.find(v => v.id === parseInt(voyageId));
        
        if (selectedVoyage) {
            setJob(prev => ({
                ...prev,
                voyage: voyageId,
                // Populate final vessel details from voyage
                finalVessel: selectedVoyage.vessel_name || selectedVoyage.vessel || '',
                finalVoyageNumber: selectedVoyage.voyage_number || '',
                shippingLine: selectedVoyage.shipping_line_name || '',  // Add this
                // Populate arrival details from voyage data
                eta: selectedVoyage.eta || '',
                etb: selectedVoyage.etb || '',
                // These fields might not be in the voyage data but can be populated if available
                arrivalDate: selectedVoyage.actual_arrival_date || '',
                berthingDate: selectedVoyage.actual_berthing_date || '',
                berthedAt: selectedVoyage.port_operator || '',
                // Clear ICD fields if no TRA manifest date
                icd: selectedVoyage.tra_manifest_ready ? selectedVoyage.icd_location || '' : '',
                icdTransferDate: selectedVoyage.tra_manifest_ready ? selectedVoyage.icd_transfer_date || '' : ''
            }));
        } else {
            setJob(prev => ({
                ...prev,
                voyage: voyageId,
                // Clear all related fields when no voyage selected
                finalVessel: '',
                finalVoyageNumber: '',
                shippingLine: '',
                eta: '',
                etb: '',
                arrivalDate: '',
                berthingDate: '',
                berthedAt: '',
                icd: '',
                icdTransferDate: ''
            }));
        }
    };
    const handleAddNewVoyage = () => {
        if (readOnly) return;
        
        // You can implement a modal or navigate to a new voyage form
        // For now, we'll show an alert
        //alert('Add New Voyage functionality - This would open a modal or navigate to voyage creation form');
        
        // Example implementation:
         setShowVoyageModal(true);
        // or
        // navigate('/masters/voyages/new');
    };

    // Add this function to refresh voyages after adding a new one
    // Update refreshVoyages function

    const refreshVoyages = async () => {
        // After adding a new voyage, refresh the voyages for the currently selected shipping line
        if (job.shippingLineId) {
            try {
                const response = await fetch(`${MASTERS_API_BASE_URL}shippinglines/${job.shippingLineId}/voyages/`, {
                    headers: getAuthHeaders()
                });
                
                if (response.ok) {
                    const voyagesData = await response.json();
                    console.log('✅ Refreshed voyages for shipping line:', voyagesData);
                    
                    const formattedVoyages = voyagesData.map(voyage => ({
                        ...voyage,
                        display_name: `${voyage.bl_carrier || 'N/A'} - ${voyage.voyage_number || 'N/A'} - ${voyage.vessel_name || voyage.vessel || 'N/A'}`
                    }));
                    
                    setFilteredVoyages(formattedVoyages);
                    
                    // Auto-select the newest voyage if there's a new one
                    if (formattedVoyages.length > 0) {
                        const newestVoyage = formattedVoyages[formattedVoyages.length - 1];
                        handleVoyageChange(newestVoyage.id);
                    }
                }
            } catch (err) {
                console.error('Error refreshing voyages:', err);
            }
        }
    };

    // Add this function to handle when a new voyage is saved
    const handleVoyageSaved = (newVoyage) => {
        if (newVoyage) {
            // Automatically select the new voyage
            setJob(prev => ({
                ...prev,
                voyage: newVoyage.id,
                finalVessel: newVoyage.vessel_name,
                finalVoyageNumber: newVoyage.voyage_number,
                shippingLine: newVoyage.bl_carrier
            }));
        }
    };

    // Container management
   const addContainer = () => {
        setContainers(prev => {
            const newArr = [...prev, {
                container_no: '', size: '', container_type: '', seal_no: '', gross_weight: '', tare_weight: '', kk_seal_no: ''
            }];
            setTimeout(recalculateCargoTotals, 0); // recalc after state update
            return newArr;
        });
    };

    const updateContainer = (index, field, value) => {
        setContainers(prev => {
            const updated = [...prev];
            if (field === 'size') {
                updated[index] = {
                    ...updated[index],
                    size: value,
                    container_type: ''
                };
            } else {
                updated[index] = { ...updated[index], [field]: value };
            }
            return updated;
        });
        // Recalculate totals if weight fields change
        if (field === 'gross_weight' || field === 'tare_weight') {
            recalculateCargoTotals();
        }

    };

    const removeContainer = (index) => {
        setContainers(containers.filter((_, i) => i !== index));
        recalculateCargoTotals();
    };


    // Similar functions for other repeatable sections...
    const addCommodity = () => {
        setCommodities([...commodities, {
            item_no: commodities.length + 1,
            hs_code: '',
            description: '',
            model_specification: '',
            component: '',
            quantity: '',
            quantity_unit: '',
            unit_price: '',
            item_inv_price: ''
        }]);
    };

    const addNote = () => {
        // Get the current user ID from localStorage or auth context
        const currentUserId = localStorage.getItem('kk_user_id');
        console.log('Current User Id:', currentUserId.toString());
        setNotes([...notes, {
            subject: '', 
            notes: '', 
            note_date: new Date().toISOString().split('T')[0],
            created_by_user: currentUserId, // This should be an integer ID
            show_on_manifest: false            
        }]);
    };

    const addLPCO = () => {
        setLpcos([...lpcos, {
            authority: '', application_no: '', remarks: ''
        }]);
    };

    // Vehicle management
    const addVehicle = () => {
        setVehicles([...vehicles, {
            chassis_no: '', engine_capacity: '', engine_no: '', model_no: '', year: ''
        }]);
    };

    // Document management
    const addDocument = () => {
        setDocuments([...documents, {
            document_type: '', document_no: '', issue_date: '', expiry_date: '',
            remarks: '', document_file: null
        }]);
    };

    // Letter management
    const addLetter = () => {
        setLetters([...letters, {
            letter_type: '', template_used: '', generated_file: null
        }]);
    };

    const recalculateCargoTotals = () => {
        // Only FCL jobs derive totals from container rows.
        if ((job.cargoType || '').toUpperCase() !== 'FCL') {
            return;
        }

        let totalGross = 0;
        let totalTare = 0;

        containers.forEach(container => {
            totalGross += parseFloat(container.gross_weight) || 0;
            totalTare += parseFloat(container.tare_weight) || 0;
        });

        setJob(prev => ({
            ...prev,
            totalGrossWeight: totalGross.toFixed(2),
            totalTare: totalTare.toFixed(2),
            totalWeight: (totalGross + totalTare).toFixed(2)
        }));
    };


    const handleConsigneeSameAsCustomer = (isYes) => {
        if (readOnly) return;

        if (isYes) {
            setConfirmDialog({
                open: true,
                title: "Confirm Consignee Same As Customer",
                message: "Could you confirm that you have checked the BL and the consignee is correct, or you will be liable for misinformation submitted.",
                onConfirm: () => {
                    // Existing logic for "Yes"
                    const selectedCustomerId = job.customer;
                    const customer = customers.find(c => c.id === parseInt(selectedCustomerId));
                    setJob(prev => ({
                        ...prev,
                        consignee: customer ? {
                            name: customer.name || '',
                            address: customer.address || '',
                            city: customer.city || '',
                            country: customer.country || '',
                            sameAsCustomer: true
                        } : {
                            name: '',
                            address: '',
                            city: '',
                            country: '',
                            sameAsCustomer: true
                        }
                    }));
                    setConfirmDialog(d => ({ ...d, open: false }));
                },
                onCancel: () => setConfirmDialog(d => ({ ...d, open: false }))
            });
        } else {
            setJob(prev => ({
                ...prev,
                consignee: {
                    name: '',
                    address: '',
                    city: '',
                    country: '',
                    sameAsCustomer: false
                }
            }));
        }
    };
    const handleNotifySameAsCustomer = (isYes) => {
        if (readOnly) return;

        if (isYes) {
            setConfirmDialog({
                open: true,
                title: "Confirm Notify Party Same As Customer",
                message: "Could you confirm that you have checked the BL and the notify party is correct, or you will be liable for misinformation submitted.",
                onConfirm: () => {
                    const selectedCustomerId = job.customer;
                    const customer = customers.find(c => c.id === parseInt(selectedCustomerId));
                    setJob(prev => ({
                        ...prev,
                        notify: customer ? {
                            name: customer.name || '',
                            address: customer.address || '',
                            city: customer.city || '',
                            country: customer.country || '',
                            sameAsCustomer: true
                        } : {
                            name: '',
                            address: '',
                            city: '',
                            country: '',
                            sameAsCustomer: true
                        }
                    }));
                    setConfirmDialog(d => ({ ...d, open: false }));
                },
                onCancel: () => setConfirmDialog(d => ({ ...d, open: false }))
            });
        } else {
            setJob(prev => ({
                ...prev,
                notify: {
                    name: '',
                    address: '',
                    city: '',
                    country: '',
                    sameAsCustomer: false
                }
            }));
        }
    };

    const handleFileChange = (e, section, index = null) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        if (section === 'documents') {
            setDocuments(prev => prev.map((doc, i) => {
            if (i !== index) return doc;
            if (lockExistingDocuments && doc.id) return doc; // block change
            return {
                ...doc,
                file_obj: file,
                local_preview_url: URL.createObjectURL(file),
                fileMeta: {
                size: file.size,
                uploaded: new Date(),
                user: localStorage.getItem('kk_user') || 'Unknown User'
                }
            };
            }));
           // if (initialData?.id) saveDocuments(initialData.id, true);
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        // if (readOnly && !documentsEditable) return;

        setError(null);
        setLoading(true);

        const hasAnyDeclaration = !!(job.idfNo || job.tansadNo || job.tansadDate);
        if (hasAnyDeclaration) {
            if (!job.idfNo) {
                toast.error(job.cargoType === 'SCT' ? "Please Enter C2 Declaration Number" : "Please Enter IDF Number");
                idfRef.current?.focus();
                idfRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setLoading(false);
                return;
            }
            if (job.cargoType !== 'SCT') {
                if (!job.tansadNo) {
                    toast.error("Please Enter Tansad Number");
                    tansadNoRef.current?.focus();
                    tansadNoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setLoading(false);
                    return;
                }
                if (!job.tansadDate) {
                    toast.error("Please Enter Tansad Date");
                    tansadDateRef.current?.focus();
                    tansadDateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setLoading(false);
                    return;
                }
            }
        }

        // Require commodities if both IDF and TANSAD are populated
        if (job.idfNo && job.tansadNo && commodities.length === 0) {
            toast.error("Please upload Commodities Excel file.");
            setLoading(false);
            return;
        }

        // Validate required document types
        let requiredDocTypes = [];
        if (job.shipmentType === 'EX BOND' && (job.cargoType || '').toUpperCase() === 'VEHICLE') {
            if (job.idfNo && job.tansadNo) {
                requiredDocTypes = ['repacking bl'];
            }
        } else if (job.shipmentType === 'EXPORT-ROAD') {
            if (job.idfNo) {
                requiredDocTypes = ['c2 form', 'exit note', 'invoice'];
            }
        } else if (job.shipmentType === 'EXPORT-SEA' && (job.cargoType || '').toUpperCase() === 'ZNZ') {
            if (job.idfNo && job.tansadNo) {
                requiredDocTypes = ['tra authorization letter','invoice'];
            }
        } else {
            requiredDocTypes = [
                'invoice',
                'packing list',
                'c36 form',
                'tra authorization letter'
            ];
        }

        const missingDocs = [];
        const docTypesPresent = documents.map(d => (d.document_type || '').toLowerCase().trim());

        // Check BL or Bill of Lading as alternatives (not for export-road or ex bond vehicle)
        if (job.shipmentType !== 'EXPORT-ROAD' && !(job.shipmentType === 'EX BOND' && (job.cargoType || '').toUpperCase() === 'VEHICLE') && !(job.shipmentType === 'EXPORT-SEA' && (job.cargoType || '').toUpperCase() === 'ZNZ')) {
            const hasBL = docTypesPresent.some(dt => dt.includes('bl') || dt.includes('bill of lading'));
            if (hasBL) {
                const blDoc = documents.find(d => {
                    const dt = (d.document_type || '').toLowerCase().trim();
                    return dt.includes('bl') || dt.includes('bill of lading');
                });
                if (blDoc && !blDoc.document_file && !blDoc.file_obj) {
                    missingDocs.push('Document type "BL/Bill of Lading" exists but has no file uploaded');
                }
            } else {
                missingDocs.push('Document type: BL/Bill of Lading');
            }
        }

        // Check other required document types
        requiredDocTypes.forEach(requiredType => {
            const hasDocType = docTypesPresent.some(dt => dt.includes(requiredType));

            if (hasDocType) {
                const docWithType = documents.find(d => (d.document_type || '').toLowerCase().trim().includes(requiredType));
                if (docWithType && !docWithType.document_file && !docWithType.file_obj) {
                    missingDocs.push(`Document type "${requiredType}" exists but has no file uploaded`);
                }
            } else {
                missingDocs.push(`${requiredType.toUpperCase()}`);
            }
        });

        if (((job.shipmentType === 'EXPORT-ROAD' && job.idfNo) || (job.idfNo && job.tansadNo)) && missingDocs.length > 0) {
            toast.error(`Please upload the required documents: ${missingDocs.join(', ')}`);
            setLoading(false);
            return;
        }

        // Keep existing MBL dependency if Tansad No entered
        if (job.tansadNo && !job.mblNo && job.cargoType !== 'ZNZ') {
            toast.error("Please Enter MBL No.");
            mblRef.current?.focus();
            mblRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setLoading(false);
            return;
        }

        if ((job.cargoType || '').toUpperCase() === 'LCL' || (job.cargoType || '').toUpperCase() === 'VEHICLE') {
            if (job.lclContainerNo && (job.lclCbm === '' || job.lclCbm == null)) {
                toast.error("CBM is required when Container Number is provided.");
                if (cbmRef.current) {
                    cbmRef.current.focus();
                    // optional smooth scroll
                    cbmRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                setLoading(false);
                return;
            }
        }
         if ((job.cargoType || '').toUpperCase() === 'LCL') {
            if (job.mblNo && !job.hblSoNo) {
                toast.error("Please Enter HBL SO No.");
                if (hblSoRef.current) {
                    hblSoRef.current.focus();
                    // optional smooth scroll
                    hblSoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                setLoading(false);
                return;
            }
        }

        if ((job.cargoType || '').toUpperCase() === 'FCL') {
            const incompleteContainer = containers.find(
                c => !c.size || !c.container_type
            );
            if (incompleteContainer) {
                toast.error("All containers must have both Size and Type selected.");
                setLoading(false);
                return;
            }

            // Validate container numbers
            const totalContainersNum = Number(job.totalContainers) || 0;
            const actualContainersNum = containers.filter(c => c.size && c.container_type).length;
            if (totalContainersNum !== actualContainersNum) {
                toast.error(
                    "Total Containers added in Container Details does not match Total Containers in Cargo Details"
                );
                setLoading(false);
                return;
            }
        }

        if (arrivalStatus === 'YES' && !job.arrivalDate) {
            toast.error('Please enter Arrival Date (ATA)');
            arrivalDateRef.current?.focus();
            setLoading(false);
            return;
        }
        if (berthingStatus === 'YES' && !job.berthingDate) {
            toast.error('Please enter Berthing Date (ATB)');
            berthingDateRef.current?.focus();
            setLoading(false);
            return;
        }

        if (requiresPvocDiChoice && pvocDiSelections.length === 0) {
            toast.error('Please select PVOC or TBS DI (or both) in the Commodities section.');
            setLoading(false);
            return;
        }

        if ((job.cargoType || '').toUpperCase() === 'ZNZ' && manualPermitsBase.length === 0) {
            toast.error('Please select at least one permit type.');
            setLoading(false);
            return;
        }
                

        const url = initialData ? `${API_BASE_URL}${initialData.id}/` : API_BASE_URL;
        const method = initialData ? 'PUT' : 'POST';

        try {
            const isLclOrVehicle = ['LCL', 'VEHICLE'].includes((job.cargoType || '').toUpperCase());
            const looseCargoPayload = isLclOrVehicle
                ? {
                    container_no: job.lclContainerNo || '',
                    cbm: job.lclCbm || '',
                    packages: job.lclPackages || '',
                    package_type: job.lclPackageType || '',
                    gross_weight: job.lclGrossWeight || ''
                    }
                : null;
            const payload = {
                // Basic Information
                shipment_type: job.shipmentType,
                cargo_type: job.cargoType,
                cargo_nature: job.cargoNature,
                dangerous_classes: job.dangerousClasses,
                email_subject: job.emailSubject,
                status: job.status,
                status_remark: job.statusRemark,

                // Related entities
                customer: job.customer,
                supplier: job.supplier,
                voyage: job.voyage,
                customer_ref: job.customerRefNo,
                shipping_line: job.shippingLineId,
                bl_carrier: job.blCarrierId,

                // Shipment Details
                mbl_no: job.mblNo,
                invoice_no: job.invoiceNo,
                kk_file_no: job.kkFileNo,
                hbl_so_no: job.hblSoNo,
                idf_no: job.tasac ? (job.idfNo ? job.idfNo.trim() + ' (TASAC)' : '') : job.idfNo,
                tansad_no: job.tansadNo,
                tansad_date: job.tansadDate ? job.tansadDate : null,
                ucr_date: job.ucrDate ? job.ucrDate : null,
                ucr_no: job.ucrNo,
                tasac: job.tasac,
                exemption_ref_no: job.exemptionRefNo,

                // Ex Bond - Previous IDF/TANSAD tracking
                previous_doc_type: job.previousDocType,
                previous_idf_no: job.previousIdfNo,
                previous_tansad_no: job.previousTansadNo,
                ex_bond_type: job.exBondType,
                change_of_ownership: job.changeOfOwnership,

                // Supplier & Consignee/Notify Details
                supplier_details: job.supplierDetails,
                // Make sure boolean values are sent correctly
                // consignee_same_as_customer: job.consigneeSameAsCustomer, // This should be true/false/null
                 consignee: {
                    name: job.consignee.name || '',
                    address: job.consignee.address || '',
                    city: job.consignee.city || '',
                    country: job.consignee.country || '',
                    sameAsCustomer: job.consignee.sameAsCustomer  // Include within JSON
                },
                
                // notify_same_as_customer: job.notifySameAsCustomer, // This should be true/false/null
                notify: {
                    name: job.notify.name || '',
                    address: job.notify.address || '',
                    city: job.notify.city || '',
                    country: job.notify.country || '',
                    sameAsCustomer: job.notify.sameAsCustomer  // Include within JSON
                },


                // Nested JSON fields
                vessel_details: {
                    first_vessel: job.firstVessel,
                    first_voyage_number: job.firstVoyageNumber,
                    shipped_on_board_date: job.shippedOnBoardDate,
                    shipping_line: job.shippingLine,
                    same_as_final_vessel: job.sameAsFinalVessel,
                    final_vessel: job.finalVessel,
                    final_voyage_number: job.finalVoyageNumber,
                    eta: job.eta,
                    etb: job.etb,
                    arrival_date: job.arrivalDate,
                    berthing_date: job.berthingDate,
                    berthed_at: job.berthedAt,
                    icd: job.icd,
                    icd_transfer_date: job.icdTransferDate,
                },

                cargo_details: {
                    ...(initialData?.cargo_details || {}),
                    country_of_loading: job.countryOfLoading,
                    port_of_loading: job.portOfLoading,
                    port_of_discharge: job.portOfDischarge,
                    nominated_by: job.nominatedBy,
                    folder_name: job.folderName, // Save Folder Name
                    nominated_icd: job.nominatedIcd,
                    marks: job.marks,
                    description: job.description,
                    port_material: job.portMaterial,
                    total_containers: job.totalContainers,
                    total_packages: job.totalPackages,
                    package_type: job.packageType,
                    total_gross_weight: job.totalGrossWeight,
                    total_tare: job.totalTare,
                    total_tare_weight: job.totalTare,
                    total_weight: job.totalWeight,
                },

                manual_permits: Array.from(new Set([
                    ...manualPermitsBase,
                    // If PVOC is required but not selected, substitute TBS DI
                    ...(() => {
                        const pvocIsRequired = requiredPermitTypes.some(isPvocPermitType);
                        const pvocSelected = pvocDiSelections.includes('PVOC');
                        const diSelected = pvocDiSelections.includes('TBS DI');
                        if (pvocIsRequired && !pvocSelected && !diSelected) {
                            return ['TBS DI'];
                        }
                        return pvocDiSelections.map((s) => normalizePermitType(s));
                    })(),
                ])),

                ...(looseCargoPayload ? { loose_cargo: looseCargoPayload } : {}),
            };
            
            console.log('Consignee Same As Customer:', job.consigneeSameAsCustomer);
            console.log('Notify Same As Customer:', job.notifySameAsCustomer);
            console.log('Consignee Data:', job.consignee);
            console.log('Notify Data:', job.notify);
            console.log('Full payload:', JSON.stringify(payload, null, 2));
            
            const response = await fetch(url, {
                method,
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });

           
            if (!response.ok) {
                const errorData = await response.json();
                
                // *** START: DUPLICATE CHECK LOGIC ***
                if (response.status === 400) {
                    if (errorData.idf_no) {
                        toast.error(errorData.idf_no[0]); // Use toast.error
                        setLoading(false);
                        return;
                    }
                    if (errorData.tansad_no) {
                        toast.error(errorData.tansad_no[0]); // Use toast.error
                        setLoading(false);
                        return;
                    }
                    if (errorData.invoice_no) {
                        toast.error(errorData.invoice_no[0]); // Use toast.error
                        setLoading(false);
                        return;
                    }
                }
                // *** END: DUPLICATE CHECK LOGIC ***

                // Handle other errors
                const errorMessage = errorData.detail || Object.values(errorData).flat().join(' ') || 'Failed to save job.';
                setError(errorMessage);
                toast.error(errorMessage);
                
            } else {
                const savedJob = await response.json();
                const jobId = savedJob.id;
                
                // Track any sub-resource errors
                let hasSubResourceErrors = false;                
                
                // Save containers if any, and explicitly clear them when cargo is LCL or VEHICLE
                if (containers.length > 0 || ['LCL', 'VEHICLE'].includes((job.cargoType || '').toUpperCase())) {
                    const containersSuccess = await saveContainers(jobId);
                    if (!containersSuccess) hasSubResourceErrors = true;
                }

                // Save vehicles if any
                if (vehicles.length > 0) {
                    const vehiclesSuccess = await saveVehicles(jobId);
                    if (!vehiclesSuccess) hasSubResourceErrors = true;
                }

                // Save commodities if any
                if (commodities.length > 0) {
                    const commoditiesSuccess = await saveCommodities(jobId);
                    if (!commoditiesSuccess) hasSubResourceErrors = true;
                }
                
                // Save documents if any
                if (documents.length > 0) {
                    const documentsSuccess = await saveDocuments(jobId);
                    if (!documentsSuccess) hasSubResourceErrors = true;
                }

                // Save letters if any
                if (letters.length > 0) {
                    const lettersSuccess = await saveLetters(jobId);
                    if (!lettersSuccess) hasSubResourceErrors = true;
                }

                // Save emails if any
                if (emails.length > 0) {
                    const emailsSuccess = await saveEmails(jobId);
                    if (!emailsSuccess) hasSubResourceErrors = true;
                }

                // Save notes if any
                if (notes.length > 0) {
                    const notesSuccess = await saveNotes(jobId);
                    if (!notesSuccess) hasSubResourceErrors = true;
                }

                // Save lpcos if any
                if (lpcos.length > 0) {
                    const lpcosSuccess = await saveLpcos(jobId);
                    if (!lpcosSuccess) hasSubResourceErrors = true;
                }
                
                // Show warning if sub-resources failed
                if (hasSubResourceErrors) {
                    setError('Job saved but some details (containers, commodities, or documents) could not be saved.');
                }  

                // Success
                toast.success('Job saved successfully!');
                if (onClose) onClose(true);
            }
        } catch (err) {
            setError('Failed to save job. Network error or server unreachable.');
            toast.error('Failed to save job. Network error or server unreachable.');
        } finally {
            setLoading(false);
        }
    };

    
    const saveContainers = async (jobId) => {
        try {
            console.log('Containers being sent:', containers);
            // Backend expects a dictionary with a 'containers' key, not an array
            const payload = { containers: containers };
            const response = await fetch(`${API_BASE_URL}${jobId}/containers/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                console.error('Failed to save containers:', await response.json());
            }
            return response.ok; // Return true if successful
        } catch (err) {
            console.error('Error saving containers:', err);
            return false; // Return false if there was an error
        }
    };

    const saveVehicles = async (jobId) => {
        try {
            const cleanedVehicles = vehicles.map(v => ({
                ...v,
                year: v.year && !isNaN(parseInt(v.year, 10)) ? parseInt(v.year, 10) : null,
            }));
            console.log('Vehicles being sent:', cleanedVehicles);
            const payload = { vehicles: cleanedVehicles };
            const response = await fetch(`${API_BASE_URL}${jobId}/vehicles/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                console.error('Failed to save vehicles:', await response.json());
            }
            return response.ok;
        } catch (err) {
            console.error('Error saving vehicles:', err);
            return false;
        }
    };

    const saveCommodities = async (jobId) => {
        try {
            // Round quantity, unit_price, and item_inv_price to 2 decimals before saving
            const roundedCommodities = commodities.map(c => ({
                ...c,
                quantity: c.quantity !== undefined && c.quantity !== null && c.quantity !== ''
                    ? Number(parseFloat(c.quantity).toFixed(2))
                    : '',
                unit_price: c.unit_price !== undefined && c.unit_price !== null && c.unit_price !== ''
                    ? Number(parseFloat(c.unit_price).toFixed(2))
                    : '',
                item_inv_price: c.item_inv_price !== undefined && c.item_inv_price !== null && c.item_inv_price !== ''
                    ? Number(parseFloat(c.item_inv_price).toFixed(2))
                    : ''
            }));

            console.log('Commodities being sent:', roundedCommodities);
            const payload = { commodities: roundedCommodities };
            const response = await fetch(`${API_BASE_URL}${jobId}/commodities/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const errorData = await response.json();
                console.error('Failed to save commodities:', errorData);
                toast.error(
                    errorData?.errors
                        ? errorData.errors.map(e => Object.values(e)[0]).join(', ')
                        : (errorData.detail || 'Failed to save commodities.')
                );
                return false;
            }
            return true;
        } catch (err) {
            console.error('Error saving commodities:', err);
            toast.error('Error saving commodities. Please check your data and try again.');
            return false;
        }
    };

    const saveDocuments = async (jobId) => {
        try {
            // For documents with file uploads, we need to use FormData
            const formData = new FormData();

            // In wharfage-only mode, preserve all existing documents and only
            // allow new/updated Wharfage Invoice documents to be added/changed.
            const docsToSave = isWharfageOnly
                ? documents.filter(doc => doc.id || doc.file_obj)
                : documents;

            // Add documents array as JSON string
            formData.append('documents_data', JSON.stringify(
                docsToSave.map(doc => ({
                    id: doc.id,
                    document_type: doc.document_type,
                    remarks: doc.remarks || ''
                }))
            ));

            // Add files separately with clear naming
            docsToSave.forEach((doc, index) => {
                if (doc.file_obj && doc.file_obj instanceof File) {
                    formData.append(`file_${index}`, doc.file_obj);
                }
            });

            console.log('Sending documents with FormData:');
            for (let [key, value] of formData.entries()) {
                console.log(key, value);
            }
            
            const response = await fetch(`${API_BASE_URL}${jobId}/documents/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
                    // Don't set Content-Type with FormData
                },
                body: formData,
            });
            
            if (!response.ok) {
                const errorData = await response.text();
                console.error('Failed to save documents:', errorData);
                return false;
            }
            const result = await response.json();
            console.log('Documents saved successfully:', result);
            return true;

        } catch (err) {
            console.error('Error saving documents:', err);
        }
    };

    const saveLetters = async (jobId) => {
        try {
            console.log('Letters being sent:', letters);
            
            // Use FormData for file uploads
            const formData = new FormData();
            
            // Add letters array as JSON string (excluding files)
            formData.append('letters_data', JSON.stringify(
                letters.map(letter => ({
                    id: letter.id,
                    letter_type: letter.letter_type,
                    template_used: letter.template_used
                    // Don't include generated_file here
                }))
            ));
            
            // Add files separately with clear naming
            letters.forEach((letter, index) => {
                if (letter.generated_file) {
                    formData.append(`file_${index}`, letter.generated_file);
                }
            });
            
            const response = await fetch(`${API_BASE_URL}${jobId}/letters/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
                    // Don't set Content-Type with FormData - browser sets it automatically
                },
                body: formData,
            });
            
            if (!response.ok) {
                const errorData = await response.text();
                console.error('Failed to save letters:', errorData);
                return false;
            }
            return true;
        } catch (err) {
            console.error('Error saving letters:', err);
            return false;
        }
    };

    const saveNotes = async (jobId) => {
        try {
            console.log('Notes being sent:', notes);
            // Backend expects a dictionary with a 'notes' key, not an array
            const payload = {
                notes: notes.map(n => ({
                id: n.id,
                subject: n.subject,
                notes: n.notes,
                note_date: n.note_date,
                created_by_user: n.created_by_user,
                show_on_manifest: !!n.show_on_manifest
                }))
            };
            const response = await fetch(`${API_BASE_URL}${jobId}/notes/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                console.error('Failed to save notes:', await response.json());
            }
            return response.ok; // Return true if successful
        } catch (err) {
            console.error('Error saving notes:', err);
            return false; // Return false if there was an error
        }
    };

    const saveEmails = async (jobId) => {
        try {
            console.log('Emails being sent:', emails);
            // Backend expects a dictionary with a 'emails' key, not an array
            const payload = { emails: emails };
            const response = await fetch(`${API_BASE_URL}${jobId}/emails/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                console.error('Failed to save emails:', await response.json());
            }
            return response.ok; // Return true if successful
        } catch (err) {
            console.error('Error saving emails:', err);
            return false; // Return false if there was an error
        }
    };

    const saveLpcos = async (jobId) => {
        try {
            console.log('Lpcos being sent:', lpcos);
            // Backend expects a dictionary with a 'commodities' key, not an array
            const payload = { lpcos: lpcos };
            const response = await fetch(`${API_BASE_URL}${jobId}/lpcos/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                console.error('Failed to save lpcos:', await response.json());
            }
            return response.ok; // Return true if successful
        } catch (err) {
            console.error('Error saving lpcos:', err);
            return false; // Return false if there was an error
        }
    };

    // Update commodity function
    const updateCommodity = (index, field, value) => {
        if (readOnly) return;
        const updated = [...commodities];
        
        // Format HS code when it's being updated
        if (field === 'hs_code') {
            updated[index][field] = formatHSCode(value);
        } else {
            updated[index][field] = value;
        }
        
        setCommodities(updated);
    };

    // Remove commodity function
    const removeCommodity = (index) => {
        if (readOnly) return;
        setCommodities(commodities.filter((_, i) => i !== index));
    };

    // Handle Excel file import        
    /*const handleCommoditiesExcel = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.name.match(/\.(xlsx|xls)$/i)) {
            alert('Please select a valid Excel file (.xlsx or .xls)');
            event.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = function(e) {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });

                // Check for required worksheets (case-insensitive)
                const availableSheets = workbook.SheetNames;
                console.log('Available sheets:', availableSheets);
                
                const itemSheet = availableSheets.find(name => 
                    name.toLowerCase().includes('item') && name.toLowerCase().includes('sample')
                ) || workbook.Sheets['ITEM_Sample'] || workbook.Sheets['Item_Sample'];
                
                const modelSheet = availableSheets.find(name => 
                    name.toLowerCase().includes('model') && name.toLowerCase().includes('sample')
                ) || workbook.Sheets['MODEL_Sample'] || workbook.Sheets['Model_Sample'];

                if (!itemSheet && !modelSheet) {
                    alert(`Missing required worksheets. Found: ${availableSheets.join(', ')}\n\nExpected: ITEM_Sample and MODEL_Sample`);
                    event.target.value = '';
                    return;
                }

                // Parse worksheets using the correct method from Shipment.html
                // Headers are on row 2 (index 1), data starts from row 3 (index 2)
                const itemData = XLSX.utils.sheet_to_json(workbook.Sheets[itemSheet], { 
                    header: 1, 
                    defval: '' 
                });
                const modelData = XLSX.utils.sheet_to_json(workbook.Sheets[modelSheet], { 
                    header: 1, 
                    defval: '' 
                });

                console.log('Raw Item Data:', itemData);
                console.log('Raw Model Data:', modelData);

                // Get headers from 2nd row (index 1) - this is the key fix
                const itemHeaders = itemData[1] || [];
                const modelHeaders = modelData[1] || [];

                console.log('Item Headers:', itemHeaders);
                console.log('Model Headers:', modelHeaders);

                // Get data from 3rd row onwards (index 2+) - skip header rows
                const itemRows = itemData.slice(2);
                const modelRows = modelData.slice(2);

                // Convert arrays to objects using headers
                const itemObjects = itemRows.map(row => {
                    const obj = {};
                    itemHeaders.forEach((header, index) => {
                        if (header) obj[header] = row[index] || '';
                    });
                    return obj;
                });

                const modelObjects = modelRows.map(row => {
                    const obj = {};
                    modelHeaders.forEach((header, index) => {
                        if (header) obj[header] = row[index] || '';
                    });
                    return obj;
                });

                console.log('Item Objects:', itemObjects);
                console.log('Model Objects:', modelObjects);

                // Process and combine the data
                const processedCommodities = [];
                const maxRows = Math.max(itemObjects.length, modelObjects.length);

                for (let i = 0; i < maxRows; i++) {
                    const itemRow = itemObjects[i] || {};
                    const modelRow = modelObjects[i] || {};

                    // Check if both rows have meaningful data
                    const itemHasData = Object.values(itemRow).some(val => 
                        val && val.toString().trim() !== ''
                    );
                    const modelHasData = Object.values(modelRow).some(val => 
                        val && val.toString().trim() !== ''
                    );

                    // Skip completely empty rows
                    if (!itemHasData && !modelHasData) {
                        continue;
                    }

                    // Format prices correctly
                    const formatPrice = (value) => {
                        if (!value || value === '') return '';
                        const num = parseFloat(value);
                        return isNaN(num) ? value : num.toFixed(2);
                    };

                    // Create commodity object with exact field mapping from Shipment.html
                    const commodity = {
                        item_no: i + 1,
                        hs_code: formatHSCode(itemRow['HS4 CODE'] || ''), // Format HS code here
                        description: itemRow['GOODS HS DESCRIPTION 2'] || '',
                        model_specification: modelRow['Model/Specification'] || '',
                        component: modelRow['Component'] || '',
                        quantity: modelRow['Quantity'] || '',
                        quantity_unit: modelRow['Quantity Unit'] || '',
                        unit_price: formatPrice(modelRow['Unit Price']),
                        item_inv_price: formatPrice(modelRow['Item Inv. Price'])
                    };

                    processedCommodities.push(commodity);
                }

                console.log('Processed Commodities:', processedCommodities);

                if (processedCommodities.length > 0) {
                    setCommodities(processedCommodities);
                    alert(`Successfully imported ${processedCommodities.length} commodities`);
                } else {
                    alert('No valid data found in the Excel file. Please check that:\n' +
                        '1. Headers are in row 2\n' +
                        '2. Data starts from row 3\n' +
                        '3. Required columns exist: HS4 CODE, GOODS HS DESCRIPTION 2, etc.');
                }

            } catch (error) {
                console.error('Error reading Excel file:', error);
                alert('Error reading Excel file. Please check the file format and try again.\n\nError: ' + error.message);
            }
            
            // Clear the file input
            event.target.value = '';
        };

        reader.onerror = function() {
            alert('Error reading file. Please try again.');
            event.target.value = '';
        };

        reader.readAsArrayBuffer(file);
    };*/

    const handleCommoditiesExcel = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.name.match(/\.(xlsx|xls)$/i)) {
            alert('Please select a valid Excel file (.xlsx or .xls)');
            event.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = function(e) {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });

                // Find sheets
                const availableSheets = workbook.SheetNames;
                const itemSheetName = availableSheets.find(name => name.toLowerCase().includes('item') && name.toLowerCase().includes('sample'));
                const modelSheetName = availableSheets.find(name => name.toLowerCase().includes('model') && name.toLowerCase().includes('sample'));

                if (!itemSheetName || !modelSheetName) {
                    alert(`Missing required worksheets. Found: ${availableSheets.join(', ')}\n\nExpected: ITEM_Sample and MODEL_Sample`);
                    event.target.value = '';
                    return;
                }

                // Parse worksheets (headers on row 2, data from row 3)
                const itemData = XLSX.utils.sheet_to_json(workbook.Sheets[itemSheetName], { header: 1, defval: '' });
                const modelData = XLSX.utils.sheet_to_json(workbook.Sheets[modelSheetName], { header: 1, defval: '' });

                const itemHeaders = itemData[1] || [];
                const modelHeaders = modelData[1] || [];

                // Build item map: item_no -> { hs_code, description }
                const itemRows = itemData.slice(2);
                const itemMap = {};
                itemRows.forEach(row => {
                    const item_no = row[0];
                    if (!item_no) return;
                    const hs_code = row[itemHeaders.indexOf('HS4 CODE')] || '';
                    const description = row[itemHeaders.indexOf('GOODS HS DESCRIPTION 2')] || '';
                    itemMap[item_no] = {
                        hs_code: formatHSCode(hs_code),
                        description: description
                    };
                });

                // Build commodities from model rows, using item_no to lookup HS code/desc
                const modelRows = modelData.slice(2);
                const processedCommodities = [];
                modelRows.forEach((row, i) => {
                    const item_no = row[0];
                    if (!item_no || !itemMap[item_no]) return;
                    const hs_code = itemMap[item_no].hs_code;
                    const description = itemMap[item_no].description;
                    const model_specification = row[modelHeaders.indexOf('Model/Specification')] || '';
                    const component = row[modelHeaders.indexOf('Component')] || '';
                    const quantity = row[modelHeaders.indexOf('Quantity')] || '';
                    const quantity_unit = row[modelHeaders.indexOf('Quantity Unit')] || '';
                    const unit_price = row[modelHeaders.indexOf('Unit Price')] || '';
                    const item_inv_price = row[modelHeaders.indexOf('Item Inv. Price')] || '';

                    processedCommodities.push({
                        item_no,
                        hs_code,
                        description,
                        model_specification,
                        component,
                        quantity,
                        quantity_unit,
                        unit_price,
                        item_inv_price
                    });
                });

                if (processedCommodities.length > 0) {
                    setCommodities(processedCommodities);
                    alert(`Successfully imported ${processedCommodities.length} commodities`);
                } else {
                    alert('No valid data found in the Excel file. Please check that:\n' +
                        '1. Headers are in row 2\n' +
                        '2. Data starts from row 3\n' +
                        '3. Required columns exist: HS4 CODE, GOODS HS DESCRIPTION 2, etc.');
                }
            } catch (error) {
                console.error('Error reading Excel file:', error);
                alert('Error reading Excel file. Please check the file format and try again.\n\nError: ' + error.message);
            }
            event.target.value = '';
        };

        reader.onerror = function() {
            alert('Error reading file. Please try again.');
            event.target.value = '';
        };

        reader.readAsArrayBuffer(file);
    };

    const isRoadConsignment = (job.shipmentType || '').toString().toUpperCase().includes('ROAD') || 
                              (job.cargoType || '').toString().toUpperCase().includes('ROAD');

    const isLclConsignment = (job.shipmentType || '').toString().toUpperCase().includes('LCL') || 
                             (job.cargoType || '').toString().toUpperCase().includes('LCL');

    const isExBondVehicle = job.shipmentType === 'EX BOND' && (job.cargoType || '').toUpperCase() === 'VEHICLE';
    const isExBond = job.shipmentType === 'EX BOND';
    // Road + FCL renders Cargo/Container Details exactly like IMPORT-SEA/EXPORT-SEA.
    const isRoadFcl = ['IMPORT-ROAD', 'EXPORT-ROAD'].includes(job.shipmentType) && job.cargoType === 'FCL';
    const isAirOrRoadLayout = ['IMPORT-AIR', 'IMPORT-ROAD', 'EXPORT-ROAD'].includes(job.shipmentType) && !isRoadFcl;

    return (
        <div className="job-form-container">
            <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1, minWidth: 120 }}>
                    <h1 style={{ margin: 0 }}>Job Details</h1>
                </div>
            </div>

            {error && <div className="form-error">{error}</div>}

            <form onSubmit={handleSubmit}>
                {/* Top Row with Toggle Buttons, Job Info, Created By and Form Buttons - Sticky Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', position: 'sticky', top: 0, backgroundColor: '#f5f5f5', padding: '10px', zIndex: 1000, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    {canShowOtherSections && !isDocumentsOnlyMode && (
                        <div className="toggle-buttons">
                            <button type="button" onClick={expandAll}>Expand All</button>
                            <button type="button" onClick={collapseAll}>Collapse All</button>
                        </div>
                    )}
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 16, fontSize: '0.75rem', color: '#666', flexWrap: 'wrap', whiteSpace: 'nowrap' }}>
                        {initialData?.id && (
                            <span>JOB ID: <strong>{initialData.id}</strong></span>
                        )}
                        {(job.idfNo || initialData?.idf_no) && (
                            <span>IDF NO.: <strong>{job.idfNo || initialData.idf_no}</strong></span>
                        )}
                        {isLclConsignment && job.hblSoNo && (
                            <span>HBL NO: <strong>{job.hblSoNo}</strong></span>
                        )}
                        {!isLclConsignment && job.mblNo && (
                            <span>BL NO: <strong>{job.mblNo}</strong></span>
                        )}
                        {initialData && (job.created_by_name || initialData.created_by_name) && (
                            <span>
                                CREATED BY: <strong>{job.created_by_name || initialData.created_by_name}</strong>
                                {(job.created_at || initialData.created_at) && (
                                    <> ON <strong>{(() => {
                                        const d = new Date(job.created_at || initialData.created_at);
                                        const pad = n => String(n).padStart(2, '0');
                                        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                                    })()}</strong></>
                                )}
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                        {/* Top Right Form Buttons */}
                        <div className="buttons" style={{ marginLeft: 'auto', marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                            {showViewExpenses && job.idfNo && (
                                <button
                                    type="button"
                                    style={{ background: 'orange', color: 'white', border: '1px solid darkorange' }}
                                    onClick={() => setShowExpensesModal(true)}
                                >
                                    <i className="fa fa-money"></i> View Expenses
                                </button>
                            )}
                            {readOnly && !documentsEditable && !openDocumentsOnly && !showEmailButtonOnly && hasPermission('JOBS', 'can_edit') && (
                                <button type="button" onClick={() => setReadOnly(false)}>Edit</button>
                            )}
                            {( !readOnly || documentsEditable ) && !showEmailButtonOnly && (
                                <button type="submit" disabled={loading}>
                                    {loading ? 'Saving...' : (initialData ? 'Update Job' : 'Create Job')}
                                </button>
                            )}
                            {showEmailButtonOnly && (
                                <button
                                    type="button"
                                    onClick={() => setShowEmailModal(true)}
                                    style={{
                                        padding: '8px 16px',
                                        background: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                    }}
                                >
                                    New Email
                                </button>
                            )}
                            <button type="button" onClick={() => onClose(false)} className="cancel-btn">
                                {readOnly ? 'Close' : 'Cancel'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Shipment / Cargo Type Section */}
                {(!isDocumentsOnlyMode || isWharfageOnly) && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('basic-section')}>Shipment / Cargo Type</h3>
                        <div id="basic-section" className="section-content" style={{ display: 'block' }}>
                            <div className="row">                            
                                <div>
                                    <label className="required">Shipment Type</label>
                                    <Select2Dropdown
                                        options={shipmentTypes.map(type => ({ id: type, text: type }))}
                                        value={job.shipmentType}
                                        onChange={(value) => handleInputChange('shipmentType', value)}
                                        placeholder="SELECT SHIPMENT TYPE"
                                        displayKey="text"
                                        valueKey="id"
                                        required
                                        disabled={readOnly}
                                        clearable={true}
                                    />
                                </div>
                                <div>
                                    <label className="required">Cargo Type</label>
                                    <Select2Dropdown
                                        options={getFilteredCargoTypes(job.shipmentType).map(type => ({ id: type, text: type }))}
                                        value={job.cargoType}
                                        onChange={handleCargoTypeChange}
                                        placeholder="SELECT CARGO TYPE"
                                        displayKey="text"
                                        valueKey="id"
                                        required
                                        disabled={readOnly}
                                        clearable={true}
                                    />
                                    {!job.shipmentType && (
                                        <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '2px' }}>
                                            Select shipment type first
                                        </small>
                                    )}
                                    {job.shipmentType && getFilteredCargoTypes(job.shipmentType).length === 0 && (
                                        <small style={{ color: '#856404', fontSize: '10px', display: 'block', marginTop: '2px' }}>
                                            No cargo types available for selected shipment type
                                        </small>
                                    )}
                                </div>
                            </div>

                            {/* Updated row with dynamic CSS class */}
                            <div className={`row compact ${job.cargoNature !== 'DANGEROUS' ? 'no-dangerous' : ''}`}>
                                <div>
                                    <label className="required">Cargo Nature</label>
                                    <Select2Dropdown
                                        options={cargoNatureOptions.map(nature => ({ id: nature, text: nature }))}
                                        value={job.cargoNature}
                                        onChange={(value) => handleInputChange('cargoNature', value)}
                                        placeholder="SELECT CARGO NATURE"
                                        displayKey="text"
                                        valueKey="id"
                                        required
                                        disabled={readOnly}
                                        clearable={true}
                                    />
                                </div>
                                
                                {/* Only show Dangerous Classes when DANGEROUS is selected */}
                                {job.cargoNature === 'DANGEROUS' && (
                                    <div>
                                        <label className="required">Dangerous Classes</label>
                                        <MultiSelectDropdown
                                            options={dangerousClassOptions}
                                            selectedValues={Array.isArray(job.dangerousClasses) ? job.dangerousClasses : []}
                                            onChange={(values) => handleInputChange('dangerousClasses', Array.isArray(values) ? values : [])}
                                            placeholder="Select Dangerous Classes..."
                                            disabled={readOnly}
                                            id="dangerous-classes"
                                        />
                                    </div>
                                )}
                                
                                <div>
                                    <label className="required">Email Subject</label>
                                    <input
                                        type="text"
                                        value={job.emailSubject}
                                        onChange={(e) => handleInputChange('emailSubject', e.target.value)}
                                        required
                                        readOnly={readOnly}
                                        placeholder="Enter email subject"
                                    />
                                </div>
                            </div>                        

                            {/* Updated Status and Remarks section with better proportions */}
                            <div className="row status-compact">
                                <div>
                                    <label>Status</label>
                                    {Array.isArray(job.status) && job.status.includes('COMPLETE') && !hasPermission('JOBS', 'can_delete') ? (
                                        <input
                                            type="text"
                                            value="COMPLETE"
                                            readOnly
                                            style={{ background: '#eee', color: '#333', fontWeight: 'bold' }}
                                        />
                                    ) : (
                                        <MultiSelectDropdown
                                            options={hasPermission('JOBS', 'can_delete') ? [...statusOptions, 'COMPLETE'] : statusOptions}
                                            selectedValues={Array.isArray(job.status) ? job.status : []}
                                            onChange={(values) => {
                                                let newStatus = Array.isArray(values) ? values : [];
                                                if (newStatus.includes('COMPLETE') && !(Array.isArray(job.status) && job.status.includes('COMPLETE'))) {
                                                    newStatus = ['COMPLETE'];
                                                }
                                                handleInputChange('status', newStatus);
                                            }}
                                            placeholder="Select Status..."
                                            disabled={readOnly}
                                            id="status"
                                        />
                                    )}
                                </div>
                                
                                {/* Status Remarks - Show when certain statuses are selected */}
                                {Array.isArray(job.status) && job.status.some && job.status.some(s => s.includes('/') || s.includes('MISSING') || s.includes('EXEMPTION')) && (
                                    <div>
                                        <label className="required">Remarks</label>
                                        <input
                                            type="text"
                                            placeholder="Enter Remarks"
                                            value={job.statusRemark || ''}
                                            onChange={(e) => handleInputChange('statusRemark', e.target.value)}
                                            readOnly={readOnly}
                                            required
                                        />
                                    </div>
                                )}
                            </div>

                            {isExBond && (
                                <div className="row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 10, marginTop: 10, fontSize: 11 }}>
                                    <div>
                                        <label className="required" style={{ fontSize: 11 }}>Previous Tansad/IDF</label>
                                        <Select2Dropdown
                                            options={[{ id: 'OLD', text: 'OLD' }, { id: 'IDF', text: 'IDF' }, { id: 'TANSAD', text: 'TANSAD' }]}
                                            value={job.previousDocType}
                                            onChange={handlePreviousDocTypeChange}
                                            placeholder="SELECT"
                                            displayKey="text"
                                            valueKey="id"
                                            required
                                            disabled={readOnly}
                                            clearable={true}
                                        />
                                    </div>
                                    <div style={{ position: 'relative' }} ref={job.previousDocType === 'IDF' ? previousLookupBoxRef : null}>
                                        <label style={{ fontSize: 11 }} className={job.previousDocType ? 'required' : ''}>Previous IDF</label>
                                        <input
                                            type="text"
                                            value={job.previousIdfNo}
                                            onChange={(e) => handlePreviousIdfInputChange(e.target.value)}
                                            onInput={formatPreviousIdfNoInput}
                                            readOnly={readOnly || job.previousDocType === 'TANSAD'}
                                            disabled={!job.previousDocType}
                                            placeholder="1234567-YY"
                                            style={job.previousDocType === 'TANSAD' ? { backgroundColor: '#e9ecef', cursor: 'not-allowed', fontSize: 11 } : { fontSize: 11 }}
                                        />
                                        {job.previousDocType === 'IDF' && previousLookupOpen && (
                                            <div style={{
                                                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
                                                background: '#fff', border: '1px solid #ccc', borderRadius: 4,
                                                maxHeight: 220, overflowY: 'auto', boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                                            }}>
                                                {previousLookupLoading && (
                                                    <div style={{ padding: 8, fontSize: 11, color: '#666' }}>Searching...</div>
                                                )}
                                                {!previousLookupLoading && previousLookupResults.length === 0 && (
                                                    <div style={{ padding: 8, fontSize: 11, color: '#666' }}>No matching IDF found</div>
                                                )}
                                                {!previousLookupLoading && previousLookupResults.map(result => (
                                                    <div
                                                        key={result.id}
                                                        onClick={() => handleSelectPreviousJob(result)}
                                                        style={{ padding: '6px 8px', fontSize: 11, cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                    >
                                                        <strong>{result.idf_no}</strong> — TANSAD: {result.tansad_no || 'N/A'} | {result.customer_name || 'N/A'}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ position: 'relative' }} ref={job.previousDocType === 'TANSAD' ? previousLookupBoxRef : null}>
                                        <label style={{ fontSize: 11 }} className={job.previousDocType && job.previousDocType !== 'IDF' ? 'required' : ''}>Previous Tansad</label>
                                        <input
                                            type="text"
                                            value={job.previousTansadNo}
                                            onChange={(e) => handlePreviousTansadInputChange(e.target.value)}
                                            onInput={formatPreviousTansadNoInput}
                                            readOnly={readOnly || job.previousDocType === 'IDF'}
                                            disabled={!job.previousDocType}
                                            placeholder="TZDL-YY-1234567"
                                            style={job.previousDocType === 'IDF' ? { backgroundColor: '#e9ecef', cursor: 'not-allowed', fontSize: 11 } : { fontSize: 11 }}
                                        />
                                        {job.previousDocType === 'TANSAD' && previousLookupOpen && (
                                            <div style={{
                                                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
                                                background: '#fff', border: '1px solid #ccc', borderRadius: 4,
                                                maxHeight: 220, overflowY: 'auto', boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                                            }}>
                                                {previousLookupLoading && (
                                                    <div style={{ padding: 8, fontSize: 11, color: '#666' }}>Searching...</div>
                                                )}
                                                {!previousLookupLoading && previousLookupResults.length === 0 && (
                                                    <div style={{ padding: 8, fontSize: 11, color: '#666' }}>No matching TANSAD found</div>
                                                )}
                                                {!previousLookupLoading && previousLookupResults.map(result => (
                                                    <div
                                                        key={result.id}
                                                        onClick={() => handleSelectPreviousJob(result)}
                                                        style={{ padding: '6px 8px', fontSize: 11, cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                    >
                                                        <strong>{result.tansad_no}</strong> — IDF: {result.idf_no || 'N/A'} | {result.customer_name || 'N/A'}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11 }}>Ex Bond Type</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 11 }}>
                                                <input
                                                    type="radio"
                                                    name="exBondType"
                                                    value="PARTIAL"
                                                    checked={job.exBondType === 'PARTIAL'}
                                                    onChange={() => handleExBondTypeChange('PARTIAL')}
                                                    disabled={readOnly}
                                                    style={{ marginRight: 3 }}
                                                />
                                                Partial
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 11 }}>
                                                <input
                                                    type="radio"
                                                    name="exBondType"
                                                    value="FULL"
                                                    checked={job.exBondType === 'FULL'}
                                                    onChange={() => handleExBondTypeChange('FULL')}
                                                    disabled={readOnly}
                                                    style={{ marginRight: 3 }}
                                                />
                                                Full
                                            </label>
                                        </div>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11 }}>&nbsp;</label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginTop: 6, fontSize: 11 }}>
                                            <input
                                                type="checkbox"
                                                checked={job.changeOfOwnership}
                                                onChange={(e) => handleChangeOfOwnershipToggle(e.target.checked)}
                                                disabled={readOnly}
                                            />
                                            <span style={{ fontWeight: 600 }}>Change of Ownership</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Shipment Details Section */}
                {(canShowOtherSections || isDocumentsOnlyMode) && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('shipment-details-section')}>Shipment Details</h3>
                        <div id="shipment-details-section" className="section-content" style={{ display: 'block' }}>
                            <div className="row" style={{ gridTemplateColumns: '1.3fr 1fr' }}>
                                <div>
                                    <label className="required">Customer</label>
                                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Select2Dropdown
                                                options={customers}
                                                value={job.customer}
                                                onChange={(value) => handleInputChange('customer', value)}
                                                placeholder="SELECT CUSTOMER"
                                                displayKey="name"
                                                valueKey="id"
                                                required
                                                disabled={readOnly}
                                                clearable={true}
                                                onDropdownOpen={fetchCustomers}
                                                loading={customersLoading}
                                            />
                                        </div>
                                        {customerNotes.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={openCustomerNotes}
                                                style={{
                                                    padding: '4px 8px',
                                                    background: '#ff0000',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                Customer Notes
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label>Supplier (Exporter)</label>
                                    <Select2Dropdown
                                        options={suppliers}
                                        value={job.supplier}
                                        onChange={(value) => handleInputChange('supplier', value)}
                                        placeholder="SELECT SUPPLIER (EXPORTER)"
                                        displayKey="name"
                                        valueKey="id"
                                        disabled={readOnly}
                                        clearable={true}
                                        onDropdownOpen={fetchSuppliers} // <-- Add this prop
                                        loading={suppliersLoading}      // <-- Show loading if supported by your Select2Dropdown
                                    />
                                </div>
                            </div>
                            {/* Add Customer Reference Row */}
                            {job.customer && customers.find(c => c.id === parseInt(job.customer))?.client_reference_required && (
                                <div className="row">
                                    <div>
                                        <label className="required">Customer Ref No.</label>
                                        <input
                                            type="text"
                                            value={job.customerRefNo}
                                            onChange={(e) => handleInputChange('customerRefNo', e.target.value)}
                                            required
                                            readOnly={readOnly}
                                            placeholder="Enter customer reference number"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="row" style={{ gridTemplateColumns: job.cargoType === 'ZNZ' ? '1fr 1fr' : '1fr 1fr 1fr' }}>
                                {job.cargoType !== 'ZNZ' && (
                                    <div>
                                        <label>{isExBond && job.exBondType === 'PARTIAL' ? 'Repacking BL' : 'MBL No.'}</label>
                                        <input
                                            type="text"
                                            value={job.mblNo}
                                            onChange={(e) => handleInputChange('mblNo', e.target.value)}
                                            onBlur={(e) => handleNominationBlur(e.target.value)}
                                            readOnly={readOnly}
                                            ref={mblRef}
                                        />
                                    </div>
                                )}
                                <div>
                                    <label>Invoice No.</label>
                                    <input
                                        type="text"
                                        value={job.invoiceNo}
                                        onChange={(e) => handleInputChange('invoiceNo', e.target.value)}                                        
                                        readOnly={readOnly}
                                    />
                                </div>
                                <div>
                                    <label>KK File No.</label>
                                    <input
                                        type="text"
                                        value={job.kkFileNo}
                                        readOnly={true}  // Always readonly - no onChange handler
                                        placeholder={
                                            initialData && initialData.kk_file_no 
                                                ? "KK File Number from database" 
                                                : "Will be generated automatically when job is saved"
                                        }
                                        style={{ 
                                            backgroundColor: '#f8f9fa',  // Always grayed out
                                            color: job.kkFileNo ? '#333' : '#6c757d',
                                            cursor: 'not-allowed'
                                        }}
                                    />
                                    {!job.kkFileNo && !initialData && (
                                        <small style={{ color: '#6c757d', fontSize: '10px' }}>
                                            KK File Number will be automatically generated by the system when the job is saved
                                        </small>
                                    )}                                    
                                </div>
                            </div>

                            {job.cargoType === 'LCL' && (
                                <div className="row">
                                    <div>
                                        <label className="required">HBL/SO No.</label>
                                        <input
                                            type="text"
                                            value={job.hblSoNo}
                                            onChange={(e) => handleInputChange('hblSoNo', e.target.value)}
                                            onBlur={(e) => handleNominationBlur(e.target.value)}
                                            readOnly={readOnly}
                                            ref={hblSoRef}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                                <div>
                                    <label>{(job.shipmentType === 'EXPORT-ROAD' && job.cargoType === 'SCT') ? 'C2 Declaration Number' : 'IDF No.'}</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <input
                                            type="text"
                                            style={{ flex: 1, minWidth: 0 }}
                                            value={job.tasac ? (job.idfNo ? job.idfNo.trim() + ' (TASAC)' : '') : job.idfNo}
                                            onChange={(e) => handleInputChange('idfNo', e.target.value.replace(/\s*\(TASAC\)\s*/gi, '').trim())}
                                            onInput={job.shipmentType === 'EXPORT-ROAD' ? undefined : formatIdfNoInput}
                                            readOnly={readOnly}
                                            placeholder={job.shipmentType === 'EXPORT-ROAD' ? 'C2 Declaration Number' : '1234567-YY'}
                                            ref={idfRef}
                                        />
                                        {job.shipmentType !== 'EXPORT-ROAD' && (
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px', margin: 0, whiteSpace: 'nowrap', cursor: readOnly ? 'default' : 'pointer', fontWeight: '600', fontSize: '11px' }}>
                                            <input
                                                type="checkbox"
                                                checked={job.tasac}
                                                onChange={(e) => handleInputChange('tasac', e.target.checked)}
                                                disabled={readOnly}
                                                style={{ margin: 0, cursor: readOnly ? 'default' : 'pointer' }}
                                            />
                                            TASAC
                                        </label>
                                        )}
                                    </div>
                                </div>
                                {job.cargoType !== 'SCT' && (
                                <div>
                                    <label>TANSAD No.</label>
                                    <input
                                        type="text"
                                        value={job.tansadNo}
                                        onChange={(e) => handleInputChange('tansadNo', e.target.value)}
                                        onInput={formatTansadNoInput}
                                        readOnly={readOnly}
                                        placeholder="TZDL-YY-1234567"
                                        ref={tansadNoRef}
                                    />
                                </div>
                                )}
                                {job.cargoType !== 'SCT' && (
                                <div>
                                    <label>IDF/TANSAD Date</label>
                                    <input
                                        type="date"
                                        value={job.tansadDate}
                                        onChange={(e) => handleInputChange('tansadDate', e.target.value)}
                                        readOnly={readOnly}
                                        ref={tansadDateRef}
                                    />
                                </div>
                                )}
                            </div>

                            <div className="row" style={{ gridTemplateColumns: job.tasac ? '1fr' : '1fr 1fr 1fr' }}>
                                {!job.tasac && (
                                    <div>
                                        <label>UCR No.</label>
                                        <input
                                            type="text"
                                            value={job.ucrNo}
                                            onChange={(e) => handleInputChange('ucrNo', e.target.value)}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                )}
                                {!job.tasac && (
                                    <div>
                                        <label>UCR Date</label>
                                        <input
                                            type="date"
                                            value={job.ucrDate}
                                            onChange={(e) => handleInputChange('ucrDate', e.target.value)}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                )}
                                <div>
                                    <label>Exemption Ref No.</label>
                                    <input
                                        type="text"
                                        value={job.exemptionRefNo}
                                        onChange={(e) => handleInputChange('exemptionRefNo', e.target.value)}
                                        readOnly={readOnly}
                                    />
                                </div>
                            </div>                           
                            {!['IMPORT-AIR', 'IMPORT-ROAD', 'EXPORT-ROAD'].includes(job.shipmentType) && !isExBondVehicle && job.cargoType !== 'ZNZ' && (
                                <div className="row" style={{ gridTemplateColumns: '1.3fr 1fr' }}>
                                    <div>
                                        <label className="required">Shipping Line</label>
                                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <Select2Dropdown
                                                    options={shippingLines}
                                                    value={job.shippingLineId}
                                                    onChange={handleShippingLineChange}
                                                    placeholder="SELECT SHIPPING LINE"
                                                    displayKey="name"
                                                    valueKey="id"
                                                    disabled={readOnly}
                                                    clearable={true}
                                                    onDropdownOpen={fetchShippingLines}
                                                    loading={shippingLinesLoading}
                                                />
                                            </div>
                                            {shippingLineNotes.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={openShippingLineNotes}
                                                    style={{
                                                        padding: '4px 8px',
                                                        background: '#ff0000',
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    Shipping Line Notes
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="required">BL Carrier</label>
                                        <Select2Dropdown
                                            options={blCarriers}
                                            value={job.blCarrierId}
                                            onChange={handleBlCarrierChange}
                                            placeholder={job.shippingLineId ? "SELECT BL CARRIER" : "Select Shipping Line first"}
                                            displayKey="name"
                                            valueKey="id"
                                            disabled={readOnly || !job.shippingLineId}
                                            clearable={true}
                                            onDropdownOpen={fetchBLCarriers} // <-- Add this prop
                                            loading={blCarriersLoading}      // <-- Show loading if supported by your Select2Dropdown
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Supplier & Notify Party Details Section */}
                 {canShowOtherSections && !isDocumentsOnlyMode && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('supplier-notify-section')}>Supplier & Notify Party Details</h3>
                        <div id="supplier-notify-section" className="section-content" style={{ display: 'block' }}>
                            {/* Supplier Details */}
                            <div className="row">
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <h3>Supplier Details</h3>
                                </div>
                            </div>
                            <div className="row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                                <div>
                                    <label>Name</label>
                                    <input
                                        type="text"
                                        value={job.supplierDetails.name}
                                        readOnly
                                    />
                                </div>
                                <div>
                                    <label>Country</label>
                                    <input
                                        type="text"
                                        value={job.supplierDetails.country}
                                        readOnly
                                    />
                                </div>
                                <div>
                                    <label>City</label>
                                    <input
                                        type="text"
                                        value={job.supplierDetails.city}
                                        readOnly
                                    />
                                </div>
                            </div>
                            
                            {/* Consignee Details */}
                            {!isExBond && job.cargoType !== 'ZNZ' &&  (
                            <div className="row" style={{ marginTop: '20px' }}>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <h3>Consignee Details</h3>
                                    <div style={{ marginBottom: '10px' }}>
                                        <label className="required" style={{ marginRight: '8px' }}>Consignee Same As Customer</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="consigneeSameAsCustomer"
                                                    value="yes"
                                                    checked={job.consignee.sameAsCustomer === true}
                                                    onChange={() => handleConsigneeSameAsCustomer(true)}
                                                    disabled={readOnly}
                                                    style={{ marginRight: '4px' }}
                                                />
                                                <span style={{ fontSize: '12px' }}>Yes</span>
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="consigneeSameAsCustomer"
                                                    value="no"
                                                    checked={job.consignee.sameAsCustomer === false}
                                                    onChange={() => handleConsigneeSameAsCustomer(false)}
                                                    disabled={readOnly}
                                                    style={{ marginRight: '4px' }}
                                                />
                                                <span style={{ fontSize: '12px' }}>No</span>
                                            </label>
                                        </div>
                                    </div>
                                    
                                    {/* Consignee fields in one row */}
                                    {(job.consignee.sameAsCustomer === true || job.consignee.sameAsCustomer === false) && (
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                name="consigneeName"
                                                placeholder="Consignee Name"
                                                value={job.consignee.name}
                                                onChange={(e) => handleNestedChange('consignee', 'name', e.target.value)}
                                                required
                                                readOnly={readOnly || job.consignee.sameAsCustomer === true}
                                                style={{ width: '180px' }}
                                            />
                                            <input
                                                type="text"
                                                name="consigneeAddress"
                                                placeholder="Address"
                                                value={job.consignee.address}
                                                onChange={(e) => handleNestedChange('consignee', 'address', e.target.value)}
                                                readOnly={readOnly || job.consignee.sameAsCustomer === true}
                                                style={{ width: '180px', marginLeft: '10px' }}
                                            />                                            
                                            <input
                                                type="text"
                                                name="consigneeCountry"
                                                placeholder="Country"
                                                value={job.consignee.country}
                                                onChange={(e) => handleNestedChange('consignee', 'country', e.target.value)}
                                                readOnly={readOnly || job.consignee.sameAsCustomer === true}
                                                style={{ width: '120px', marginLeft: '10px' }}
                                            />
                                            <input
                                                type="text"
                                                name="consigneeCity"
                                                placeholder="City"
                                                value={job.consignee.city}
                                                onChange={(e) => handleNestedChange('consignee', 'city', e.target.value)}
                                                readOnly={readOnly || job.consignee.sameAsCustomer === true}
                                                style={{ width: '120px', marginLeft: '10px' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            )}

                            {!isExBond && (
                            <div className="row" style={{ marginTop: '20px' }}>
                                {/* Notify Party Details */}
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <h3>Notify Party Details</h3>
                                    <div style={{ marginBottom: '10px' }}>
                                        <label className="required" style={{ marginRight: '8px' }}>Notify Party Same As Customer</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="notifySameAsCustomer"
                                                    value="yes"
                                                    checked={job.notify.sameAsCustomer === true}
                                                    onChange={() => handleNotifySameAsCustomer(true)}
                                                    disabled={readOnly}
                                                    style={{ marginRight: '4px' }}
                                                />
                                                <span style={{ fontSize: '12px' }}>Yes</span>
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="notifySameAsCustomer"
                                                    value="no"
                                                    checked={job.notify.sameAsCustomer === false}
                                                    onChange={() => handleNotifySameAsCustomer(false)}
                                                    disabled={readOnly}
                                                    style={{ marginRight: '4px' }}
                                                />
                                                <span style={{ fontSize: '12px' }}>No</span>
                                            </label>
                                        </div>
                                    </div>
                                    
                                    {/* Notify fields in one row */}
                                    {(job.notify.sameAsCustomer === true || job.notify.sameAsCustomer === false) && (
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                name="notifyName"
                                                placeholder="Notify Name"
                                                value={job.notify.name}
                                                onChange={(e) => handleNestedChange('notify', 'name', e.target.value)}
                                                required
                                                readOnly={readOnly || job.notify.sameAsCustomer === true}
                                                style={{ width: '180px' }}
                                            />
                                            <input
                                                type="text"
                                                name="notifyAddress"
                                                placeholder="Address"
                                                value={job.notify.address}
                                                onChange={(e) => handleNestedChange('notify', 'address', e.target.value)}
                                                readOnly={readOnly || job.notify.sameAsCustomer === true}
                                                style={{ width: '180px', marginLeft: '10px' }}
                                            />
                                            <input
                                                type="text"
                                                name="notifyCountry"
                                                placeholder="Country"
                                                value={job.notify.country}
                                                onChange={(e) => handleNestedChange('notify', 'country', e.target.value)}
                                                readOnly={readOnly || job.notify.sameAsCustomer === true}
                                                style={{ width: '120px', marginLeft: '10px' }}
                                            />
                                            <input
                                                type="text"
                                                name="notifyCity"
                                                placeholder="City"
                                                value={job.notify.city}
                                                onChange={(e) => handleNestedChange('notify', 'city', e.target.value)}
                                                readOnly={readOnly || job.notify.sameAsCustomer === true}
                                                style={{ width: '120px', marginLeft: '10px' }}
                                            />                                            
                                        </div>
                                    )}
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Vessel/Line Details Section */}
                {canShowOtherSections && !isDocumentsOnlyMode && !isExBondVehicle && job.cargoType !== 'ZNZ' && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('vessel-details-section')}>Vessel/Line Details</h3>
                        <div id="vessel-details-section" className="section-content" style={{ display: 'block' }}>
                            <div className="vessel-details-container">
                                {/* Loading Vessel Details - Left Side */}
                                <div className="vessel-loading-section">
                                    <h3>Loading Vessel Details</h3>
                                    <div className="vessel-form-group">
                                        {!['IMPORT-ROAD', 'EXPORT-ROAD'].includes(job.shipmentType) && (
                                            <div>
                                                <label className="required">
                                                    {job.shipmentType === 'IMPORT-AIR' ? 'Airline Name' : 'First Vessel'}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={job.firstVessel}
                                                    onChange={(e) => handleInputChange('firstVessel', e.target.value)}
                                                    required
                                                    readOnly={readOnly}
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <label className="required">
                                                {job.shipmentType === 'IMPORT-AIR' 
                                                    ? 'Flight Number' 
                                                    : ['IMPORT-ROAD', 'EXPORT-ROAD'].includes(job.shipmentType) 
                                                        ? 'Vehicle Number' 
                                                        : 'First Voyage Number'}
                                            </label>
                                            <input
                                                type="text"
                                                value={job.firstVoyageNumber}
                                                onChange={(e) => handleInputChange('firstVoyageNumber', e.target.value)}
                                                required
                                                readOnly={readOnly}
                                            />
                                        </div>
                                        {!isRoadConsignment && (
                                            <div>
                                                <label className="required">Shipped On Board Date</label>
                                                <input
                                                    type="date"
                                                    value={job.shippedOnBoardDate}
                                                    onChange={(e) => handleInputChange('shippedOnBoardDate', e.target.value)}
                                                    required
                                                    readOnly={readOnly}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    {/*<div className="vessel-form-group">
                                                                               
                                        <div className="vessel-form-group">
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <label className="checkbox-item">
                                                    <input
                                                        type="checkbox"
                                                        checked={job.sameAsFinalVessel}
                                                        onChange={(e) => handleSameAsFinalVessel(e.target.checked)}
                                                        disabled={readOnly}
                                                    />
                                                    <span>Same as Final Vessel</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>*/}
                                </div>

                                {/* Divider */}
                                <div className="vessel-divider"></div>

                                {/* Final Vessel Details - Right Side */}
                                <div className="vessel-final-section">
                                    {!['IMPORT-AIR', 'IMPORT-ROAD', 'EXPORT-ROAD'].includes(job.shipmentType) && (
                                        <>
                                            <h3>Final Vessel Details</h3>
                                            <div className="vessel-form-group">
                                                {/*<div>
                                                    <label>Voyage</label>
                                                    <div className="voyage-select-container">
                                                        <Select2Dropdown
                                                            options={filteredVoyages}
                                                            value={job.voyage}
                                                            onChange={(value) => handleVoyageChange(value)}
                                                            placeholder={
                                                                !job.shippingLineId 
                                                                    ? "Select Shipping Line first" 
                                                                    : filteredVoyages.length === 0 
                                                                        ? "No voyages available for selected shipping line"
                                                                        : "SELECT VOYAGE"
                                                            }
                                                            displayKey="display_name"
                                                            valueKey="id"
                                                            disabled={readOnly || job.sameAsFinalVessel || !job.shippingLineId || filteredVoyages.length === 0}
                                                            clearable={true}
                                                            style={{ width: '900%' }}
                                                        />
                                                        {!readOnly && job.shippingLineId && (
                                                            <button 
                                                                type="button" 
                                                                onClick={handleAddNewVoyage}
                                                                className="add-voyage-btn"
                                                                disabled={job.sameAsFinalVessel}
                                                            >
                                                                Add New
                                                            </button>
                                                        )}
                                                    </div>
                                                    {job.shippingLineId && filteredVoyages.length === 0 && (
                                                        <small style={{ color: '#856404', fontSize: '10px', display: 'block', marginTop: '2px' }}>
                                                            No voyages found for the selected shipping line. You may need to add a new voyage.
                                                        </small>
                                                    )}
                                                </div> */}                              
                                            </div>
                                            <div className="vessel-form-group">
                                                <div>
                                                    <label>Shipping Line</label>
                                                    <input
                                                        type="text"
                                                        value={job.shippingLine}
                                                        onChange={(e) => handleInputChange('shippingLine', e.target.value)}                                                
                                                        readOnly
                                                    />
                                                </div>
                                                <div>
                                                    <label>Voyage Number</label>
                                                    <input
                                                        type="text"
                                                        value={job.finalVoyageNumber}
                                                        onChange={(e) => handleInputChange('finalVoyageNumber', e.target.value)}                                                
                                                        readOnly
                                                    />
                                                </div>
                                                <div>
                                                    <label>Vessel</label>
                                                    <input
                                                        type="text"
                                                        value={job.finalVessel}
                                                        onChange={(e) => handleInputChange('finalVessel', e.target.value)}                                               
                                                        readOnly
                                                    />
                                                </div>
                                            </div>

                                            {/* Arrival Details - Now inside Final Vessel Details */}
                                            <div className="vessel-form-group">
                                                <div style={{ gridColumn: '1 / -1' }}>
                                                    <h4 style={{ margin: '20px 0 10px 0', color: '#555' }}>Arrival Details</h4>
                                                </div>
                                    </div>
                                    <div className="vessel-form-group">
                                        <div>
                                            <label>ETA</label>
                                            <input
                                                type="date"
                                                value={job.voyage_details?.eta || job.eta}
                                                readOnly
                                                style={{ backgroundColor: '#f8f9fa' }}
                                            />
                                        </div>
                                        <div>
                                            <label>ETB</label>
                                            <input
                                                type="date"
                                                value={job.voyage_details?.etb || job.etb}
                                                readOnly
                                                style={{ backgroundColor: '#f8f9fa' }}
                                            />
                                        </div>
                                    </div>
                                    <div className="vessel-form-group">
                                        <div>
                                            <label>Arrival Date (ATA)</label>
                                            <input
                                                type="date"
                                                value={job.arrivalDate}
                                                onChange={(e) => handleInputChange('arrivalDate', e.target.value)}
                                                readOnly={
                                                    readOnly ||
                                                    (
                                                      !job.arrivalDate &&  // empty
                                                      arrivalStatus !== 'YES' // only editable if user chose YES
                                                    )
                                                }
                                                ref={arrivalDateRef}
                                                style={{ backgroundColor: (readOnly || (!job.arrivalDate && arrivalStatus !== 'YES')) ? '#f8f9fa' : '#fff' }}
                                            />
                                            {(!job.arrivalDate && !readOnly) && (
                                                <div style={{ marginTop: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 20 }}>
                                                    <span style={{ fontWeight: 500 }}>Vessel Arrived?</span>
                                                    <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        <input
                                                            type="radio"
                                                            name="vessel_arrived"
                                                            value="YES"
                                                            checked={arrivalStatus === 'YES'}
                                                            onChange={() => {
                                                                setArrivalStatus('YES');
                                                                toast.info('Enter Arrival Date (ATA)');
                                                                setTimeout(() => {
                                                                    arrivalDateRef.current?.removeAttribute('readonly');
                                                                    arrivalDateRef.current?.focus();
                                                                }, 50);
                                                            }}
                                                            disabled={readOnly}
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        <input
                                                            type="radio"
                                                            name="vessel_arrived"
                                                            value="NO"
                                                            checked={arrivalStatus === 'NO'}
                                                            onChange={() => {
                                                                setArrivalStatus('NO');
                                                                handleInputChange('arrivalDate', '');
                                                            }}
                                                            disabled={readOnly}
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <label>Berthing Date (ATB)</label>
                                            <input
                                                type="date"
                                                value={job.berthingDate}
                                                onChange={(e) => handleInputChange('berthingDate', e.target.value)}
                                                readOnly={
                                                    readOnly ||
                                                    (
                                                      !job.berthingDate &&
                                                      berthingStatus !== 'YES'
                                                    )
                                                }
                                                ref={berthingDateRef}
                                                style={{ backgroundColor: (readOnly || (!job.berthingDate && berthingStatus !== 'YES')) ? '#f8f9fa' : '#fff' }}
                                            />
                                            {(!job.berthingDate && !readOnly) && (
                                                <div style={{ marginTop: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 20 }}>
                                                    <span style={{ fontWeight: 500 }}>Vessel Berthed?</span>
                                                    <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        <input
                                                            type="radio"
                                                            name="vessel_berthed"
                                                            value="YES"
                                                            checked={berthingStatus === 'YES'}
                                                            onChange={() => {
                                                                setBerthingStatus('YES');
                                                                toast.info('Enter Berthing Date (ATB)');
                                                                setTimeout(() => {
                                                                    berthingDateRef.current?.removeAttribute('readonly');
                                                                    berthingDateRef.current?.focus();
                                                                }, 50);
                                                            }}
                                                            disabled={readOnly}
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        <input
                                                            type="radio"
                                                            name="vessel_berthed"
                                                            value="NO"
                                                            checked={berthingStatus === 'NO'}
                                                            onChange={() => {
                                                                setBerthingStatus('NO');
                                                                handleInputChange('berthingDate', '');
                                                            }}
                                                            disabled={readOnly}
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="vessel-form-group">
                                        <div>
                                            <label>Berth Terminal</label>
                                            <input
                                                type="text"
                                                value={job.berthedAt}
                                                readOnly
                                                style={{ backgroundColor: '#f8f9fa' }}
                                            />
                                        </div>
                                        {/* Conditional ICD section based on TRA Manifest Date */}
                                        {(() => {
                                            const selectedVoyage = filteredVoyages.find(v => v.id === parseInt(job.voyage));
                                            const hasTraManifestDate = selectedVoyage && selectedVoyage.tra_manifest_ready;
    
                                            if (hasTraManifestDate) {
                                                return (
                                                    <div>
                                                        <label>ICD</label>
                                                        <input
                                                            type="text"
                                                            value={job.icd}
                                                            readOnly
                                                            style={{ backgroundColor: '#f8f9fa' }}
                                                        />
                                                        {/*<Select2Dropdown
                                                            options={icdOptions.map(icd => ({ id: icd, text: icd }))}
                                                            value={job.icd}
                                                            onChange={(value) => handleInputChange('icd', value)}
                                                            placeholder="Select ICD"
                                                            displayKey="text"
                                                            valueKey="id"
                                                            required
                                                            disabled={readOnly}
                                                            clearable={true}
                                                        />*/}
                                                    </div>
                                                );
                                            } else {
                                                return (
                                                    <div style={{ color: 'red', fontWeight: 'bold', fontSize: '12px' }}>
                                                        TRA Manifest Date MUST be updated to update ICD.
                                                    </div>
                                                );
                                            }
                                        })()}
                                    
                                        {/* ICD Transfer Date - only show if TRA Manifest Date exists */}
                                        {(() => {
                                            const selectedVoyage = filteredVoyages.find(v => v.id === parseInt(job.voyage));
                                            const hasTraManifestDate = selectedVoyage && selectedVoyage.tra_manifest_ready;
    
                                            if (hasTraManifestDate) {
                                                return (
                                                    <div className="vessel-form-group">
                                                        <div>
                                                            <label className="required">ICD Transfer Date</label>
                                                            <input
                                                                type="date"
                                                                value={job.icdTransferDate}
                                                                onChange={(e) => handleInputChange('icdTransferDate', e.target.value)}                                                                
                                                                readOnly={readOnly}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                    </>
                                  )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Cargo Details Section */}
                {canShowOtherSections && !isDocumentsOnlyMode && job.cargoType !== 'ZNZ' && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('cargo-details-section')}>Cargo Details</h3>
                        <div id="cargo-details-section" className="section-content" style={{ display: 'block' }}>
                            {!isExBondVehicle && (<div className="row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                                <div>
                                    <label className="required">Country of Loading</label>
                                    <Select2Dropdown
                                        options={countries}
                                        value={job.countryOfLoading}
                                        onChange={(value) => handleInputChange('countryOfLoading', value)}
                                        placeholder="SELECT COUNTRY"
                                        displayKey="name"
                                        valueKey="cca2"
                                        required
                                        disabled={readOnly}
                                        clearable={true}
                                    />
                                </div>
                                <div>
                                    <label className="required">
                                        {['IMPORT-AIR', 'IMPORT-ROAD', 'EXPORT-ROAD'].includes(job.shipmentType) ? 'City of Loading' : 'Port of Loading'}
                                    </label>
                                    <Select2Dropdown
                                        options={cities.map(city => ({ 
                                            id: city, 
                                            text: city, 
                                            name: city,
                                            value: city  // Add this to ensure consistent value matching
                                        }))}
                                        value={job.portOfLoading}
                                        onChange={handlePortChange}
                                        placeholder={
                                            loadingCities 
                                                ? "Loading..." 
                                                : cities.length === 0 && job.countryOfLoading 
                                                    ? "No options available" 
                                                    : ['IMPORT-AIR', 'IMPORT-ROAD', 'EXPORT-ROAD'].includes(job.shipmentType) ? "SELECT CITY" : "SELECT PORT"
                                        }
                                        displayKey="text"
                                        valueKey="text"
                                        required
                                        disabled={readOnly || !job.countryOfLoading || loadingCities}
                                        clearable={true}
                                    />
                                    {loadingCities && (
                                        <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '2px' }}>
                                            🔄 Loading options...
                                        </small>
                                    )}
                                    {!loadingCities && cities.length === 0 && job.countryOfLoading && (
                                        <small style={{ color: '#856404', fontSize: '10px', display: 'block', marginTop: '2px' }}>
                                            No options found for selected country
                                        </small>
                                    )}
                                    
                                </div>
                                <div>
                                    <label className="required">{isRoadConsignment ? 'Border' : 'Port of Discharge'}</label>
                                    {isRoadConsignment ? (
                                        <Select2Dropdown
                                            options={(ports || []).filter(p => p.port_type === 'BORDER').map(port => ({ id: port.name, text: port.name }))}
                                            value={job.portOfDischarge}
                                            onChange={value => handleInputChange('portOfDischarge', value)}
                                            placeholder="SELECT BORDER PORT"
                                            displayKey="text"
                                            valueKey="id"
                                            required
                                            disabled={readOnly}
                                            clearable={true}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={job.portOfDischarge}
                                            onChange={(e) => handleInputChange('portOfDischarge', e.target.value)}
                                            required
                                            readOnly={readOnly}
                                        />
                                    )}
                                </div>
                            </div>)}

                            <div className="row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                                {!['IMPORT-AIR', 'IMPORT-ROAD', 'EXPORT-ROAD'].includes(job.shipmentType) && !isLclConsignment && !isExBondVehicle && (
                                    <>
                                        <div>
                                            <label className="required">Nominated By</label>
                                            <Select2Dropdown
                                                options={nominatedByOptions.map(option => ({ id: option, text: option }))}
                                                value={job.nominatedBy}
                                                onChange={(value) => handleInputChange('nominatedBy', value)}
                                                placeholder="SELECT NOMINATED BY"
                                                displayKey="text"
                                                valueKey="id"
                                                required
                                                disabled={readOnly}
                                                clearable={true}
                                            />
                                        </div>
                                        {job.nominatedBy === 'CONSIGNEE' && (
                                            <div>
                                                <label>Folder Name</label>
                                                <input
                                                    type="text"
                                                    value={job.folderName || ''}
                                                    onChange={(e) => handleInputChange('folderName', e.target.value)}
                                                    placeholder="Enter Folder Name"
                                                    readOnly={readOnly}
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <label>Nominated ICD</label>
                                            <Select2Dropdown
                                                options={icdOptions.map(icd => ({ id: icd.name, text: icd.short_name ? `${icd.name} | ${icd.short_name}` : icd.name }))}
                                                value={job.nominatedIcd || ''}
                                                onChange={(value) => handleInputChange('nominatedIcd', value)}
                                                placeholder="SELECT ICD"
                                                displayKey="text"
                                                valueKey="id"
                                                disabled={readOnly}
                                                clearable={true}
                                            />
                                        </div>
                                    </>
                                )}
                                {!isExBond && (
                                <div>
                                    <label>Marks</label>
                                    <input
                                        type="text"
                                        value={job.marks}
                                        onChange={(e) => handleInputChange('marks', e.target.value)}
                                        readOnly={readOnly}
                                    />
                                </div>
                                )}
                            </div>

                            <div className="row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                                <div>
                                    <label className="required">Description</label>
                                    <textarea
                                        value={job.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        required
                                        readOnly={readOnly}
                                        rows="3"
                                    />
                                </div>
                                {!isExBond && (
                                <div>
                                    <label className="required">Material for Port Report</label>
                                    <input
                                        type="text"
                                        value={job.portMaterial}
                                        onChange={(e) => handleInputChange('portMaterial', e.target.value.toUpperCase())}
                                        maxLength={30}
                                        required
                                        readOnly={readOnly}
                                        placeholder="Max 30 characters"
                                    />
                                </div>
                                )}
                            </div>
                            {
                                ['LCL', 'VEHICLE'].includes((job.cargoType || '').toUpperCase()) ? (
                                    <div className="row" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
                                    {!isExBond && (
                                    <div>
                                        <label>Container Number</label>
                                        <input
                                        type="text"
                                        value={job.lclContainerNo}
                                        onChange={e => handleInputChange('lclContainerNo', e.target.value.toUpperCase())}
                                        readOnly={readOnly}
                                        />
                                    </div>
                                    )}
                                    <div>
                                        <label>CBM {job.lclContainerNo ? <span style={{color:'red'}}>*</span> : null}</label>
                                        <input
                                        type={readOnly ? 'text' : 'number'}
                                        min="0"
                                        step="0.01"
                                        value={readOnly ? formatDisplayNumber(job.lclCbm) : job.lclCbm}
                                        onChange={e => handleInputChange('lclCbm', e.target.value)}
                                        readOnly={readOnly}
                                        required={!!job.lclContainerNo}
                                        ref={cbmRef}
                                        />
                                    </div>
                                    <div>
                                        <label>Packages</label>
                                        <input
                                        type="number"
                                        min="0"
                                        value={job.lclPackages}
                                        onChange={e => handleInputChange('lclPackages', e.target.value)}
                                        readOnly={readOnly}
                                        />
                                    </div>
                                    <div>
                                        <label>Package Type</label>
                                        <Select2Dropdown
                                            options={packageTypes}
                                            value={job.lclPackageType}
                                            onChange={(value) => handleInputChange('lclPackageType', value)}
                                            placeholder="SELECT PACKAGE TYPE"
                                            displayKey="text"
                                            valueKey="id"
                                            disabled={readOnly}
                                            clearable={true}
                                        />
                                    </div>
                                    <div>
                                        <label>Gross Weight</label>
                                        <input
                                        type={readOnly ? 'text' : 'number'}
                                        min="0"
                                        step="0.01"
                                        value={readOnly ? formatDisplayNumber(job.lclGrossWeight) : job.lclGrossWeight}
                                        onChange={e => handleInputChange('lclGrossWeight', e.target.value)}
                                        readOnly={readOnly}
                                        />
                                    </div>
                                    </div>
                                ) : (
                                    <div className="row" style={{ gridTemplateColumns: 'repeat(6, 1fr)', gap: 12 }}>
                                        {!isAirOrRoadLayout && (
                                            <div>
                                                <label className="required">Total Containers</label>
                                                <input
                                                    type="number"
                                                    value={job.totalContainers}
                                                    onChange={e => handleInputChange('totalContainers', e.target.value)}
                                                    required={!isAirOrRoadLayout}
                                                    readOnly={readOnly}
                                                    min={0}
                                                    style={{ maxWidth: 110 }}
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <label className="required">Total Packages</label>
                                            <input
                                                type="number"
                                                value={job.totalPackages}
                                                onChange={e => handleInputChange('totalPackages', e.target.value)}
                                                required
                                                readOnly={readOnly}
                                                style={{ maxWidth: 110 }}
                                            />
                                        </div>
                                        <div>
                                            <label className="required">Package Type</label>
                                            <Select2Dropdown
                                                options={packageTypes}
                                                value={job.packageType}
                                                onChange={(value) => handleInputChange('packageType', value)}
                                                placeholder="SELECT PACKAGE TYPE"
                                                displayKey="text"
                                                valueKey="id"
                                                required
                                                disabled={readOnly}
                                                clearable={true}
                                                style={{ maxWidth: 140 }}
                                            />
                                        </div>
                                        <div>
                                            <label className="required">Total Gross Weight</label>
                                            <input
                                                type={(readOnly || !isAirOrRoadLayout) ? 'text' : 'number'}
                                                value={(readOnly || !isAirOrRoadLayout) ? formatDisplayNumber(job.totalGrossWeight) : job.totalGrossWeight}
                                                onChange={(e) => handleInputChange('totalGrossWeight', e.target.value)}
                                                required
                                                readOnly={readOnly || !isAirOrRoadLayout}
                                                style={{ maxWidth: 120 }}
                                            />
                                        </div>
                                        {!isAirOrRoadLayout && (
                                            <div>
                                                <label>Total Tare</label>
                                                <input
                                                    type={readOnly ? 'text' : 'number'}
                                                    value={readOnly ? formatDisplayNumber(job.totalTare) : job.totalTare}
                                                    onChange={(e) => handleInputChange('totalTare', e.target.value)}
                                                    readOnly={readOnly}
                                                    style={{ maxWidth: 110 }}
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <label>Total Gross + Tare Weight</label>
                                            <input
                                                type="text"
                                                value={formatDisplayNumber(job.totalWeight)}
                                                readOnly
                                                style={{ maxWidth: 140 }}
                                            />
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                )}

                {/* Cargo Details Section - ZNZ */}
                {canShowOtherSections && !isDocumentsOnlyMode && job.cargoType === 'ZNZ' && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('cargo-details-section')}>Cargo Details</h3>
                        <div id="cargo-details-section" className="section-content" style={{ display: 'block' }}>
                            <div className="row" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                                <div>
                                    <label className="required">Total Packages</label>
                                    <input
                                        type="number"
                                        value={job.totalPackages}
                                        onChange={e => handleInputChange('totalPackages', e.target.value)}
                                        required
                                        readOnly={readOnly}
                                        style={{ maxWidth: 110 }}
                                    />
                                </div>
                                <div>
                                    <label className="required">Package Type</label>
                                    <Select2Dropdown
                                        options={packageTypes}
                                        value={job.packageType}
                                        onChange={(value) => handleInputChange('packageType', value)}
                                        placeholder="SELECT PACKAGE TYPE"
                                        displayKey="text"
                                        valueKey="id"
                                        required
                                        disabled={readOnly}
                                        clearable={true}
                                        style={{ maxWidth: 140 }}
                                    />
                                </div>
                                <div>
                                    <label className="required">Gross Weight</label>
                                    <input
                                        type="number"
                                        value={job.totalGrossWeight}
                                        onChange={(e) => handleInputChange('totalGrossWeight', e.target.value)}
                                        required
                                        readOnly={readOnly}
                                        style={{ maxWidth: 120 }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Vehicle Shipment Section */}
                {canShowOtherSections && canShowVehicles && !isDocumentsOnlyMode && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('vehicles-section')}>Vehicle Shipment ({vehicles.length})</h3>
                        <div id="vehicles-section" className="section-content" style={{ display: 'block' }}>
                            {vehicles.map((vehicle, index) => (
                                <div key={index} className="repeatable-row">
                                    <div>
                                        <label>Chassis No.</label>
                                        <input
                                            type="text"
                                            value={vehicle.chassis_no}
                                            onChange={(e) => {
                                                const updated = [...vehicles];
                                                updated[index].chassis_no = e.target.value;
                                                setVehicles(updated);
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    <div>
                                        <label>Engine Capacity</label>
                                        <input
                                            type="text"
                                            value={vehicle.engine_capacity}
                                            onChange={(e) => {
                                                const updated = [...vehicles];
                                                updated[index].engine_capacity = e.target.value;
                                                setVehicles(updated);
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    <div>
                                        <label>Engine No.</label>
                                        <input
                                            type="text"
                                            value={vehicle.engine_no}
                                            onChange={(e) => {
                                                const updated = [...vehicles];
                                                updated[index].engine_no = e.target.value;
                                                setVehicles(updated);
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    <div>
                                        <label>Model No.</label>
                                        <input
                                            type="text"
                                            value={vehicle.model_no}
                                            onChange={(e) => {
                                                const updated = [...vehicles];
                                                updated[index].model_no = e.target.value;
                                                setVehicles(updated);
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    <div>
                                        <label>Year</label>
                                        <input
                                            type="number"
                                            value={vehicle.year}
                                            onChange={(e) => {
                                                const updated = [...vehicles];
                                                updated[index].year = e.target.value;
                                                setVehicles(updated);
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    <div>
                                        <label>Vehicle Type</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingTop: 4 }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 'normal' }}>
                                                <input
                                                    type="radio"
                                                    value="SALOON"
                                                    checked={vehicle.vehicle_type === 'SALOON'}
                                                    onChange={(e) => {
                                                        const updated = [...vehicles];
                                                        updated[index].vehicle_type = e.target.value;
                                                        setVehicles(updated);
                                                    }}
                                                    disabled={readOnly}
                                                />
                                                Saloon
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 'normal' }}>
                                                <input
                                                    type="radio"
                                                    value="HEAVY"
                                                    checked={vehicle.vehicle_type === 'HEAVY'}
                                                    onChange={(e) => {
                                                        const updated = [...vehicles];
                                                        updated[index].vehicle_type = e.target.value;
                                                        setVehicles(updated);
                                                    }}
                                                    disabled={readOnly}
                                                />
                                                Heavy
                                            </label>
                                        </div>
                                    </div>
                                    {!readOnly && (
                                        <button type="button" onClick={() => setVehicles(vehicles.filter((_, i) => i !== index))} className="remove-btn">
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                            {!readOnly && (
                                <button type="button" onClick={addVehicle}>Add Vehicle</button>
                            )}
                        </div>
                    </div>
                )}

                {/* Container Details Section */}
                {canShowOtherSections && canShowContainers && !isDocumentsOnlyMode && job.cargoType !== 'ZNZ' && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('containers-section')}>Container Details ({containers.length})</h3>
                        <div id="containers-section" className="section-content" style={{ display: 'block' }}>
                        {containers.map((container, index) => (
                            <div key={index} className="repeatable-row">
                            <div>
                                <label>Container No.</label>
                                <input
                                type="text"
                                value={container.container_no}
                                onChange={(e) => updateContainer(index, 'container_no', e.target.value)}
                                readOnly={readOnly}
                                />
                            </div>
                            <div>
                                <label className="required">Size</label>
                                <Select2Dropdown
                                options={containerSizeOptions}
                                value={container.size || ''}
                                onChange={(value) => updateContainer(index, 'size', value)}
                                placeholder="SELECT SIZE"
                                displayKey="text"
                                valueKey="id"
                                required
                                disabled={readOnly}
                                clearable={true}
                                />
                            </div>
                            <div>
                                <label className="required">Type</label>
                                <Select2Dropdown
                                options={
                                    (containerTypeOptionsBySize[container.size] || []).map(type => ({ id: type, text: type }))
                                }
                                value={container.container_type || ''}
                                onChange={(value) => updateContainer(index, 'container_type', value)}
                                placeholder="SELECT TYPE"
                                displayKey="text"
                                valueKey="id"
                                required
                                disabled={readOnly || !container.size}
                                clearable={true}
                                />
                            </div>
                            <div>
                                <label>Seal No.</label>
                                <input
                                type="text"
                                value={container.seal_no}
                                onChange={(e) => updateContainer(index, 'seal_no', e.target.value)}
                                readOnly={readOnly}
                                />
                            </div>
                            <div>
                                <label>Gross Weight</label>
                                <input
                                type={readOnly ? 'text' : 'number'}
                                value={readOnly ? formatDisplayNumber(container.gross_weight) : container.gross_weight}
                                onChange={(e) => updateContainer(index, 'gross_weight', e.target.value)}
                                readOnly={readOnly}
                                />
                            </div>
                            <div>
                                <label>Tare</label>
                                <input
                                type={readOnly ? 'text' : 'number'}
                                value={readOnly ? formatDisplayNumber(container.tare_weight) : container.tare_weight}
                                onChange={(e) => updateContainer(index, 'tare_weight', e.target.value)}
                                readOnly={readOnly}
                                />
                            </div>
                            <div>
                                <label>KK Seal No.</label>
                                <input
                                type="text"
                                value={container.kk_seal_no}
                                onChange={(e) => updateContainer(index, 'kk_seal_no', e.target.value)}
                                readOnly
                                />
                            </div>
                            {!readOnly && canRemoveRows && (
                                <button type="button" onClick={() => removeContainer(index)} className="remove-btn">
                                Remove
                                </button>
                            )}
                            </div>
                        ))}
                        
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                                {!readOnly && (
                                    <button type="button" onClick={addContainer}>Add Container</button>
                                )}
                                <span style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
                                    {getContainerSummary()}
                                </span>
                            </div>
                       
                        </div>
                    </div>
                    )}
                {/* Commodities Section */}
                {canShowOtherSections && !isDocumentsOnlyMode && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('commodities-section')}>Commodities ({commodities.length})</h3>
                        <div id="commodities-section" className="section-content" style={{ display: 'block' }}>
                            {/* Add Excel Import Section */}
                            {!readOnly && (
                                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <label className="required" style={{ display: 'block', marginBottom: '5px' }}>
                                            Import Commodities from Excel
                                        </label>
                                        <input 
                                            type="file" 
                                            accept=".xlsx,.xls" 
                                            onChange={handleCommoditiesExcel}
                                            style={{ marginBottom: '10px' }}
                                        />
                                        <small style={{ color: '#666', fontSize: '11px', display: 'block' }}>
                                            Expected format: Excel file with 'ITEM_Sample' and 'MODEL_Sample' worksheets
                                        </small>
                                    </div>                                    
                                </div>
                            )}

                            {job.cargoType !== 'SCT' && !isExBondVehicle && (
                            <div style={{ marginBottom: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                                {job.cargoType === 'ZNZ' ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 600 }}>Select required:</span>
                                        {requiredPermitTypes.map((permitType) => (
                                            <label key={permitType} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={manualPermitsBase.includes(normalizePermitType(permitType))}
                                                    onChange={() => handleManualPermitToggle(permitType)}
                                                    disabled={readOnly}
                                                />
                                                {permitType}
                                            </label>
                                        ))}
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ fontSize: '12px', marginBottom: showPvocDiChoices ? '8px' : 0 }}>
                                            <strong>Required Permits:</strong> {requiredPermitTypes.length > 0 ? requiredPermitTypes.join(', ') : 'None'}
                                        </div>
                                        {showPvocDiChoices && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: '12px', fontWeight: 600 }}>Select required:</span>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={pvocDiSelections.includes('PVOC')}
                                                        onChange={() => handlePvocDiToggle('PVOC')}
                                                        disabled={readOnly}
                                                    />
                                                    PVOC
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={pvocDiSelections.includes('TBS DI')}
                                                        onChange={() => handlePvocDiToggle('TBS DI')}
                                                        disabled={readOnly}
                                                    />
                                                    TBS DI
                                                </label>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            )}
                            
                            {/* Commodities Table */}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                    <thead style={{ backgroundColor: '#f1f7ff' }}>
                                        <tr>
                                            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>ITEM NO.</th>
                                            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>HS CODE</th>
                                            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>DESCRIPTION</th>
                                            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>MODEL/SPECIFICATION</th>
                                            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>COMPONENT</th>
                                            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>QUANTITY</th>
                                            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>QUANTITY UNIT</th>
                                            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>UNIT PRICE</th>
                                            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>ITEM INV. PRICE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {commodities.length === 0 ? (
                                            <tr>
                                                <td colSpan={readOnly ? "9" : "10"} style={{ 
                                                    border: '1px solid #ddd', 
                                                    padding: '20px', 
                                                    textAlign: 'center',
                                                    color: '#666'
                                                }}>
                                                    {readOnly ? 'No commodities data available' : 'No commodities data. Import an Excel file or add manually.'}
                                                </td>
                                            </tr>
                                        ) : (
                                            commodities.map((commodity, index) => (
                                                <tr key={index}>
                                                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                                        <input
                                                            type="text"
                                                            value={commodity.item_no}
                                                            readOnly
                                                            style={{ 
                                                                border: 'none', 
                                                                background: 'transparent', 
                                                                width: '100%', 
                                                                textAlign: 'center',
                                                                fontSize: '12px'
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                                        <input
                                                            type="text"
                                                            value={commodity.hs_code}                                                            
                                                            readOnly
                                                            style={{ 
                                                                border: 'none', 
                                                                background: 'transparent', 
                                                                width: '100%', 
                                                                textAlign: 'center',
                                                                fontSize: '12px'
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                                        <input
                                                            type="text"
                                                            value={commodity.description}
                                                            readOnly
                                                            style={{ 
                                                                border: 'none', 
                                                                background: 'transparent', 
                                                                width: '100%', 
                                                                textAlign: 'left',
                                                                fontSize: '12px'
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                                        <input
                                                            type="text"
                                                            value={commodity.model_specification}
                                                            readOnly
                                                            style={{ 
                                                                border: 'none', 
                                                                background: 'transparent', 
                                                                width: '100%', 
                                                                textAlign: 'center',
                                                                fontSize: '12px'
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                                        <input
                                                            type="text"
                                                            value={commodity.component}
                                                            readOnly
                                                            style={{ 
                                                                border: 'none', 
                                                                background: 'transparent', 
                                                                width: '100%', 
                                                                textAlign: 'center',
                                                                fontSize: '12px'
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                                        <input
                                                            type="text"
                                                            value={formatAccounting(commodity.quantity)}
                                                            readOnly
                                                            style={{ 
                                                                border: 'none', 
                                                                background: 'transparent', 
                                                                width: '100%', 
                                                                textAlign: 'right',
                                                                fontSize: '12px'
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                                        <input
                                                            type="text"
                                                            value={commodity.quantity_unit}
                                                            readOnly
                                                            style={{ 
                                                                border: 'none', 
                                                                background: 'transparent', 
                                                                width: '100%', 
                                                                textAlign: 'center',
                                                                fontSize: '12px'
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                                        <input
                                                            type="text"
                                                            value={formatAccounting(commodity.unit_price)}
                                                            readOnly
                                                            style={{ 
                                                                border: 'none', 
                                                                background: 'transparent', 
                                                                width: '100%', 
                                                                textAlign: 'right',
                                                                fontSize: '12px'
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                                        <input
                                                            type="text"
                                                            value={formatAccounting(commodity.item_inv_price)}
                                                            readOnly
                                                            style={{ 
                                                                border: 'none', 
                                                                background: 'transparent', 
                                                                width: '100%', 
                                                                textAlign: 'right',
                                                                fontSize: '12px'
                                                            }}
                                                        />
                                                    </td>                                                    
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>                            
                        </div>
                    </div>
                )}

                {/* Documents Section */}
                {(canShowOtherSections || isDocumentsOnlyMode) && (
                    <div className="section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 onClick={() => toggleSection('documents-section')}>Documents ({documents.length + doAttachmentDocs.length + exBondPreviousDocuments.length})</h3>
                            {isAdmin && (
                                <button
                                    type="button"
                                    onClick={() => setDepartmentAttachmentsModal({ department: 'ALL' })}
                                    style={{ fontSize: 12, background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer' }}
                                >
                                    View All Attachments
                                </button>
                            )}
                        </div>
                        <div id="documents-section" className="section-content" style={{ display: 'block' }}>
                            
                            {(() => {
                                const getFileNameFromUrl = (url) => {
                                    if (!url || typeof url !== 'string') return 'Unknown File';
                                    try {
                                        return url.substring(url.lastIndexOf('/') + 1);
                                    } catch {
                                        return 'Unknown File';
                                    }
                                };

                                // Group saved, non-payable documents by uploader department
                                const departmentGroups = {};
                                const unsavedDocs = [];
                                const allDisplayDocs = [...documents, ...doAttachmentDocs].filter(doc =>
                                    !isWharfageOnly || (doc.document_type || '').toLowerCase() === 'wharfage invoice'
                                );
                                allDisplayDocs.forEach((doc) => {
                                    // Use the document's actual index in the documents state array so setDocuments updates the right item.
                                    const docIndex = doc.is_do_attachment ? -1 : documents.indexOf(doc);
                                    if (doc.is_do_attachment) {
                                        const dept = doc.uploaded_by_department || 'Unknown';
                                        if (!departmentGroups[dept]) departmentGroups[dept] = [];
                                        departmentGroups[dept].push({ doc, idx: docIndex });
                                    } else if (!doc.id) {
                                        unsavedDocs.push({ doc, idx: docIndex });
                                    } else if (!doc.is_payable_document) {
                                        const dept = doc.uploaded_by_department || 'Unknown';
                                        if (!departmentGroups[dept]) departmentGroups[dept] = [];
                                        departmentGroups[dept].push({ doc, idx: docIndex });
                                    }
                                });

                                const renderDocumentRow = (document, index) => {
                                    const existing = !!document.id;
                                    const locked = lockExistingDocuments && existing;
                                    return (
                                        <div key={index} className="repeatable-row" style={{ alignItems: 'center', gap: 12 }}>
                                        <div>
                                            <label className="required">Document Type</label>
                                            <Select2Dropdown
                                            options={documentTypes.map(type => ({ id: type.name, text: type.name }))}
                                            value={document.document_type}
                                            onChange={value => {
                                                if (locked) return;
                                                setDocuments(docs => docs.map((d,i)=> i===index ? { ...d, document_type: value } : d));
                                            }}
                                            placeholder="Select document type"
                                            displayKey="text"
                                            valueKey="id"
                                            required
                                            disabled={(readOnly && !documentsEditable) || locked}
                                            clearable={!locked}
                                            allowCreate={false}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label className="required">Document Upload</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            {/* --- CONDITIONAL UI LOGIC START --- */}
                                            {document.file_obj ? (
                                                // If a new file has been chosen (file_obj exists), display its name
                                                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#007bff', fontStyle: 'italic' }}>
                                                    {document.file_obj.name}
                                                </span>
                                            ) : replacingDocIndex === index || !document.document_file ? (
                                                // If we are in replace mode or there's no existing file, show the input
                                                <input
                                                    type="file"
                                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                    onChange={(e) => {
                                                        handleFileChange(e, 'documents', index);
                                                        setReplacingDocIndex(null); // Exit replace mode after selecting a file
                                                    }}
                                                    disabled={(readOnly && !documentsEditable) || locked}
                                                    style={{ width: 200 }}
                                                />
                                            ) : (
                                                // Otherwise, show the existing file's name
                                                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
                                                    {getFileNameFromUrl(document.document_file)}
                                                </span>
                                            )}
                                            {/* --- CONDITIONAL UI LOGIC END --- */}
                                            {(document.document_file || document.local_preview_url) && (
                                                <button
                                                type="button"
                                                onClick={() => {
                                                    const url = document.local_preview_url
                                                    || (typeof document.document_file === 'string'
                                                            ? document.document_file
                                                            : (document.document_file_url || ''));
                                                    if (url) openPreview(url, document.file_obj?.name || 'file');
                                                }}
                                                style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                                                title="View Document"
                                                >
                                                View
                                                </button>
                                            )}
                                            {/* Replace button only if not locked */}
                                            {!readOnly && !locked && document.document_file && (
                                                <button
                                                type="button"
                                                onClick={() => setReplacingDocIndex(index)}
                                                style={{ fontSize: 12, background: '#ffc107', color: '#333', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                                                >
                                                Replace
                                                </button>
                                            )}
                                            </div>
                                            {document.document_file && document.fileMeta && (
                                            <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>
                                                <span>
                                                Size: {document.fileMeta.size < 1024 * 1024
                                                    ? (document.fileMeta.size / 1024).toFixed(1) + ' KB'
                                                    : (document.fileMeta.size / (1024 * 1024)).toFixed(2) + ' MB'}
                                                </span>
                                                {" | "}
                                                <span>
                                                Uploaded: {document.fileMeta.uploaded.toLocaleDateString()} {document.fileMeta.uploaded.toLocaleTimeString()}
                                                </span>
                                                {" | "}
                                                <span>
                                                By: {document.fileMeta.user}
                                                </span>
                                            </div>
                                            )}
                                        </div>
                                        <div>
                                            <label>Remarks</label>
                                            <input
                                            type="text"
                                            value={document.remarks}
                                            onChange={e => {
                                                if (locked) return;
                                                setDocuments(docs => docs.map((d,i)=> i===index ? { ...d, remarks: e.target.value } : d));
                                            }}
                                            readOnly={ (readOnly && !documentsEditable) || locked }
                                            />
                                        </div>
                                        {/* Remove button only for new (unsaved) docs */}
                                        {(!locked && (documentsEditable || !readOnly)) && !document.id && (
                                            <button
                                            type="button"
                                            onClick={() => setDocuments(docs => docs.filter((_,i)=>i!==index))}
                                            className="remove-btn"
                                            >
                                            Remove
                                            </button>
                                        )}
                                        </div>
                                    );
                                };

                                const renderExBondPreviousDocumentRow = (document) => {
                                    const fileName = getFileNameFromUrl(document.document_file_url || document.document_file);
                                    return (
                                        <div key={`ex-bond-prev-${document.id}`} className="repeatable-row" style={{ alignItems: 'center', gap: 12 }}>
                                            <div>
                                                <label>Document Type</label>
                                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#007bff', padding: '6px 0' }}>
                                                    {document.document_type}
                                                </div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label>Document Upload</label>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
                                                        {fileName}
                                                    </span>
                                                    {(document.document_file_url || document.document_file) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => openPreview(document.document_file_url || document.document_file, fileName)}
                                                            style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                                                            title="View Document"
                                                        >
                                                            View
                                                        </button>
                                                    )}
                                                </div>
                                                {document.fileMeta && (
                                                    <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>
                                                        <span>
                                                            Size: {document.fileMeta.size < 1024 * 1024
                                                                ? (document.fileMeta.size / 1024).toFixed(1) + ' KB'
                                                                : (document.fileMeta.size / (1024 * 1024)).toFixed(2) + ' MB'}
                                                        </span>
                                                        {" | "}
                                                        <span>
                                                            Uploaded: {document.fileMeta.uploaded.toLocaleDateString()} {document.fileMeta.uploaded.toLocaleTimeString()}
                                                        </span>
                                                        {" | "}
                                                        <span>
                                                            By: {document.fileMeta.user}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                };

                                const renderDoAttachmentRow = (document) => {
                                    const fileName = getFileNameFromUrl(document.document_file);
                                    return (
                                        <div key={document.id} className="repeatable-row" style={{ alignItems: 'center', gap: 12, background: '#f8f9fa' }}>
                                            <div>
                                                <label>Document Type</label>
                                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#007bff', padding: '6px 0' }}>
                                                    {document.document_type}
                                                </div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label>Document Upload</label>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
                                                        {fileName}
                                                    </span>
                                                    {document.document_file_url && (
                                                        <button
                                                            type="button"
                                                            onClick={() => openPreview(document.document_file_url, fileName)}
                                                            style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                                                            title="View Document"
                                                        >
                                                            View
                                                        </button>
                                                    )}
                                                </div>
                                                {document.fileMeta && (
                                                    <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>
                                                        <span>
                                                            Size: {document.fileMeta.size < 1024 * 1024
                                                                ? (document.fileMeta.size / 1024).toFixed(1) + ' KB'
                                                                : (document.fileMeta.size / (1024 * 1024)).toFixed(2) + ' MB'}
                                                        </span>
                                                        {" | "}
                                                        <span>
                                                            Uploaded: {document.fileMeta.uploaded.toLocaleDateString()} {document.fileMeta.uploaded.toLocaleTimeString()}
                                                        </span>
                                                        {" | "}
                                                        <span>
                                                            By: {document.fileMeta.user}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                };

                                return (
                                    <>
                                        {exBondPreviousDocuments.length > 0 && (
                                            <div style={{ marginBottom: 20, border: '1px solid #ddd', borderRadius: 4, padding: '10px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
                                                    <h4 style={{ margin: 0, color: '#007bff' }}>
                                                        Previous IDF Documents (Documentation)
                                                    </h4>
                                                </div>
                                                {exBondPreviousDocuments.map(doc => renderExBondPreviousDocumentRow(doc))}
                                            </div>
                                        )}
                                        {Object.entries(departmentGroups).map(([dept, items]) => {
                                            const isCollapsed = !!collapsedDepartments[dept];
                                            return (
                                                <div key={dept} style={{ marginBottom: 20, border: '1px solid #ddd', borderRadius: 4, padding: '10px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isCollapsed ? 0 : 10, borderBottom: isCollapsed ? 'none' : '1px solid #eee', paddingBottom: isCollapsed ? 0 : 8 }}>
                                                        <h4
                                                            onClick={() => setCollapsedDepartments(prev => ({ ...prev, [dept]: !prev[dept] }))}
                                                            style={{ margin: 0, color: '#007bff', cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 8 }}
                                                            title="Click to collapse/expand"
                                                        >
                                                            <span style={{ display: 'inline-block', width: 12, transition: 'transform 0.2s', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                                                                ▼
                                                            </span>
                                                            {dept}
                                                        </h4>
                                                        {isAdmin && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setDepartmentAttachmentsModal({ department: dept })}
                                                                style={{ fontSize: 12, background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer' }}
                                                            >
                                                                View All Attachments
                                                            </button>
                                                        )}
                                                    </div>
                                                    {!isCollapsed && items.map(({ doc, idx }) =>
                                                        doc.is_do_attachment ? renderDoAttachmentRow(doc) : renderDocumentRow(doc, idx)
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {unsavedDocs.length > 0 && (
                                            <div style={{ marginBottom: 20 }}>
                                                <h4 style={{ color: '#888', marginBottom: 10 }}>New Documents</h4>
                                                {unsavedDocs.map(({ doc, idx }) => renderDocumentRow(doc, idx))}
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                            <div className="doc-actions" style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                                {(documentsEditable || !readOnly) && (
                                    <button
                                        type="button"
                                        className="btn-add-doc"
                                        onClick={() => {
                                        setDocuments(prev => [
                                            ...prev,
                                            {
                                            id: null,
                                            document_type: isWharfageOnly ? 'Wharfage Invoice' : '',
                                            remarks: '',
                                            document_file: null,
                                            file_obj: null,
                                            local_preview_url: null,
                                            fileMeta: null
                                            }
                                        ]);
                                        }}
                                    >
                                        <i className="fa fa-plus" /> Add Document
                                    </button>
                                )}
                                {showEmailButtonOnly && (
                                    <button 
                                        type="button" 
                                        className="btn-save-docs" 
                                        onClick={handleSaveDocuments}
                                        disabled={savingDocuments}
                                    >
                                        {savingDocuments ? (<><i className="fa fa-spinner fa-spin" /> Saving...</>) : (<><i className="fa fa-save" /> Save Documents</>)}
                                    </button>
                                )}
                            </div>
                            
                            {/* Document Preview Modal */}
                            {/* <DocumentPreviewModal
                                open={previewOpen}
                                file={previewFile}
                                onClose={() => setPreviewOpen(false)}
                            /> */}
                        </div>
                    </div>
                )}

                {/* Letters Section */}
                {canShowOtherSections && !isDocumentsOnlyMode && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('letters-section')}>Letters ({letters.length})</h3>
                        <div id="letters-section" className="section-content" style={{ display: 'block' }}>
                            {letters.map((letter, index) => (
                                <div key={index} className="repeatable-row">
                                    <div>
                                        <label>Letter Type</label>
                                        <input
                                            type="text"
                                            value={letter.letter_type}
                                            onChange={(e) => {
                                                const updated = [...letters];
                                                updated[index].letter_type = e.target.value;
                                                setLetters(updated);
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    <div>
                                        <label>Template Used</label>
                                        <input
                                            type="text"
                                            value={letter.template_used}
                                            onChange={(e) => {
                                                const updated = [...letters];
                                                updated[index].template_used = e.target.value;
                                                setLetters(updated);
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    <div>
                                        <label>Generated File</label>
                                        <input
                                            type="file"
                                            onChange={(e) => {
                                                const updated = [...letters];
                                                updated[index].generated_file = e.target.files[0];
                                                setLetters(updated);
                                            }}
                                            disabled={readOnly}
                                        />
                                    </div>
                                    {!readOnly && (
                                        <button type="button" onClick={() => setLetters(letters.filter((_, i) => i !== index))} className="remove-btn">
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                            {!readOnly && (
                                <button type="button" onClick={addLetter}>Add Letter</button>
                            )}
                        </div>
                    </div>
                )}

                {/* Emails Section */}
                {(canShowOtherSections || isDocumentsOnlyMode) && !hideEmailSection && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('emails-section')}>Emails ({emails.length})</h3>
                        <div id="emails-section" className="section-content" style={{ display: 'block' }}>
                            {/* Add Email Button */}
                            <div style={{ marginBottom: '15px' }}>
                                {!readOnly && (
                                    <button 
                                        type="button" 
                                        onClick={() => setShowEmailModal(true)}
                                        style={{
                                            padding: '8px 16px',
                                            background: '#007bff',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            marginBottom: '10px'
                                        }}
                                    >
                                        + Add New Email
                                    </button>
                                )}
                                
                                {/* Emails Table */}
                                {emails.length > 0 ? (
                                    <table style={{ 
                                        width: '100%', 
                                        borderCollapse: 'collapse', 
                                        fontSize: '11px',
                                        border: '1px solid #ddd'
                                    }}>
                                        <thead style={{ background: '#f8f9fa' }}>
                                            <tr>
                                                <th style={emailTableHeaderStyle}>Date</th>
                                                <th style={emailTableHeaderStyle}>Subject</th>
                                                <th style={emailTableHeaderStyle}>From</th>
                                                <th style={emailTableHeaderStyle}>To</th>
                                                <th style={emailTableHeaderStyle}>Body Preview</th>
                                                <th style={emailTableHeaderStyle}>User</th>
                                                {!readOnly && <th style={emailTableHeaderStyle}>Actions</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {emails.map((email, index) => (
                                                <tr 
                                                    key={email.id || index} 
                                                    style={{ 
                                                        cursor: 'pointer',
                                                        '&:hover': { backgroundColor: '#f5f5f5' }
                                                    }} 
                                                    onClick={() => setViewEmailModal(email)}
                                                >
                                                    <td style={emailTableCellStyle}>
                                                        {email.email_date || email.date || '-'}
                                                    </td>
                                                    <td style={{...emailTableCellStyle, fontWeight: 'bold'}}>
                                                        {email.subject || '-'}
                                                    </td>
                                                    <td style={emailTableCellStyle}>
                                                        {email.from_email || email.from || '-'}
                                                    </td>
                                                    <td style={emailTableCellStyle}>
                                                        {email.to_email || email.to || '-'}
                                                    </td>
                                                    <td style={{...emailTableCellStyle, maxWidth: '200px'}}>
                                                        <div style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {email.body ? 
                                                                email.body.replace(/<[^>]*>/g, '').substring(0, 50) + '...' : 
                                                                'No content'
                                                            }
                                                        </div>
                                                    </td>
                                                    <td style={emailTableCellStyle}>
                                                        {email.user || 'Unknown User'}
                                                    </td>
                                                    {!readOnly && (
                                                        <td style={emailTableCellStyle}>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditEmailModal(email);
                                                                    setShowEmailModal(true);
                                                                }}
                                                                style={{
                                                                    padding: '2px 6px',
                                                                    fontSize: '10px',
                                                                    background: '#28a745',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '3px',
                                                                    cursor: 'pointer',
                                                                    marginRight: '4px'
                                                                }}
                                                            >
                                                                Edit
                                                            </button>
                                                            {canRemoveRows && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (window.confirm('Are you sure you want to delete this email?')) {
                                                                            setEmails(emails.filter((_, i) => i !== index));
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        padding: '2px 6px',
                                                                        fontSize: '10px',
                                                                        background: '#dc3545',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '3px',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    Delete
                                                                </button>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div style={{ 
                                        textAlign: 'center', 
                                        padding: '20px', 
                                        color: '#666',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        background: '#f9f9f9'
                                    }}>
                                        No emails recorded for this job.
                                        {!readOnly && (
                                            <div style={{ marginTop: '10px' }}>
                                                <button 
                                                    type="button"
                                                    onClick={() => setShowEmailModal(true)}
                                                    style={{
                                                        padding: '6px 12px',
                                                        background: '#007bff',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Send First Email
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Notes Section */}
                {canShowOtherSections && !isDocumentsOnlyMode && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('notes-section')}>Notes ({notes.length})</h3>
                        <div id="notes-section" className="section-content" style={{ display: 'block' }}>
                            {notes.map((note, index) => (
                                <div key={index} className="repeatable-row">
                                    <div>
                                        <label>Subject</label>
                                        <input
                                            type="text"
                                            placeholder="Enter Subject"
                                            value={note.subject || ''}
                                            onChange={(e) => {
                                                const updated = [...notes];
                                                updated[index].subject = e.target.value;
                                                setNotes(updated);
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    
                                    <div>
                                        <label>Notes</label>
                                        <textarea
                                            placeholder="Enter Notes"
                                            value={note.notes || ''}
                                            onChange={(e) => {
                                                const updated = [...notes];
                                                updated[index].notes = e.target.value;
                                                setNotes(updated);
                                            }}
                                            readOnly={readOnly}
                                            rows={3}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <input
                                            type="checkbox"
                                            checked={note.show_on_manifest}
                                            onChange={(e) => {
                                                const updated = [...notes];
                                                updated[index] = { ...updated[index], show_on_manifest: e.target.checked };
                                                setNotes(updated);
                                            }}
                                            disabled={readOnly}
                                            />
                                            <span style={{ fontSize: 12 }}>Show on Manifest</span>
                                        </label>
                                    </div>
                                    
                                    <div>
                                        <label>Date</label>
                                        <input
                                            type="date"
                                            value={note.note_date || ''}
                                            onChange={(e) => {
                                                const updated = [...notes];
                                                updated[index].note_date = e.target.value;
                                                setNotes(updated);
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    
                                    <div>
                                        <label>Created By</label>
                                        <input
                                            type="text"
                                            value={note.created_by_username || localStorage.getItem('kk_user') || 'Unknown User'}
                                            readOnly={true}
                                            style={{ backgroundColor: '#f5f5f5' }}
                                        />
                                        {/* Hidden field to store the actual user ID */}
                                        <input
                                            type="hidden"
                                            name={`note_${index}_created_by_user`}
                                            value={note.created_by_user || ''}
                                        />
                                    </div>
                                    
                                    {!readOnly && canRemoveRows && (
                                        <button type="button" onClick={() => setNotes(notes.filter((_, i) => i !== index))} className="remove-btn">
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                            {!readOnly && (
                                <button type="button" onClick={addNote}>Add Note</button>
                            )}
                        </div>
                    </div>
                )}

                {/* LPCO Section */}
                {canShowOtherSections && !isDocumentsOnlyMode && (
                    <div className="section">
                        <h3 onClick={() => toggleSection('lpco-section')}>LPCO ({lpcos.length})</h3>
                        <div id="lpco-section" className="section-content" style={{ display: 'block' }}>
                            {lpcos.map((item, index) => (
                                <div key={index} className="repeatable-row">
                                    <div>
                                        <label>Authority</label>
                                        <Select2Dropdown
                                            options={lpcoAuthorityOptions}
                                            value={item.authority}
                                            onChange={(value) => {
                                                const updated = [...lpcos];
                                                updated[index].authority = value;
                                                setLpcos(updated);
                                            }}
                                            placeholder="SELECT AUTHORITY"
                                            displayKey="text"
                                            valueKey="id"
                                            disabled={readOnly}
                                            clearable={true}
                                        />
                                    </div>
                                    <div>
                                        <label>Application No.</label>
                                        <input
                                            type="text"
                                            value={item.application_no}
                                            onChange={(e) => {
                                                const updated = [...lpcos];
                                                updated[index].application_no = e.target.value;
                                                setLpcos(updated);
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    <div>
                                        <label>Remarks</label>
                                        <input
                                            type="text"
                                            value={item.remarks}
                                            onChange={(e) => {
                                                const updated = [...lpcos];
                                                updated[index].remarks = e.target.value;
                                                setLpcos(updated);
                                            }}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    {!readOnly && canRemoveRows && (
                                        <button type="button" onClick={() => setLpcos(lpcos.filter((_, i) => i !== index))} className="remove-btn">
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                            {!readOnly && (
                                <button type="button" onClick={addLPCO}>Add LPCO</button>
                            )}
                        </div>
                    </div>
                )}

                {/* Form Buttons */}
                <div className="buttons">
                    {readOnly && !documentsEditable && !openDocumentsOnly && !showEmailButtonOnly && hasPermission('JOBS', 'can_edit') && (
                        <button type="button" onClick={() => setReadOnly(false)}>Edit</button>
                    )}
                    {( !readOnly || documentsEditable ) && !showEmailButtonOnly && (
                        <button type="submit" disabled={loading}>
                            {loading ? 'Saving...' : (initialData ? 'Update Job' : 'Create Job')}
                        </button>
                    )}
                    {showEmailButtonOnly && (
                        <button 
                            type="button" 
                            onClick={() => setShowEmailModal(true)}
                            style={{
                                padding: '8px 16px',
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                marginBottom: '10px'
                            }}
                        >
                            New Email
                        </button>
                    )}
                    <button type="button" onClick={() => onClose(false)} className="cancel-btn">
                        {readOnly ? 'Close' : 'Cancel'}
                    </button>
                </div>
            </form>
            {/* Email Modal for Create/Edit */}
            <EmailModal
                isOpen={showEmailModal}
                onClose={() => {
                    setShowEmailModal(false);
                    setEditEmailModal(null);
                }}
                onSave={(emailData) => {
                    if (editEmailModal) {
                        // Update existing email
                        const updatedEmails = emails.map((email, index) => 
                            email === editEmailModal ? { 
                                ...emailData, 
                                id: email.id || Date.now(),
                                user: emailData.user || getCurrentUser()
                            } : email
                        );
                        setEmails(updatedEmails);
                    } else {
                        // Add new email
                        setEmails([...emails, { 
                            ...emailData, 
                            id: Date.now(),
                            user: emailData.user || getCurrentUser()
                        }]);
                    }
                    setEditEmailModal(null);
                    // If this was triggered from the Wharfage Report, mark the job as sent.
                    if (showEmailButtonOnly && (defaultEmailTemplateName || '').toLowerCase() === "wharfage invoice") {
                        handleWharfageSent();
                    }
                }}
                editEmail={editEmailModal}
                readOnly={readOnly && !!editEmailModal}
                defaultFrom={localStorage.getItem('kk_email')}
                defaultSubjectPrefix={job.emailSubject ? `Re: ${job.emailSubject}` : ''}
                showAttachments={true}
                customerEmailGroups={customerEmailGroups}
                jobAttachments={documents || []}
                jobData={{
                    id: job.id,
                    customer_name: customers.find(c => c.id === parseInt(job.customer))?.name || 'Unknown Client',
                    idf_no: job.idfNo,
                    customer_ref: job.customerRefNo,
                    bl_number: job.hblSoNo || job.mblNo,
                    invoice_number: job.invoiceNo,
                    vessel: job.finalVessel,
                    supplier: job.supplierDetails?.name,
                    eta: (job.shipmentType?.includes('SEA') ? (job.voyage_details?.eta || job.eta) : job.airport_eta)
                }}
                defaultTemplateName={defaultEmailTemplateName}
            />

            {/* Email Viewer Modal */}
            <EmailViewer
                email={viewEmailModal}
                isOpen={!!viewEmailModal}
                onClose={() => setViewEmailModal(null)}
                onEdit={(email) => {
                    setViewEmailModal(null);
                    setEditEmailModal(email);
                    setShowEmailModal(true);
                }}
                readOnly={readOnly}
            />
            <VoyageModalWrapper
                isOpen={showVoyageModal}
                onClose={() => setShowVoyageModal(false)}
                onSave={refreshVoyages}
            />
            <ShippingLineNotesModal
                isOpen={showShippingLineNotes}
                onClose={() => setShowShippingLineNotes(false)}
                notes={shippingLineNotes}
            />
            <CustomerNotesModal
                isOpen={showCustomerNotes}
                onClose={() => setShowCustomerNotes(false)}
                notes={customerNotes}
            />
            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={confirmDialog.onCancel}
            />

            {showExpensesModal && (
                <PopoutWindow
                    title={`Expenses - IDF #${job.idfNo}`}
                    name={`job_expenses_${(job.idfNo || '').replace(/\s+/g, '_')}`}
                    onClose={() => setShowExpensesModal(false)}
                >
                    <PayableExpensesView
                        idfNo={job.idfNo}
                        onClose={() => setShowExpensesModal(false)}
                    />
                </PopoutWindow>
            )}

            {departmentAttachmentsModal && (
                <DepartmentAttachmentsPopout
                    jobId={initialData?.id || job?.id}
                    department={departmentAttachmentsModal.department}
                    onClose={() => setDepartmentAttachmentsModal(null)}
                />
            )}
        </div>
    );
};

export default JobForm;